// Tiny i18n primitive — no library dependency. A flat dictionary keyed by dotted paths
// (e.g. "nav.discover"), plus a React context that exposes `t(key)` and `setLocale(lang)`.
// Selected locale persists to localStorage. Translations are sourced from xapi-new's
// i18n/locales/{en,zh}/* JSON files so technical-term phrasing stays consistent across the
// two frontends (Console / 控制台, Workspace / 工作区, etc.).
//
// To add a new key: drop the same path into both `en` and `zh` blocks below. To add a third
// locale: extend `LOCALES` and add a third block. The runtime falls back to `en` if a key
// is missing in the active locale, then to the literal key string.

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';

export const LOCALES = ['en', 'zh'];

const dict = {
  en: {
    'api.referencePrices.title': 'Model reference prices',
    'api.referencePrices.explanation': 'Billed at upstream actual cost × {ratio}. Prices below are estimates in USD per million tokens; final charges follow the response cost.',
    'api.referencePrices.model': 'Model',
    'api.referencePrices.input': 'Input / 1M',
    'api.referencePrices.output': 'Output / 1M',
    'api.referencePrices.updated': 'Reference price updated',
    'api.referencePrices.expand': 'Show all {count} models',
    'api.referencePrices.collapse': 'Collapse',
    'api.referencePrices.rules': 'View billing rules',
    'api.referencePrices.official': 'OpenRouter reference (input / output)',
    'api.referencePrices.audioInput': 'Input', 'api.referencePrices.audioOutput': 'Output',
    'api.referencePrices.characterExplanation': 'Current character rates and official reference prices are shown separately. Final charges follow the existing billing rules.',
    'api.referencePrices.audioUnknownExplanation': 'Official reference rates use the displayed units. See billing rules for the actual charge.',
    'api.referencePrices.audioExplanation': 'Estimated rates in the displayed units. Billed at upstream actual cost × {ratio}; unknown units have no quote.',
    'api.referencePrices.unknownExplanation': 'Official reference prices in USD per million tokens. See billing rules for the actual charge.',
    // ===== Earnings / Withdrawal (earnings.jsx) =====
    'earnings.title': 'My Earnings',
    'earnings.loading': 'Loading…',
    'earnings.loadError': 'Unable to load earnings. Please retry.',
    'earnings.wd.REQUESTED': 'Pending review',
    'earnings.wd.APPROVED': 'Approved',
    'earnings.wd.QUEUED': 'Processing',
    'earnings.wd.BROADCAST': 'Processing',
    'earnings.wd.CONFIRMED': 'Paid',
    'earnings.wd.REJECTED': 'Rejected',
    'earnings.wd.FAILED': 'Failed',
    'earnings.wd.CANCELED': 'Canceled',
    'earnings.earn.PENDING': 'Pending',
    'earnings.earn.SETTLED': 'Settled',
    'earnings.gate.loginTitle': 'Sign in to view earnings & withdrawals',
    'earnings.gate.loginCta': 'Sign in',
    'earnings.gate.notProviderTitle': "You're not a provider yet",
    'earnings.gate.notProviderDesc':
      'Publish your API to the marketplace — earnings from calls appear here and are withdrawable.',
    'earnings.gate.notProviderCta': 'Publish API',
    'earnings.blocked.title': 'Withdrawals restricted',
    'earnings.blocked.reasonSep': ': {reason}',
    'earnings.blocked.body':
      '. You currently cannot initiate a withdrawal — please contact an admin via Telegram.',
    'earnings.blocked.contact': 'Contact admin ↗',
    'earnings.withdrawable': 'Withdrawable',
    'earnings.totalNet': 'Total earnings (net)',
    'earnings.btn.blocked': 'Restricted',
    'earnings.btn.collapse': 'Collapse',
    'earnings.btn.withdraw': 'Withdraw',
    'earnings.btn.blockedTitle': 'Withdrawals restricted — contact an admin',
    'earnings.settledTotal': 'Settled total {amt} (source of withdrawable)',
    'earnings.pendingMore':
      ' · plus {amt} pending, moved to withdrawable once settled',
    'earnings.onlyProvider':
      ' · Only provider API earnings are withdrawable (not top-up / bonus balance)',
    'earnings.minSuffix': ' · min {amt}',
    'earnings.maxSuffix': ' · max {amt}',
    'earnings.periodEnd': '.',
    'earnings.form.amountLabel': 'Amount (USDC)',
    'earnings.form.all': 'All',
    'earnings.form.amountHint': 'Withdrawable {amt}',
    'earnings.form.addrLabel': 'Destination address (Base / EVM)',
    'earnings.form.addrHintEmpty':
      'EVM wallet address to receive USDC ({network}).',
    'earnings.form.addrValid': '✓ Valid address: {addr}',
    'earnings.form.addrInvalid':
      '✕ Not a valid EVM address (must start with 0x, 42 chars, correct checksum).',
    'earnings.form.next': 'Next: review',
    'earnings.form.cancel': 'Cancel',
    'earnings.confirm.title': 'Review your withdrawal',
    'earnings.confirm.amount': 'Amount',
    'earnings.confirm.toAddress': 'Destination',
    'earnings.confirm.network': 'Network / Token',
    'earnings.confirm.remaining': 'Remaining after',
    'earnings.confirm.warning':
      '⚠️ On-chain payouts are irreversible. A wrong address means funds are permanently lost and unrecoverable — double-check the destination matches your wallet exactly. Submissions still require manual review before payout.',
    'earnings.confirm.submit': 'Confirm & submit',
    'earnings.confirm.submitting': 'Submitting…',
    'earnings.confirm.back': 'Back',
    'earnings.network.baseMainnet': 'Base Mainnet',
    'earnings.network.baseSepolia': 'Base Sepolia Testnet',
    'earnings.err.amountGt0': 'Amount must be greater than 0',
    'earnings.err.minPerTx': 'Minimum {amt} per withdrawal',
    'earnings.err.maxPerTx': 'Maximum {amt} per withdrawal',
    'earnings.err.exceed': 'Exceeds withdrawable earnings',
    'earnings.toast.enterAmount': 'Enter a withdrawal amount',
    'earnings.toast.invalidAddr': 'Destination is not a valid EVM address',
    'earnings.toast.submitted': 'Withdrawal submitted, pending review',
    'earnings.toast.submitFail': 'Submission failed',
    'earnings.toast.canceled': 'Canceled',
    'earnings.toast.cancelFail': 'Cancel failed',
    'earnings.tab.detail': 'Earnings',
    'earnings.tab.records': 'Withdrawals',
    'earnings.stat.monthly': 'This month',
    'earnings.stat.pending': 'Pending',
    'earnings.stat.pendingHint': 'Moves to withdrawable once settled',
    'earnings.settlementNote': 'Pending earnings become settled when the settlement job completes. Timing may vary.',
    'earnings.stat.totalCalls': 'Total calls',
    'earnings.stat.activeCallers': 'Active callers',
    'earnings.detailNote':
      'Earnings are net of platform fees. Your account default is {pct}%; service overrides may differ. Each record shows its actual fee.',
    'earnings.noEarnings':
      'No earnings yet — once your APIs are called and billed, each split appears here.',
    'earnings.col.api': 'API',
    'earnings.col.gross': 'Gross',
    'earnings.col.fee': 'Fee',
    'earnings.col.net': 'Net',
    'earnings.col.status': 'Status',
    'earnings.col.time': 'Time',
    'earnings.pageTotal': 'Page total',
    'earnings.noWithdrawals': 'No withdrawals yet',
    'earnings.record.failedRequeued':
      'Last payout failed, returned to pending review: {reason}',
    'earnings.record.rejectReason': 'Rejection reason: {reason}',
    'earnings.record.rejectNoNote': '(no note from admin)',
    'earnings.record.failReason': 'Failure reason: {reason}',
    'earnings.record.cancel': 'Cancel',
    'nav.discover': 'Market',
    'nav.mcp': 'MCP',
    'nav.skill': 'Skill',
    'nav.ai': 'AI',
    'nav.sandbox': 'Sandbox',
    'nav.playground': 'Playground',
    'nav.capabilities': 'Capabilities',
    'nav.domains': 'Domains',
    'nav.workspace': 'Workspace',
    'nav.activity': 'Activity',
    'nav.review': 'Review',
    'nav.referral': 'Referral',
    'nav.menu': 'Menu',
    'nav.account': 'Account',
    'nav.skillHub': 'Skill Market',

    'auth.logIn': 'Log in',
    'auth.signOut': 'Sign out',
    'auth.getStarted': 'Get started',
    'auth.dashboard': 'Dashboard',
    'auth.getApiKey': 'Get API Key',

    'auth.login.title': 'Get started',
    'auth.login.subtitle':
      'Sign in to get started.',
    'auth.login.quickstart': 'Quick start (instant key)',
    'auth.login.quickstart.busy': 'Creating…',
    'auth.login.twitter': 'Continue with Twitter',
    'auth.login.twitter.busy': 'Redirecting…',
    'auth.login.divider': 'or sign in with an existing key',
    'auth.login.apiKey.label': 'API key',
    'auth.login.apiKey.placeholder': 'sk-…',
    'auth.login.apiKey.submit': 'Sign in with key',
    'auth.login.apiKey.busy': 'Signing in…',
    'auth.login.apiKey.required': 'API key required',
    'auth.login.referral.add': '+ Add referral code',
    'auth.login.referral.label': 'Referral code (optional)',
    'auth.login.referral.placeholder': 'ref_…',

    'auth.wizard.eyebrow': 'Account created',
    'auth.wizard.title': 'Save your API key',
    'auth.wizard.body':
      "We {ONLY} Copy or download it now — without it you'll lose access to this account.",
    'auth.wizard.body.only': 'only show this key once.',
    'auth.wizard.warn':
      'Treat this like a password. We store only a salted hash on the server — nobody at xapi.to can recover it for you.',
    'auth.wizard.keyLabel': 'Your new API key',
    'auth.wizard.copy': 'Copy to clipboard',
    'auth.wizard.download': 'Download .env',
    'auth.wizard.saved': 'Key saved — connect X to publish APIs.',
    'auth.wizard.gateHint':
      'Continue is enabled after you copy or download the key.',
    'auth.wizard.continueX': 'Continue with X',
    'auth.wizard.continueX.busy': 'Preparing X authorisation…',
    'auth.wizard.skip': 'Skip for now',
    'auth.wizard.footnote':
      'You can keep using xapi as a virtual account — connect later from the console.',

    'topbar.search': 'Search APIs, endpoints, docs…',
    'topbar.invite': 'Invite & earn',
    'topbar.balance': 'Balance',
    'topbar.topUp': 'Top up',
    'topbar.rechargeBalance': 'Recharge balance (spend on API calls)',
    'topbar.withdrawableEarnings': 'Withdrawable earnings (API revenue)',
    'topbar.earnings': 'Earnings / Withdraw',
    'topbar.console': 'Console',
    'topbar.apiKeys': 'API keys',
    'topbar.myApis': 'My APIs',
    'topbar.bindEmail': 'Bind email',
    'topbar.emailSettings': 'Email',
    'topbar.emailUnbound': 'Email not bound',
    // Email binding
    'email.modal.title': 'Bind email',
    'email.modal.changeTitle': 'Change email',
    'email.modal.emailLabel': 'Email address',
    'email.modal.emailHint':
      'Add an email for account recovery and important notifications.',
    'email.modal.sendCode': 'Send code',
    'email.modal.sending': 'Sending…',
    'email.modal.codeLabel': 'Verification code',
    'email.modal.codeSentTo': 'We sent a 6-digit code to {email}.',
    'email.modal.verify': 'Verify & bind',
    'email.modal.verifying': 'Verifying…',
    'email.modal.resend': 'Resend code',
    'email.modal.resendIn': 'Resend in {s}s',
    'email.modal.changeEmail': 'Use another email',
    'email.modal.cancel': 'Cancel',
    'email.modal.invalidEmail': 'Enter a valid email address',
    'email.modal.invalidCode': 'Enter the 6-digit code',
    'email.modal.sendFailed': 'Failed to send code',
    'email.modal.verifyFailed': 'Verification failed',
    'email.modal.success': 'Email bound successfully',
    'email.reminder.title': 'Bind your email',
    'email.reminder.body':
      'Secure your account: add an email to enable account recovery and receive important notifications.',
    'email.reminder.later': 'Maybe later',
    'email.reminder.bind': 'Bind now',

    'portal.hero.badge': 'xAPI Protocol: The universal layer for AI Agents',
    'portal.hero.title': 'XAPI: API Platform for Agents',
    'portal.hero.description':
      'Pay once to unlock every API — Web Search, Twitter, Reddit, TikTok, SMS, AI models, and Crypto data.',
    'portal.hero.descriptionMobile':
      'One key for every API your agent calls — search, social, SMS, LLMs, on-chain.',
    'portal.hero.exploreApis': 'Explore APIs',
    // Homepage Version-A — tabbed hero (provider flow) + stats bar + 4 feature blocks.
    'portal.hero.becomeProvider': 'Become a provider',
    'portal.hero.aDesc':
      'Pay once to call every API — web search, Twitter, AI models, crypto data. Or list your own API and earn on every agent call.',
    'portal.hero.tl1': 'Teach your agent to publish',
    'portal.hero.tlb1': 'Paste this into Cursor / Claude — any agent:',
    'portal.hero.tl2': 'Describe your API',
    'portal.hero.tlb2':
      'Hand it your docs; it registers and wires the endpoints:',
    'portal.hero.tl3': 'Ship it and earn',
    'portal.hero.tlb3':
      'It builds the service, runs tests, submits for review — then bills per call:',
    'portal.feat.stat.calls': 'Total calls',
    'portal.feat.stat.apis': 'APIs · endpoints',
    'portal.feat.stat.providers': 'Providers',
    'portal.feat.stat.uptime': 'Uptime',
    'portal.feat.b1.chip': 'Users',
    'portal.feat.b1.title': 'One key, one bill, call every API',
    'portal.feat.b1.desc':
      'One gateway, one key, unified billing — tap into every API on the market. AI, search, social and crypto data, built agent-native.',
    'portal.feat.b1.cta': 'Get an API key',
    'portal.feat.b2.chip': 'Providers',
    'portal.feat.b2.title': 'Turn your API into recurring revenue',
    'portal.feat.b2.desc':
      'List once, earn on every call. Real-time settlement, withdraw anytime.',
    'portal.feat.b2.cta': 'Become a provider',
    'portal.feat.b3.title': 'Connect any agent to xAPI in minutes with Skills',
    'portal.feat.b3.desc':
      'Install the xAPI Skill and let agents auto-discover, authenticate and call any API with a single key.',
    'portal.feat.b3.cta': 'Explore Skills',
    'portal.feat.b4.chip': 'AI',
    'portal.feat.b4.title': 'One endpoint, every AI model',
    'portal.feat.b4.desc':
      'Better prices and smart routing across providers — no separate integration per model.',
    'portal.feat.b4.cta': 'xAPI AI Router',
    'portal.feat.skill.line': 'register api.example.com',
    'portal.feat.skill.ok': 'service created · 12 endpoints · live',

    'portal.howto.title': 'How to use xAPI',
    'portal.howto.steps': '3 steps · ~60s',
    'portal.howto.s1.title': 'Teach your agent the xAPI skill',
    'portal.howto.s1.body': 'Paste into Cursor / Claude / any agent:',
    'portal.howto.s1.cmd':
      'install xAPI skill: npx skills add xapi-labs/xapi-cli',
    'portal.howto.s2.title': 'Provide an API Key',
    'portal.howto.s2.body':
      'Paste this prompt — the agent registers an account for you and returns the key:',
    'portal.howto.s2.example':
      'register me an xAPI account, invite code: {CODE}',
    'portal.howto.s3.title': 'Watch it work',
    'portal.howto.s3.body':
      'Your agent picks the right API and calls it. For example:',
    'portal.howto.s3.example': "fetch Elon Musk's last 10 tweets",
    'portal.howto.skill': "What's a skill?",
    'portal.howto.orMcp': 'Or use MCP',

    'portal.popular.title': 'Production APIs, vetted.',
    'portal.popular.viewAll': 'View all',
    'portal.popular.empty.title': 'No published APIs yet',

    // ---- Portal hero terminal (3-step CLI demo) ----
    'portal.kicker': 'universal API gateway',
    'portal.heroTerm.s1.title': 'Teach your agent the xAPI skill',
    'portal.heroTerm.s2.title': 'Set your API key',
    'portal.heroTerm.s3.title': 'Watch one call go through',
    'portal.heroTerm.s3.userMsg': "fetch elonmusk's last 3 tweets",
    'portal.heroTerm.s3.respLine1':
      '{ "user": "elonmusk", "count": 3, "tweets": [',
    'portal.heroTerm.s3.respLine2':
      '  { "id":"1789…", "text":"raptor 3 hot fire", "likes":412300 },',
    'portal.heroTerm.s3.respLine3':
      '  { "id":"1789…", "text":"interesting times", "likes":156800 } ]',

    // ---- Portal sections (homepage rhythm) ----
    'portal.section01.eyebrow': 'the marketplace',
    'portal.section01.live': '{N} live',
    'portal.section01.titleA': 'Production APIs',
    'portal.section01.titleB': 'agent-ready.',
    'portal.section01.viewAll': 'Browse all',
    'portal.section01.empty.title': 'No published APIs yet',
    'portal.section01.empty.body':
      "The marketplace is waiting for its first API. If you're a provider, register one — approval and first call typically within 24h.",
    'portal.section01.empty.register': 'Register an API',

    'portal.section02.eyebrow': 'the proposition',
    'portal.section02.titleA': 'One API Key',
    'portal.section02.titleB': 'every API, pay per call.',

    'portal.cap.01.kicker': 'ROUTE',
    'portal.cap.01.titleA': 'One key.',
    'portal.cap.01.titleB': 'Every API.',
    'portal.cap.01.body':
      'Built-in MCP server resolves any tool from a single endpoint — Claude, Cursor, your custom agent — point at mcp.xapi.to and you’re done.',
    'portal.cap.01.fact.live': 'live',
    'portal.cap.01.fact.tools': '500+ APIs',
    'portal.cap.01.fact.tail': 'MCP-ready',

    'portal.cap.02.kicker': 'METER',
    'portal.cap.02.titleA': 'Single-hop proxy.',
    'portal.cap.02.titleB': '~30ms overhead.',
    'portal.cap.02.body':
      'Edge gateway sits next to upstream regions. We add auth, metering and billing in the same TCP round — no extra hops.',
    'portal.cap.02.fact.head': 'p50',
    'portal.cap.02.fact.tail': 'streaming + non-streaming',

    'portal.cap.03.kicker': 'BILL',
    'portal.cap.03.titleA': 'Pay-as-you-call.',
    'portal.cap.03.titleB': 'Stable-coin.',
    'portal.cap.03.body':
      'One balance covers every API on the gateway. Per-call, per-token or per-resource — debited atomically before forwarding. No monthly minimums.',
    'portal.cap.03.fact.head': 'from',
    'portal.cap.03.fact.tail': 'USDC / USDT / USD',

    'portal.cap.04.kicker': 'EARN',
    'portal.cap.04.titleA': 'Providers keep',
    'portal.cap.04.titleB': '80%.',
    'portal.cap.04.body':
      'Drop an OpenAPI spec, set pricing, hit publish. xapi handles auth, metering, billing — you keep 80% of every call.',
    'portal.cap.04.fact.share': '80% rev share',

    'portal.section03.eyebrow': 'capabilities',
    'portal.section03.titleA': 'Capabilities',
    'portal.section03.titleB': 'agents call now.',
    'portal.section03.summary':
      'One gateway for the API work agents actually do — fetch, enrich, call, render, settle.',
    'portal.section03.point1': 'AI / Search / Social / On-chain',
    'portal.section03.point2': 'call / token / image / free',
    'portal.section03.point3': 'auth / logs / quotas / discovery',
    'portal.section03.visualLive': 'live',
    'portal.section03.visualConnecting': 'connecting…',
    'portal.section03.actionsCount': '{N} actions live',
    'portal.section03.colCategory': 'CATEGORY',
    'portal.section03.colActions': 'ACTIONS',
    'portal.section03.loading': 'loading…',
    'portal.section03.more': '+{N} more',

    'portal.audiences.eyebrow': 'for both sides',
    'portal.audiences.titleA': 'For callers,',
    'portal.audiences.titleB': 'and for providers.',
    'portal.audiences.consumer.chip': 'Consumers',
    'portal.audiences.consumer.title': 'Call any API, instantly',
    'portal.audiences.consumer.desc':
      'One gateway, one key — reach every API and capability in the marketplace, agent-ready out of the box.',
    'portal.audiences.consumer.p1': 'One key, unified gateway',
    'portal.audiences.consumer.p2': 'Pay per call, no lock-in',
    'portal.audiences.consumer.p3': 'AI · search · social · on-chain',
    'portal.audiences.consumer.cta': 'Get an API key',
    'portal.audiences.provider.chip': 'Providers',
    'portal.audiences.provider.title': 'Turn your API into agent revenue',
    'portal.audiences.provider.desc':
      'List once, earn on every agent call. Billing, auth, routing and packaging — all handled.',
    'portal.audiences.provider.p1': 'Real-time settlement, withdraw anytime',
    'portal.audiences.provider.p2': 'API → agent skill in minutes',
    'portal.audiences.provider.p3': 'Gateway-managed billing & auth',
    'portal.audiences.provider.cta': 'Become a provider',
    'portal.cta.eyebrow': 'ready when you are',
    'portal.cta.titleA': 'Get an API key',
    'portal.cta.titleB': 'or publish your first endpoint.',
    'portal.cta.register': 'Register an API',

    'portal.getstarted.eyebrow': 'get started',
    'portal.getstarted.titleA': 'Two steps',
    'portal.getstarted.titleB': 'and your agent calls a real API.',
    'portal.getstarted.s1.title': 'Install the skill',
    'portal.getstarted.s1.body':
      'One agent skill that hands your AI the live xapi tool catalog. Works with Claude Code, Cursor, Codex CLI, Gemini CLI, and any MCP-compatible harness.',
    'portal.getstarted.s1.bodyMobile':
      'Loads the live xapi tool catalog into Claude Code, Cursor, Codex, or any MCP agent.',
    'portal.getstarted.s1.cmd': 'npx skills add xapi-labs/xapi-cli',
    'portal.getstarted.s1.note': 'xAPI Skill is open source on GitHub: {LINK}',
    'portal.getstarted.s2.title': 'Use it',
    'portal.getstarted.s2.body':
      'Type /xapi followed by what you want. The skill picks the right action, fills params, and routes through xapi — usage is metered to your key.',
    'portal.getstarted.s2.bodyMobile':
      'Type /xapi + what you want. The skill picks the action and meters usage to your key.',
    'portal.getstarted.s2.ex1.cmd':
      '/xapi find tweets from @elonmusk this week',
    'portal.getstarted.s2.ex2.cmd':
      '/xapi google search "agent frameworks 2026"',
    'portal.getstarted.s2.ex3.cmd':
      '/xapi rent a US number for an OpenAI signup',
    // Skill page — role-based onboarding guide (consumer / provider)
    'skill.role.consumer': 'Consumer',
    'skill.role.provider': 'Provider',
    'skill.role.subConsumer': 'Call any API from your agent — one key, usage metered to it.',
    'skill.role.subProvider': 'Register, price and manage your own APIs from your agent.',
    'skill.provider.install.cmd': 'npx skills add xapi-labs/xapi-provider',
    'skill.provider.s1.body':
      'The provider skill hands your agent the tools to register, price and manage your APIs on xapi — straight from the CLI. Works with Claude Code, Cursor, Codex CLI, Gemini CLI and any MCP harness.',
    'skill.provider.s1.note': 'xAPI Provider skill is open source on GitHub: {LINK}',
    'skill.provider.s2.body':
      'Type /xapi followed by what you want to publish. The skill scaffolds the service, sets pricing and walks it through review.',
    'skill.provider.s2.ex1.cmd': '/xapi register api.weather.com as a service',
    'skill.provider.s2.ex2.cmd': '/xapi price my weather endpoint at $0.002 per call',
    'skill.provider.s2.ex3.cmd': '/xapi submit my API for review',
    'skill.c.keystep.title': 'Get an API key & give it to your agent',
    'skill.p.keystep.title': 'Register, scope & hand off your API key',
    'skill.scopes.more': 'Show all scopes',
    'skill.scopes.less': 'Collapse',
    'skill.title.consumer': 'Call any API from your agent',
    'skill.title.provider': 'Publish your API from your agent',
    'skill.quickref': 'QUICK START',
    'skill.quickref.install': 'Install',
    'skill.quickref.repo': 'Open source: {LINK}',
    'skill.quickref.worksWith': 'Works with',
    // — Consumer guide —
    'skill.c.s1.title': 'Get an API key',
    'skill.c.s1.body': 'Sign up and your key (sk-…) is created automatically — it is how usage is metered and billed. Grab it from the console, or let the skill run `xapi register` for you on first use.',
    'skill.c.s1.cta': 'Get an API key',
    'skill.c.s2.title': 'Install the skill',
    'skill.c.s2.body': 'One agent skill that hands your AI the live xapi tool catalog. Works with Claude Code, Cursor, Codex CLI, Gemini CLI and any MCP harness.',
    'skill.c.s2.note': 'xAPI Skill is open source on GitHub: {LINK}',
    'skill.c.s3.title': 'Give your agent the key',
    'skill.c.s3.body': 'Paste your sk-… key into the agent chat the first time it asks — shell env vars don’t reach an LLM in another process. For the CLI directly, run `xapi config set apiKey=sk-…`.',
    'skill.c.s4.title': 'Start calling',
    'skill.c.s4.body': 'Type /xapi and what you want. The skill picks the action, fills params and routes through xapi — metered to your key.',
    'skill.c.s4.ex1': '/xapi find tweets from @elonmusk this week',
    'skill.c.s4.ex2': '/xapi google search "agent frameworks 2026"',
    'skill.c.s4.ex3': '/xapi rent a US number for an OpenAI signup',
    // — Provider guide —
    'skill.p.s1.title': 'Register & scope your API key',
    'skill.p.s1.body': 'You need an entity account — sign in with X. Then open the console → API Keys → Permissions and enable the scopes below (every scope is off by default; the agent gets 403 without them).',
    'skill.p.s1.cta': 'Open API Keys',
    'skill.p.s1.scopesLabel': 'Scopes the provider skill needs',
    'skill.p.s1.scope.create': 'service:create — register / create a service',
    'skill.p.s1.scope.read': 'service:read — read services & review reports',
    'skill.p.s1.scope.update': 'service:update — edit contracts',
    'skill.p.s1.scope.version': 'version:create — new major / working revision',
    'skill.p.s1.scope.publish': 'service:publish — submit for review / go live',
    'skill.p.s2.title': 'Install the provider skill',
    'skill.p.s2.body': 'The provider skill teaches your agent the full lifecycle: register → configure → auto-review → publish → version → roll back. Works with any MCP harness.',
    'skill.p.s2.note': 'xAPI Provider skill is open source on GitHub: {LINK}',
    'skill.p.s3.title': 'Give your agent the key',
    'skill.p.s3.body': 'Hand your scoped sk-… key to the agent, then just describe what to publish — the skill handles the OpenAPI, pricing and review flow.',
    'skill.p.s4.title': 'Go live & earn',
    'skill.p.s4.body': 'Register your OpenAPI spec, submit for automated review, and once published you earn on every call. Real-time settlement, withdraw anytime.',
    'skill.p.s4.ex1': '/xapi register this openapi.json as a service',
    'skill.p.s4.ex2': '/xapi price my weather endpoint at $0.002 per call',
    'skill.p.s4.ex3': '/xapi submit my API for review, then publish',
    'portal.getstarted.cli.title': 'Install the CLI',
    'portal.getstarted.cli.badge': 'OPTIONAL',
    'portal.getstarted.cli.body':
      'Run xapi calls outside the skill: in CI, scripts, or one-off shell pipes. Same auth, same metering, no editor required.',
    'portal.getstarted.cli.cmd': 'npm i -g xapi-to',
    'portal.getstarted.cli.note':
      'Then `npx xapi-to list` to browse capabilities, or `xapi-to run twitter.search --q "@elonmusk AI"` to fire actions from any shell.',
    'portal.getstarted.stay.title': 'Stay updated',
    'portal.getstarted.stay.body':
      'New actions, providers, and pricing changes ship weekly. Follow @xapi_to on X for the changelog and the occasional outage post-mortem.',
    'portal.getstarted.stay.cta': 'Follow @xapi_to on X',
    'portal.getstarted.expand': 'Show',
    'portal.getstarted.collapse': 'Hide',

    'portal.footer.copyright': '© 2026 xAPI',
    'portal.footer.twitter': 'X (Twitter)',
    'portal.footer.telegram': 'Telegram',
    'portal.footer.github': 'GitHub',

    // ---- AI Relay (中转站) marketing page ----
    'ai.kicker': 'ai model gateway',
    'ai.hero.eyebrow': 'one key · every model',
    'ai.hero.titleA': 'One API key',
    'ai.hero.titleB': 'every model, every protocol.',
    'ai.hero.description':
      'Point any AI request to https://ai.xapi.to — one key, the best price, and smart routing across providers.',
    'ai.hero.descriptionMobile':
      'Anthropic-compatible smart router — one key, auto-failover across all upstreams.',
    'ai.hero.cta.primary': 'Get an API key',
    'ai.hero.cta.secondary': 'View docs',
    'ai.hero.cta.models': 'Browse all models',
    'ai.hero.baseUrl.label': 'ANTHROPIC_BASE_URL',
    'ai.hero.term.userMsg': 'refactor this function',
    'ai.hero.term.baseUrlNote': 'the only line you change',
    'ai.hero.term.cc.note':
      '# Claude Code → xapi: change one env var, zero code',
    'ai.hero.term.codex.then': '# then launch',
    'ai.hero.term.copy': 'copy',
    'ai.hero.term.copied': 'copied',
    'ai.hero.term.routeLabel': 'auto-route · best upstream',
    'ai.hero.term.cheapestHealthy': ' (cheapest healthy)',
    'ai.hero.term.healthy': 'healthy',
    'ai.hero.term.save': 'save {P}%',
    'ai.hero.term.metric1.k': 'ttft',
    'ai.hero.term.metric2.k': 'tokens',
    'ai.hero.term.metric3.k': 'cost',

    // Stats strip — section 01 (light density row under the hero)
    'ai.stats.providers': '{N} upstream relays integrated',
    'ai.stats.calls': '{N} calls in last 30d',
    'ai.stats.models': '{N}+ models with live pricing',
    'ai.stats.compat': 'OpenAI · Claude · Responses',

    // Section 02 — real per-upstream pricing
    'ai.catalog.eyebrow': 'live pricing',
    'ai.catalog.categoryFilter': 'Model type',
    'ai.catalog.category.chat': 'Chat', 'ai.catalog.category.decisions': 'Decisions', 'ai.catalog.category.tts': 'TTS', 'ai.catalog.category.stt': 'STT',
    'ai.catalog.actualCost': 'estimated · actual usage', 'ai.catalog.dynamicCost': 'dynamic billing',
    'ai.catalog.titleA': 'Real prices,',
    'ai.catalog.titleB': 'per upstream · per model.',
    'ai.catalog.summary':
      "Browse chat and decision models. Prices are USD per million tokens. Dynamic quotes are estimated from upstream rates and configured markup; actual charges follow request usage.",
    'ai.catalog.colUpstream': 'UPSTREAM',
    'ai.catalog.colModel': 'MODEL',
    'ai.catalog.colFamily': 'FAMILY',
    'ai.catalog.colIn': 'INPUT',
    'ai.catalog.colOut': 'OUTPUT',
    'ai.catalog.colInUpstream': 'INPUT',
    'ai.catalog.colOutUpstream': 'OUTPUT',
    'ai.catalog.colNote': 'NOTE',
    'ai.catalog.colProviders': 'PROVIDERS',
    'ai.catalog.colCalls': 'CALLS / 30D',
    'ai.catalog.tokensVia': '{N} tokens',
    'ai.catalog.upstreamsTitle': 'Upstreams · cheapest first',
    'ai.catalog.noUpstreams': 'No upstream detail',
    'ai.catalog.viewCards': 'Cards',
    'ai.catalog.viewTable': 'Table',
    'ai.catalog.viewList': 'Ranking',
    'ai.catalog.via': 'via',
    'ai.catalog.in': 'in',
    'ai.catalog.out': 'out',
    'ai.catalog.upstreamsN': '{N} upstreams',
    'ai.catalog.filterAll': 'All',
    'ai.catalog.vendorMore': '+{N} more',
    'ai.catalog.vendorLess': 'Collapse',
    'ai.catalog.allHint':
      'unique models · cheapest single-upstream combined price',
    'ai.catalog.bestVia': 'via {U}',
    'ai.catalog.official': 'official',
    'ai.catalog.discountHint': 'OpenRouter official price',
    'ai.catalog.search': 'Search model / vendor',
    'ai.catalog.showAll': 'Browse all models →',
    'ai.catalog.shownCount': 'Showing {X} of {Y}',
    'ai.catalog.noMatch': 'No model matches',

    // Section 03 — why xapi (4 capability cells)
    'ai.cap.eyebrow': 'the proposition',
    'ai.cap.titleA': 'Why route through xapi',
    'ai.cap.titleB': 'instead of stitching your own.',
    'ai.cap.01.kicker': 'COVERAGE',
    'ai.cap.01.title': 'Every model from every lab.',
    'ai.cap.01.body':
      'OpenAI, Anthropic, Google, DeepSeek, Meta, Alibaba, xAI, Mistral — 80+ models behind one endpoint. Add a new model id when it ships, your code stays the same.',
    'ai.cap.01.fact': '80+ models · 9 model families',
    'ai.cap.02.kicker': 'STABILITY',
    'ai.cap.02.title': 'Multi-upstream pool.',
    'ai.cap.02.body':
      'Every model is wired through 2-4 independent upstream relays. When one provider rate-limits or 5xxs, the next one in the pool answers — your request never sees the fault.',
    'ai.cap.02.fact': '99.97% success · auto failover',
    'ai.cap.03.kicker': 'SPEED',
    'ai.cap.03.title': 'Edge-warmed connections.',
    'ai.cap.03.body':
      'Connection-warmed pool to every upstream means no cold-start TLS on your hot path. Streaming pass-through with ~20ms gateway overhead, no buffering.',
    'ai.cap.03.fact': 'p50 TTFT ~190ms · streaming SSE',
    'ai.cap.04.kicker': 'PRICING',
    'ai.cap.04.title': 'Pay-per-token, settle in stables.',
    'ai.cap.04.body':
      'No monthly minimum, no seat licenses. Top up once, every model is priced per-1M-token at upstream rates plus a flat 5% gateway fee. Settle in USDC / USDT / USD.',
    'ai.cap.04.fact': 'no minimum · 5% gateway fee',

    // Section 04 — upstream pool
    'ai.upstream.eyebrow': 'upstream pool · live',
    'ai.upstream.titleA': '{N} live relay providers,',
    'ai.upstream.titleB': 'merged into one pool.',
    'ai.upstream.summary':
      'Real upstream relays the aggregated ai.xapi.to router dispatches to. When you call /v1/messages, the router picks one of these per your chosen strategy. You can also call any upstream directly by its subdomain (e.g. skyapi.ai.xapi.to/v1/chat/completions) when you need OpenAI Chat Completions or other protocols. Numbers below are live marketplace stats.',
    'ai.upstream.callsLabel': 'calls',
    'ai.upstream.empty.title': 'No AI relays published yet',
    'ai.upstream.empty.body':
      "The marketplace doesn't have any AI-category services yet. New relay providers appear here automatically once registered.",

    // Section "multi-protocol" — speak OpenAI + Claude + others natively
    'ai.compat.eyebrow': 'multi-protocol native',
    'ai.compat.titleA': 'Smart router,',
    'ai.compat.titleB': 'direct upstreams.',
    'ai.compat.body':
      'Point Anthropic SDK at https://ai.xapi.to and you get auto-routing + failover for free. Need OpenAI Chat Completions or Responses? Point your OpenAI SDK at a specific upstream subdomain — same key, same billing, different protocol.',
    'ai.compat.bullet1': 'ai.xapi.to/v1/messages — aggregated Anthropic router',
    'ai.compat.bullet2':
      'ai.xapi.to/{quality|speed|cost}/v1/messages — pick strategy',
    'ai.compat.bullet3':
      '{upstream}.ai.xapi.to — per-upstream OpenAI/Responses/Completions',
    'ai.compat.codeTitle':
      'python · anthropic SDK (aggregated) + openai SDK (per-upstream)',

    // Final CTA
    'ai.cta.eyebrow': 'ready when you are',
    'ai.cta.titleA': 'Get an AI key',
    'ai.cta.titleB': 'and call any model in 60 seconds.',
    'ai.cta.primary': 'Get API Key',
    'ai.cta.secondary': 'View docs',

    // ---- Category labels (display-only; the underlying value stays the canonical English ID) ----
    'category.all': 'All',
    'category.aiModels': 'AI Models',
    'category.aiServices': 'AI Services',
    'category.social': 'Social',
    'category.crypto': 'Crypto',
    'category.data': 'Data',
    'category.dataAnalysis': 'Data Analysis',
    'category.search': 'Search',
    'category.media': 'Media',
    'category.devTools': 'Dev Tools',
    'category.sandbox': 'Sandbox',
    'category.publicUtils': 'Public Utils',
    'category.infrastructure': 'Infrastructure',

    // ---- Capability groups ----
    // Group labels come straight from the backend `meta.category` field; only the
    // fallback label for capabilities the backend hasn't categorized lives here.
    'cap.group.uncategorized': 'Uncategorized',

    'market.eyebrow': 'Marketplace',
    'market.title': 'Discover APIs',
    'market.searchPlaceholder': 'Search 500+ APIs',
    'market.results': 'results',
    'market.category': 'Category',
    'market.billing': 'Billing',
    'market.status': 'Status',
    'market.verifiedOnly': 'Verified only',
    'market.filters': 'Filters',
    'market.hideFilters': 'Hide filters',
    'market.showFilters': 'Show filters',
    'market.applyFilters': 'Done',
    'market.billing.perCall': 'Per call',
    'market.billing.perToken': 'Per token',
    'market.billing.freeTier': 'Free tier',
    'market.sort.popular': 'Most popular',
    'market.sort.new': 'Recently added',
    'market.sort.latency': 'Lowest latency',
    'market.loadingApis': 'Loading APIs…',
    'market.empty.title': 'No APIs published yet',
    'market.empty.body':
      "This marketplace is empty. If you're a provider, you can register the first one and start earning on the first call.",
    'market.empty.register': 'Register an API',
    'market.noMatch':
      'No APIs match your filter. Try a different category or search term.',
    'market.favAdd': 'Add to favorites',
    'market.favRemove': 'Remove from favorites',
    'market.col.name': 'Name',
    'market.col.category': 'Category',
    'market.col.endpoints': 'Endpoints',
    'market.col.calls': 'Calls',
    'market.col.p95': 'p95',
    'market.col.uptime': 'Uptime',
    'market.callsSuffix': 'calls',

    'apiDetail.tab.try': 'Try it',
    'apiDetail.tab.endpoints': 'Endpoints',
    'apiDetail.tab.docs': 'Documentation',
    'apiDetail.tab.changelog': 'Changelog',
    'apiDetail.uptime': 'Uptime',
    'apiDetail.p95Latency': 'P95 Latency',
    'apiDetail.totalCalls': 'Total Calls',
    'apiDetail.starred': 'Starred',
    'apiDetail.star': 'Star',
    'apiDetail.useThisApi': 'Use this API',
    'apiDetail.send': 'Send request',
    'apiDetail.abort': 'Abort request',
    'apiDetail.setApiKey': 'Set XAPI_KEY to send',
    'apiDetail.queryParams': 'Query params',
    'apiDetail.pathParams': 'Path params',
    'apiDetail.headers': 'Headers',
    'apiDetail.body': 'Body',
    'apiDetail.response': 'Response',
    'apiDetail.exampleResponse': 'Example response',

    'common.copy': 'Copy',
    'common.copied': 'Copied',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.save': 'Save',
    'common.close': 'Close',
    'common.expand': 'Expand',
    'common.loading': 'Loading…',
    'common.next': 'Next',
    'common.prev': 'Prev',
    'common.page': 'page',
    'common.signIn': 'Sign in',
    'common.add': 'Add',
    'common.remove': 'Remove',
    'common.refresh': 'Refresh',
    'common.actions': 'Actions',
    'common.name': 'Name',
    'common.status': 'Status',
    'common.created': 'Created',
    'common.updated': 'Updated',
    'common.description': 'Description',
    'common.lastDay': 'Last 24h',
    'common.last7d': 'Last 7 days',
    'common.last30d': 'Last 30 days',
    'common.last90d': 'Last 90 days',
    'common.retry': 'Retry',

    'console.nav.overview': 'Overview',
    'console.nav.keys': 'API Keys',
    'console.nav.env': 'Environment',
    'console.nav.usage': 'Usage & Logs',
    'console.nav.aiLogs': 'AI Logs',
    'console.nav.sandbox': 'Sandboxes',
    'console.nav.workers': 'Workers',
    "console.keys.permissions.groups.workers.title": "Workers",
    "console.keys.permissions.workersRead.label": "Read Workers",
    "console.keys.permissions.workersRead.desc": "View Workers, deployments, resources, logs and billing",
    "console.keys.permissions.workersWrite.label": "Manage Workers",
    "console.keys.permissions.workersWrite.desc": "Create, deploy, pause, resume and delete Workers; includes read access",
    "console.keys.workers.access": "Worker instance access",
    "console.keys.workers.all": "All Workers in this account",
    "console.keys.workers.own": "Only Workers created by this key",
    "console.keys.workers.selected": "Own Workers and selected Workers",
    "console.keys.workers.accessNote": "Applies to listing, deployments, resources, secrets, logs and deletion.",
    "console.keys.workers.empty": "No additional Workers available to authorize.",
    "console.keys.workers.updated": "Worker instance access updated",
    "console.keys.workers.failed": "Worker access update failed",
    'console.nav.billing': 'Billing',
    'console.nav.referral': 'Referral',
    'console.nav.settings': 'Settings',
    'console.nav.workspace': 'Workspace',
    'console.nav.register': 'Register API',
    'console.nav.earnings': 'Earnings',
    'console.nav.admin': 'Admin Review',
    'console.nav.providerHeading': 'Provider',
    'console.nav.analytics': 'Analytics',

    // Sandbox instance logs (components/SandboxLogs.jsx) — shared by the consumer
    // /sandbox page and the console's Sandbox tab.
    'sandbox.log.tab.operations': 'Operations',
    'sandbox.log.tab.events': 'State timeline',
    'sandbox.log.tab.segments': 'Billing segments',
    'sandbox.log.tab.periods': 'Settlement ledger',
    'sandbox.log.empty.operations': 'No operations recorded yet.',
    'sandbox.log.empty.events': 'No state changes recorded yet.',
    'sandbox.log.empty.segments': 'No billing segments yet.',
    'sandbox.log.empty.periods': 'No settled billing periods yet.',
    'sandbox.log.loading': 'Loading…',
    'sandbox.log.command': 'Command',
    'sandbox.log.input': 'Input',
    'sandbox.log.stdout': 'Stdout',
    'sandbox.log.stderr': 'Stderr',
    'sandbox.log.truncated': 'Output truncated — only the head is kept',
    'sandbox.log.error': 'Error',
    'sandbox.log.exit': 'exit',
    'sandbox.log.reason': 'reason',
    'sandbox.log.col.state': 'State',
    'sandbox.log.col.kind': 'Kind',
    'sandbox.log.col.window': 'Window',
    'sandbox.log.col.duration': 'Duration',
    'sandbox.log.col.amount': 'Amount',
    'sandbox.log.col.settle': 'Settlement',
    'sandbox.log.segOpen': 'in progress',
    'sandbox.log.segTotal': 'Segment total',
    'sandbox.log.totalCost': 'Instance total',
    'sandbox.log.periodTotal': 'Settlement total',
    'sandbox.log.loadAll': 'Load complete history ({shown} / {total})',

    'console.aiLogs.title': 'AI Logs',
    'console.aiLogs.subtitle':
      'Per-call breakdown for your PER_TOKEN AI usage.',
    'console.aiLogs.signinSubtitle': 'Sign in to view your AI call history.',
    'console.aiLogs.chartTitle': 'Calls per day',
    'console.aiLogs.empty': 'No AI calls in this range.',
    'console.aiLogs.loadMore': 'Load more',
    'console.aiLogs.noMore': 'End of results',
    'console.aiLogs.stat.calls': 'Calls',
    'console.aiLogs.stat.inputTokens': 'Input tokens',
    'console.aiLogs.stat.outputTokens': 'Output tokens',
    'console.aiLogs.stat.cost': 'Total cost',
    'console.aiLogs.stat.providerLatency': 'Avg provider latency',
    'console.aiLogs.stat.throughput': 'Avg throughput',
    'console.aiLogs.filter.allKeys': 'All API Keys',
    'console.aiLogs.filter.allProviders': 'All Providers',
    'console.aiLogs.filter.allModels': 'All Models',
    'console.aiLogs.filter.allEndpoints': 'All Endpoints',
    'console.aiLogs.filter.allOutcomes': 'All Outcomes',
    'console.aiLogs.filter.success': 'Success (2xx)',
    'console.aiLogs.filter.errors': 'Errors (4xx/5xx)',
    'console.aiLogs.origin.USER': 'Account',
    'console.aiLogs.origin.REQUEST': 'Request',
    'console.aiLogs.origin.UPSTREAM': 'Upstream',
    'console.aiLogs.origin.GATEWAY': 'Gateway',
    'console.aiLogs.col.time': 'Time',
    'console.aiLogs.col.apiKey': 'API Key',
    'console.aiLogs.col.provider': 'Provider',
    'console.aiLogs.col.model': 'Model',
    'console.aiLogs.col.input': 'Input',
    'console.aiLogs.col.output': 'Output',
    'console.aiLogs.col.cache': 'Cache',
    'console.aiLogs.col.latency': 'Latency',
    'console.aiLogs.col.cost': 'Cost',
    'console.aiLogs.col.speed': 'Speed',
    'console.aiLogs.col.status': 'Status',
    'console.aiLogs.drawer.title': 'AI call detail',
    'console.aiLogs.drawer.tokens': 'Tokens',
    'console.aiLogs.drawer.cacheRead': 'Cache read',
    'console.aiLogs.drawer.cacheCreate': 'Cache create',
    'console.aiLogs.drawer.total': 'Total',
    'console.aiLogs.drawer.timing': 'Timing',
    'console.usage.drawer.title': 'Call detail',
    'console.aiLogs.drawer.providerLatency': 'Provider latency',
    'console.aiLogs.drawer.generation': 'Generation',
    'console.aiLogs.drawer.gatewayOverhead': 'Gateway overhead',
    'console.aiLogs.drawer.connect': 'Connect (DNS+TCP+TLS)',
    'console.aiLogs.drawer.wait': 'Upstream wait',
    'console.aiLogs.drawer.reused': 'reused',
    'console.aiLogs.drawer.reusedConn': 'Connection reused (keep-alive)',
    'console.aiLogs.drawer.nonStream':
      'Non-streaming response (no generation phase)',
    'console.aiLogs.drawer.responseTime': 'Total time',
    'console.aiLogs.tag.stream': 'stream',
    'console.aiLogs.tag.sync': 'sync',
    'console.aiLogs.tag.streamTitle':
      'Streaming response (chunked, generation phase available)',
    'console.aiLogs.tag.syncTitle':
      'Non-streaming response (whole packet, no generation phase)',
    'console.aiLogs.speed.nonStreamHint':
      'Non-streaming response — tok/s only computable on streaming',
    'console.aiLogs.speed.noData': 'No throughput data',
    'console.endpoint.drawer.title': 'Endpoint · avg phase breakdown',
    'console.endpoint.drawer.subtitle':
      'Avg of {N} recent calls (connect merged; per-call drawer has DNS/TCP/TLS split)',
    'console.aiLogs.drawer.streamDuration': 'Stream duration',
    'console.aiLogs.drawer.upstreamModel': 'Upstream model',
    'console.aiLogs.drawer.billing': 'Billing',
    'console.aiLogs.drawer.request': 'Request',
    'console.aiLogs.drawer.endpoint': 'Endpoint',
    'console.aiLogs.drawer.error': 'Error',
    'console.aiLogs.drawer.errorOrigin': 'Fault origin',
    'console.aiLogs.drawer.errorCode': 'Error code',
    'console.aiLogs.drawer.errorMessage': 'Error message',

    'console.overview.title': 'Overview',
    'console.overview.welcome': 'Welcome back, {USER}',
    'console.overview.subtitle': 'Your activity across the {RANGE}.',
    'console.overview.signinSubtitle': 'Sign in to see your real usage.',
    'console.overview.stat.balance': 'Balance',
    'console.overview.stat.spent': 'Spent',
    'console.overview.stat.calls': 'Calls',
    'console.overview.stat.apiKeys': 'API Keys',
    'console.overview.callsTitle': 'Calls · {RANGE}',
    'console.overview.activityTitle': 'Recent activity',
    'console.overview.dailySpend': 'Daily spend',
    'console.overview.topApis': 'Top APIs · {RANGE}',
    'console.overview.topApisCount': 'APIs',
    'console.overview.topApisEmpty': 'No API calls in this period yet.',
    'console.overview.loadErr': 'Some stats failed to load.',
    'console.overview.vsPrev': 'vs previous period',
    'console.overview.allApis': 'All APIs',
    'console.overview.col.time': 'Time',
    'console.overview.col.api': 'API Service',
    'console.overview.col.endpoint': 'Endpoint',
    'console.overview.col.path': 'Method & Path',
    'console.overview.col.status': 'Status',
    'console.overview.col.cost': 'Cost',
    'console.overview.col.key': 'API Key',
    'console.overview.empty': 'No calls yet — make one via Try-It or our SDK.',

    "console.keys.permissions.groups.runtime.title": "Hosted agents",
    "console.keys.permissions.groups.runtime.note": "This is a permission on a regular API key; it does not change the key type.",
    "console.keys.permissions.runtime.label": "Use XNT Cloud",
    "console.keys.permissions.runtime.desc": "Access the hosted agent environment, including this account’s sessions and sandboxes. Use a dedicated key with a spend limit.",
    "console.keys.permissions.groups.sandbox.title": "Sandbox",
    "console.keys.permissions.sandbox.label": "Sandbox access",
    "console.keys.permissions.sandbox.desc": "View and call your unpublished sandbox APIs through MCP.",
    "console.keys.permissions.groups.agent.title": "Agent management",
    "console.keys.permissions.groups.agent.note": "Let an agent use this key to manage API services programmatically.",
    "console.keys.permissions.serviceCreate.label": "Create / register",
    "console.keys.permissions.serviceCreate.desc": "Create or register new API services.",
    "console.keys.permissions.serviceRead.label": "Read",
    "console.keys.permissions.serviceRead.desc": "Read your services, versions, and review reports.",
    "console.keys.permissions.serviceUpdate.label": "Update",
    "console.keys.permissions.serviceUpdate.desc": "Modify version contracts, including endpoints, billing, and upstream settings.",
    "console.keys.permissions.versionCreate.label": "Create version",
    "console.keys.permissions.versionCreate.desc": "Create a major version or a working copy.",
    "console.keys.permissions.servicePublish.label": "Publish",
    "console.keys.permissions.servicePublish.desc": "Submit for review and publish. Automated review still applies.",
    "console.keys.permissions.serviceRollback.label": "Roll back",
    "console.keys.permissions.serviceRollback.desc": "Roll back or switch the default major version.",
    "console.keys.permissions.serviceDelete.label": "Delete",
    "console.keys.permissions.serviceDelete.desc": "Unpublish or delete services.",
    "console.keys.permissions.groups.skills.title": "Skill marketplace",
    "console.keys.permissions.groups.skills.note": "Use this key to upload, update, or submit xAPI skills.",
    "console.keys.permissions.skillRead.desc": "Read your skills, submission status, and skill specifications.",
    "console.keys.permissions.skillCreate.label": "Create",
    "console.keys.permissions.skillCreate.desc": "Reserve a skill slug and create a draft.",
    "console.keys.permissions.skillUpdate.label": "Upload / update",
    "console.keys.permissions.skillUpdate.desc": "Import from GitHub, a folder, or a ZIP archive and save a new version.",
    "console.keys.permissions.skillSubmit.label": "Submit for review",
    "console.keys.permissions.skillSubmit.desc": "Submit a skill package for automated and manual review.",
    "console.keys.permissions.count": "{n} enabled",
    "console.keys.permissions.none": "None enabled",
    "console.keys.permissions.manage": "Manage",
    "console.keys.permissions.title": "Permissions",
    "console.keys.permissions.intro": "Control what this API key can do. Permissions are off by default; enable them as needed. High-risk permissions let an agent modify your live services.",
    "console.keys.permissions.highRisk": "High risk",
    "console.keys.permissions.settingsDesc": "Hosted agents, sandbox access, and programmatic management permissions",
    "console.keys.limit.zero": "$0 · set limit to use",
    "console.keys.loadFailed": "Failed to load keys",
    "console.keys.updateFailed": "Update failed",
    "console.keys.limit.updated": "Limit updated",
    "console.keys.limit.failed": "Limit update failed",
    "console.keys.ip.updated": "IP allowlist updated",
    "console.keys.ip.failed": "IP allowlist update failed",
    "console.keys.deleted": "Key deleted",
    "console.keys.deleteFailed": "Delete failed",
    "console.keys.oauth.connected": "Provider connected",
    "console.keys.oauth.connectFailed": "Provider connection failed",
    "console.keys.oauth.pickFirst": "Pick a provider first",
    "console.keys.oauth.noUrl": "Authorization URL not returned",
    "console.keys.oauth.startFailed": "Could not start OAuth",
    "console.keys.oauth.revoked": "Binding revoked",
    "console.keys.oauth.revokeFailed": "Revoke failed",
    "console.keys.signInKey": "Sign-in key",
    "console.keys.created": "Key created — copy it now",
    "console.keys.createFailed": "Create key failed",
    "console.keys.verifyRequired": "Verify with X to manage API keys.",
    "console.keys.sandboxEnabled": "Sandbox enabled",
    "console.keys.sandboxDisabled": "Sandbox disabled",
    "console.keys.deleteConfirm": "Delete key \"{name}\"? This cannot be undone.",
    "console.keys.oauth.provider": "Provider",
    "console.keys.delete": "Delete key",
    "console.keys.verifyCreate": "Verify with X to create keys",
    "console.keys.signedOut": "You're not signed in.",
    "console.keys.signIn": "Go to Sign in",
    "console.keys.notFound": "No key found on this account.",
    "console.keys.loading": "Loading keys…",
    "console.keys.empty": "No API keys yet.",
    "console.keys.createFirst": "Create your first key",
    "console.keys.verify": "Verify with X",
    "console.keys.create": "Create key",
    "console.keys.totalCalls": "Total calls",
    "console.keys.spent": "Spent",
    "console.keys.usage": "Usage",
    "console.keys.active": "active",
    "console.keys.oauth.revoking": "Revoking…",
    "console.keys.verifyHint": "You signed in with this key. Link an X account to create more keys, set spend limits, or publish APIs.",
    "console.keys.readOnlyBanner": "You signed in with an API key and have read-only access. Verify with X to create or delete keys and edit spend limits.",
    "console.keys.limit.of": "of ${amount}",
    "console.keys.oauth.via": "via {provider}",
    "console.keys.oauth.revokeTitle": "Revoke {provider}?",
    "console.keys.oauth.revokeDesc": "The stored token will be destroyed. Calls that depend on {provider} will fail until you reconnect.",
    "console.keys.nameRequired": "Name is required",
    "console.keys.createTitle": "Create API key",
    "console.keys.namePlaceholder": "e.g. Production",
    "console.keys.createHint": "Configure spend limits, permissions, and provider connections after creating the key.",
    "console.keys.reveal.title": "Key \"{name}\" created",
    "console.keys.reveal.copyNow": "Copy this key now.",
    "console.keys.reveal.warning": "For security, this key is shown only once. We store a hash and cannot display the key again.",
    "console.keys.reveal.label": "Your new API key",
    "console.keys.reveal.envDesc": "Set from key creation",
    "console.keys.reveal.envUpdated": "XAPI_KEY environment variable updated",
    "console.keys.reveal.useEnv": "Use as XAPI_KEY",
    "console.keys.reveal.saved": "I've saved it",
    "console.keys.saving": "Saving…",
    "console.keys.lastUsed.never": "never",
    "console.keys.lastUsed.now": "just now",
    "console.keys.lastUsed.minutes": "{n}m ago",
    "console.keys.lastUsed.hours": "{n}h ago",
    "console.keys.lastUsed.days": "{n}d ago",

    'console.keys.readOnlyLogin': 'API key login (read-only)',
    'console.keys.readOnlyCapabilities': 'You can call APIs, top up, and browse the marketplace. Verify with X to manage keys, view detailed usage, or publish APIs.',
    'console.keys.title': 'API Keys',
    'console.keys.subtitle':
      'Manage spend limits, permissions, IP allowlists, and third-party connections for each key.',
    'console.keys.signinSubtitle': 'Sign in to manage your keys.',
    'console.keys.readOnlySubtitle':
      "API key login is read-only — you see the key you signed in with but can't create or revoke keys. Verify with X to unlock key management.",
    'console.keys.createNew': 'New key',
    'console.keys.manage': 'Manage settings',
    'console.keys.done': 'Done',

    'console.keys.col.name': 'Name',
    'console.keys.col.key': 'Key',
    'console.keys.col.connect': 'Connect',
    'console.keys.col.limit': 'Limit',
    'console.keys.col.ipAllowlist': 'IP allowlist',
    'console.keys.col.features': 'Features',
    'console.keys.col.lastUsed': 'Last used',
    'console.keys.settings.title': 'Key settings',
    'console.keys.ip.title': 'IP allowlist',
    'console.keys.ip.desc':
      'Restrict this key to selected public IPv4/IPv6 addresses or CIDR ranges.',
    'console.keys.ip.unrestricted': 'Unrestricted',
    'console.keys.ip.entries': '{n} entries',
    'console.keys.ip.hint':
      'Enter one IP or CIDR per line. Leave empty to allow requests from any IP.',
    'console.keys.ip.placeholder': '203.0.113.10\n2001:db8:1234::/64',
    'console.keys.ip.save': 'Save allowlist',
    'console.keys.flag.sandbox.label': 'Sandbox',
    'console.keys.flag.sandbox.desc':
      'Callable from public MCP & browser playgrounds',
    // Backed by ApiKey.allowRegister — gates POST /v1/agent/register-api-service.
    // Was mislabeled "Provider credential" which implied an upstream secret;
    // it actually controls whether THIS key can register new API services on
    // the gateway via the agent endpoint.
    'console.keys.flag.provider.label': 'Allow API registration',
    'console.keys.flag.provider.desc':
      'Permit this key to register new API services via the /agent/register-api-service endpoint',

    // Per-key cumulative spend cap (NOT monthly — there's no auto-reset).
    // Backed by ApiKey.balanceLimit + usedBalance; once usedBalance ≥
    // balanceLimit the key is blocked. null means no cap (key shares the
    // account balance). Matches xapi-new wording ("usageCap").
    'console.keys.limit.title': 'Usage cap',
    'console.keys.limit.desc':
      'Total spend allowed on this key before it stops accepting calls. Resets only via manual reset or top-up.',
    'console.keys.limit.noLimit': 'no cap',
    'console.keys.limit.perMonth': 'total',
    'console.keys.limit.edit': 'Edit',

    'console.keys.oauth.title': 'OAuth bindings',
    'console.keys.oauth.activeCount': '{n} active',
    'console.keys.oauth.availableCount': '{n} available',
    'console.keys.oauth.desc':
      'Calls made with this key inject the matched token upstream. Secrets stay in the vault.',
    'console.keys.oauth.empty': 'No OAuth bindings yet.',
    'console.keys.oauth.connect': 'Connect a provider',
    'console.keys.oauth.addMore': 'Add more',
    'console.keys.oauth.addProvider': 'Add provider',
    'console.keys.oauth.legacyHint':
      'This key was created before the OAuth feature was added. Enable OAuth on this key, or create a new one.',
    'console.keys.oauth.enableCta': 'Enable OAuth',
    'console.keys.oauth.enableTitle': 'Enable OAuth on this key',
    'console.keys.oauth.enableNote':
      'Paste the plaintext API key once. We derive an X25519 public key from it and store only the public half — the plaintext is never written to disk.',
    'console.keys.oauth.enablePlaintextLabel': 'Plaintext API key',
    'console.keys.oauth.enablePlaintextHint':
      'Used once for derivation. If you no longer have the plaintext, create a new key instead.',
    'console.keys.oauth.enabling': 'Enabling…',
    'console.keys.oauth.enableConfirm': 'Enable',
    'console.keys.oauth.enableSuccess': 'OAuth enabled for this key',
    'console.keys.oauth.enableFail': 'Failed to enable OAuth',
    'console.keys.oauth.manageWithBindings':
      'Manage settings · {n} OAuth binding(s)',

    'console.oauth.dialog.title': 'Connect {provider}',
    'console.oauth.dialog.toKey': 'To key',
    'console.oauth.dialog.consentNote':
      "We'll open {provider}'s OAuth consent. The token is encrypted at rest and injected only on calls made with this key.",
    'console.oauth.dialog.requestedScopes': 'Requested scopes',
    'console.oauth.dialog.basicTitle': 'Basic',
    'console.oauth.dialog.requiredHint': 'required',
    'console.oauth.dialog.advancedTitle': 'Advanced',
    'console.oauth.dialog.scopesTitle': 'Scopes',
    'console.oauth.dialog.optionalHint': 'optional',
    'console.oauth.dialog.selectAll': 'Select all',
    'console.oauth.dialog.deselectAll': 'Deselect all',
    'console.oauth.dialog.cancel': 'Cancel',
    'console.oauth.dialog.opening': 'Opening…',
    'console.oauth.dialog.continue': 'Continue',
    'console.oauth.dialog.back': 'Back',
    'console.oauth.dialog.pickProviderTitle': 'Choose a provider',
    'console.oauth.dialog.noAvailableProviders':
      'No providers available — this key is already bound to every active provider.',
    'console.oauth.dialog.alreadyConnectedTitle': 'Connected to {provider}',
    'console.oauth.dialog.bound': 'Bound',
    'console.oauth.dialog.grantedScopes': 'Granted scopes',
    'console.oauth.dialog.modifyHint':
      '{n} more scope(s) available — click "Modify scopes" to grant.',
    'console.oauth.dialog.modifyScopes': 'Modify scopes',
    'console.oauth.dialog.reauthorize': 'Re-authorize',
    'console.oauth.dialog.close': 'Close',
    'console.oauth.revoke': 'Revoke',
    'console.keys.limit.usedNoLimit': 'spent · no cap',

    // API health card (api-detail page)
    'console.health.title': 'API health',
    'console.health.score24h': '24h health',
    'console.health.avgLatency24h': '24h avg latency',
    'console.health.lastCheck': 'Last activity',
    'console.health.refresh': 'Refresh',
    'console.health.trend24h': '24-hour trend',
    'console.health.trend30d': '30-day trend',
    'console.health.status.healthy': 'Healthy',
    'console.health.status.available': 'Available',
    'console.health.status.degraded': 'Partial outage',
    'console.health.status.down': 'Mostly down',
    'console.health.status.unknown': 'No data',
    'console.health.endpoints.title': 'Per-endpoint',
    'console.health.endpoints.sort.worst': 'Worst first',
    'console.health.endpoints.sort.calls': 'Most calls',
    'console.health.endpoints.sort.path': 'Path A→Z',
    'console.health.endpoints.empty': 'No endpoints',

    'console.env.title': 'Environment',
    'console.env.subtitle':
      'Local-only variables injected into Try-It, MCP config, and code examples. Stored in your browser — xapi servers never receive these values.',

    'console.usage.title': 'Usage & Logs',
    'console.usage.subtitle': 'Every call through your keys, in real time.',
    'console.usage.subtitleN': '{N} total calls',
    'console.usage.signinSubtitle': 'Sign in to view your call history.',

    'console.billing.title': 'Billing',
    'console.billing.subtitle': 'Balance, top-ups, and transactions.',
    'console.billing.signinSubtitle': 'Sign in to view your billing.',
    'console.billing.balance': 'Current balance',
    'console.billing.balanceHint': 'Available for API usage',
    'console.billing.recharged': 'Total recharged',
    'console.billing.spent': 'Total spent (all time)',
    'console.billing.transactions': 'Transactions',
    'console.billing.manualLabel': 'One-time payment',
    'console.billing.manualTitle': 'Add funds',
    'console.billing.manualDescription': 'Choose an amount and pay with a card or your crypto wallet.',
    'console.billing.manualMethods': 'Card · Base USDC · BSC stablecoins',
    'console.billing.manualAction': 'Top up now',
    'console.billing.manualClose': 'Hide payment form',
    'console.billing.autoDiscoveryAction': 'Auto top-up settings',
    'topup.amount': 'Amount (USD)',
    'topup.paymentMethod': 'Payment method',
    'topup.card': 'Card',
    'topup.cardHint': 'Visa, Mastercard, Amex · no extra fee',
    'topup.useCrypto': 'Use crypto',
    'topup.cardDescription': 'Add credits securely with your bank card.',
    'topup.cryptoDescription': 'Add credits with stablecoins from your wallet.',
    'topup.cardMethods': 'Visa · Mastercard · Amex',
    'topup.cryptoMethods': 'Base USDC · BSC stablecoins',
    'topup.cryptoNetwork': 'Network & currency',
    'topup.baseHint': 'Pay from your wallet · x402 on-chain settlement',
    'topup.bsc': 'BSC stablecoins',
    'topup.bscHint': 'U · USD1 · USDT · USDC · B402 settlement',
    'topup.bscToken': 'BSC token',
    'topup.youPay': 'You pay',
    'topup.credited': 'Credited to balance',
    'topup.wallet': 'Wallet',
    'topup.notConnected': 'Not connected',
    'topup.disconnect': 'Disconnect',
    'topup.connecting': 'Connecting…',
    'topup.connectWallet': 'Connect wallet',
    'topup.walletHint': 'Pick a wallet to sign and pay',
    'topup.noWallet': 'No wallets detected. Install a supported wallet to continue.',
    'topup.verifying': 'Verifying payment with the backend…',
    'topup.awaitingWallet': 'Awaiting wallet…',
    'topup.openingCheckout': 'Opening checkout…',
    'topup.payWith': 'Pay with {asset}',
    'topup.connectToPay': 'Connect wallet to pay',
    'topup.continue': 'Continue to Stripe',
    'autoRecharge.label': 'Stripe · Auto top-up',
    'autoRecharge.cardTitle': 'Auto top-up',
    'autoRecharge.cardDescription': 'Keep your balance topped up with an authorized card.',
    'autoRecharge.title': 'Automatically add funds',
    'autoRecharge.description': 'Charge your authorized card for a set amount when your balance falls below the threshold. You can turn this off at any time.',
    'autoRecharge.enabled': 'Enabled',
    'autoRecharge.disabled': 'Disabled',
    'autoRecharge.loading': 'Loading auto top-up settings…',
    'autoRecharge.unavailable': 'Auto top-up is unavailable. You can still make a one-time payment.',
    'autoRecharge.stripeUnavailable': 'Stripe auto top-up is unavailable. You can still disable an existing authorization below.',
    'autoRecharge.attempt': 'Latest auto top-up: ${amount}, {state}.',
    'autoRecharge.attempt.pending': 'processing',
    'autoRecharge.attempt.canceled': 'canceled before a charge was started',
    'autoRecharge.attempt.succeeded': 'credited',
    'autoRecharge.attempt.failed': 'payment failed; auto top-up paused',
    'autoRecharge.attempt.partially_reversed': 'partially reversed',
    'autoRecharge.attempt.reversed': 'fully reversed',
    'autoRecharge.loadError': 'Unable to confirm auto top-up status. Settings cannot be edited.',
    'autoRecharge.retry': 'Retry',
    'autoRecharge.setupCancel': 'Card setup was canceled. Auto top-up remains off. Review your card and rule, then authorize and enable it when ready.',
    'autoRecharge.setupPending': 'Returned from Stripe. Waiting for card setup confirmation; refresh the status shortly.',
    'autoRecharge.suspendedHelp': 'Auto top-up is suspended after a refund or dispute. Contact support before enabling it again.',
    'autoRecharge.pendingPaymentHelp': 'A previous payment still needs reconciliation. Auto top-up cannot be enabled yet.',
    'autoRecharge.card': 'Card for automatic charges',
    'autoRecharge.noCard': 'No card added',
    'autoRecharge.cardHint': 'Enter the amounts and accept the authorization below before adding a card.',
    'autoRecharge.flowIntro': 'Set the balance trigger and charge amount, then authorize a card. Adding a card alone will not enable automatic charges.',
    'autoRecharge.ruleStep': 'Top-up rule',
    'autoRecharge.ruleHint': 'Choose when to top up and how much to add each time.',
    'autoRecharge.rulePreview': 'Below ${threshold}, add ${amount} to your balance.',
    'autoRecharge.cardStep': 'Payment card',
    'autoRecharge.cardStepHint': 'Stripe securely stores one card for automatic charges.',
    'autoRecharge.consentStep': 'Review and authorize',
    'autoRecharge.setupNext': 'After returning from Stripe, review these settings and enable auto top-up here.',
    'autoRecharge.setupReady': 'Card saved. Review the rule, accept the authorization again, then enable auto top-up.',
    'autoRecharge.addCardNext': 'Continue to Stripe to add a card',
    'autoRecharge.currentRule': 'Current rule',
    'autoRecharge.manageIntro': 'Automatic top-up is active with this rule and card.',
    'autoRecharge.readyIntro': 'Your rule and card are ready. Review the authorization to enable automatic charges.',
    'autoRecharge.activateConsent': 'I authorize xAPI to charge ${amount} to this card when my balance falls below ${threshold}.',
    'autoRecharge.editRule': 'Change rule',
    'autoRecharge.editRuleHint': 'Update the balance trigger or the amount charged each time.',
    'autoRecharge.editRuleConsent': 'I authorize xAPI to charge ${amount} to my saved card whenever my balance falls below ${threshold}, subject to the terms below.',
    'autoRecharge.authorizationLimits': 'At least 15 minutes between attempts, up to 3 in 24 hours. Another charge can occur if the balance falls again. Turn this off at any time.',
    'autoRecharge.changeCardWarning': 'Auto top-up pauses when you start changing your card. Return here to authorize and enable it again, even if you cancel.',
    'autoRecharge.changeCardInactiveHint': 'Choose a new card in Stripe. Return here to authorize and enable auto top-up.',
    'autoRecharge.changeCardConsent': 'I agree to save a new card with Stripe for the current ${threshold} / ${amount} rule. I understand auto top-up pauses when replacement starts and requires fresh authorization to enable again, even if I cancel.',
    'autoRecharge.changeCardNext': 'Continue to Stripe to change card',
    'autoRecharge.backToOverview': 'Back to current settings',
    'autoRecharge.fullTerms': 'Full authorization terms',
    'autoRecharge.changeCardHint': 'To change this card, confirm the authorization below, then continue to Stripe.',
    'autoRecharge.failureHelp': 'The latest automatic charge failed. Check the card and authorization before trying again.',
    'autoRecharge.refreshCard': 'Check card status',
    'autoRecharge.expiry': 'Expires {month}/{year}',
    'autoRecharge.addCard': 'Add card',
    'autoRecharge.changeCard': 'Change card',
    'autoRecharge.threshold': 'Balance below (USD)',
    'autoRecharge.topUp': 'Amount per top-up (USD)',
    'autoRecharge.invalidAmounts': 'Enter $1–$100 with at most two decimal places. The top-up amount must exceed the threshold.',
    'autoRecharge.consent': 'I agree to save the selected card with Stripe and authorize xAPI to charge the amount above when my balance falls below the threshold. Attempts are at least 15 minutes apart, with at most 3 attempts in 24 hours. Another charge may occur if my balance falls below the threshold again. Successful payments are added to my xAPI balance. I can turn this off here at any time; failed charges may require card verification.',
    'autoRecharge.enable': 'Enable auto top-up',
    'autoRecharge.save': 'Save auto top-up settings',
    'autoRecharge.disable': 'Turn off auto top-up',
    'autoRecharge.refresh': 'Refresh status',
    'autoRecharge.busy': 'Processing…',
    'autoRecharge.saved': 'Auto top-up settings saved.',
    'autoRecharge.disabledMessage': 'Auto top-up turned off.',
    'autoRecharge.unconfirmed': 'Unable to confirm whether the setting took effect. Refresh the status and try again.',
    'autoRecharge.saveError': 'Could not save settings. Try again.',
    'autoRecharge.setupError': 'Could not open the Stripe card setup page.',
    'autoRecharge.setupUrlError': 'Stripe did not return a valid card setup link.',
    'autoRecharge.compactEnabled': 'When your balance falls below ${threshold}, charge ${amount} to your saved card.',
    'autoRecharge.compactDisabled': 'Set a balance threshold, top-up amount, and card for automatic charges.',
    'autoRecharge.expand': 'Set up auto top-up',
    'autoRecharge.manage': 'Manage auto top-up',
    'autoRecharge.collapse': 'Hide settings',

    'console.referral.title': 'Referral',
    'console.referral.subtitle':
      'Earn {pct}% of the platform fee on every call your invitees make — forever.',
    'console.referral.signinSubtitle': 'Sign in to see your referral stats.',
    'console.referral.ledger.usage': 'Usage rewards',
    'console.referral.ledger.recharge': 'Recharge history',
    'console.referral.ledger.frozen': 'Legacy ledger · read-only',
    'console.referral.ledger.usageEmpty':
      "No usage rewards yet. Rewards settle every ~10 minutes from your invitees' usage.",
    'console.referral.ledger.loadMore': 'Load more',

    'console.settings.title': 'Settings',
    'console.settings.subtitle': 'Account, billing, security.',
    'console.settings.signinSubtitle': 'Sign in to view your account.',

    'console.analytics.title': 'Provider Analytics',
    'console.analytics.subtitle':
      'Revenue, traffic, and caller breakdown across all your published APIs.',
    'console.analytics.signinSubtitle':
      'Sign in to see your provider analytics.',
    'console.analytics.gateBody':
      'Provider analytics is only available to verified providers. Get whitelisted to publish APIs and start earning, then come back here.',
    'console.analytics.gateCta': 'Apply to be a provider',
    'console.analytics.empty':
      'No earnings yet — your APIs need to be live and called before stats appear here.',
    'console.analytics.stat.totalNet': 'Total earnings',
    'console.analytics.stat.rangeNet': 'Earnings · {RANGE}',
    'console.analytics.stat.pending': 'Pending settlement',
    'console.analytics.stat.settled': 'Settled',
    'console.analytics.stat.totalCalls': 'Total calls',
    'console.analytics.stat.rangeCalls': 'Calls · {RANGE}',
    'console.analytics.stat.todayCalls': 'Calls today',
    'console.analytics.stat.uniqueCallers': 'Active callers (30d)',
    'console.analytics.stat.totalApis': 'Live APIs',
    'console.analytics.stat.platformFee': 'Platform fee',
    'console.analytics.trend.title': 'Earnings trend',
    'console.analytics.trend.peak': 'Peak day',
    'console.analytics.trend.avg': 'Avg / day',
    'console.analytics.apis.title': 'APIs',
    'console.analytics.apis.subtitle':
      'Per-API breakdown — gross income, platform fees, net earnings, and traffic shape.',
    'console.analytics.apis.col.api': 'API',
    'console.analytics.apis.col.calls': 'Calls',
    'console.analytics.apis.col.gross': 'Gross',
    'console.analytics.apis.col.fees': 'Fees',
    'console.analytics.apis.col.net': 'Net',
    'console.analytics.apis.col.callers': 'Callers',
    'console.analytics.apis.col.latency': 'Latency',
    'console.analytics.apis.col.success': 'Success',
    'console.analytics.history.title': 'Earnings history',
    'console.analytics.history.subtitle':
      'Per-call earning records. Pending entries become settled after the platform reconciles.',
    'console.analytics.history.col.date': 'Date',
    'console.analytics.history.col.api': 'API',
    'console.analytics.history.col.gross': 'Gross',
    'console.analytics.history.col.fee': 'Fee',
    'console.analytics.history.col.net': 'Net',
    'console.analytics.history.col.status': 'Status',
    'console.analytics.history.status.pending': 'Pending',
    'console.analytics.history.status.settled': 'Settled',
    'console.analytics.history.empty': 'No earnings records in this range.',
    'console.analytics.history.totals':
      'In view: gross {GROSS} · fees {FEES} · net {NET}',
    'console.analytics.scope.all': 'All APIs',
    'console.analytics.scope.label': 'Viewing',
    'console.analytics.endpoints.title': 'Endpoints',
    'console.analytics.endpoints.subtitle':
      'Per-endpoint traffic for the selected API.',
    'console.analytics.endpoints.col.endpoint': 'Endpoint',
    'console.analytics.endpoints.col.calls': 'Calls',
    'console.analytics.endpoints.col.cost': 'Gross',
    'console.analytics.endpoints.col.callers': 'Callers',
    'console.analytics.endpoints.col.latency': 'Latency',
    'console.analytics.endpoints.col.success': 'Success',
    'console.analytics.endpoints.empty':
      'No traffic on any endpoint in this range.',

    // ---- Claim wizard ----
    'claim.crumb': 'Claim account',
    'claim.title': 'Upgrade to a full account',
    'claim.subtitle':
      'Tweet a one-time code to link your X handle. Unlocks API publishing, full balance dashboard, and provider analytics.',
    'claim.alreadyVerified': 'Already verified as {HANDLE}',
    'claim.goToConsole': 'Go to console',
    'claim.signInFirst':
      'Sign in first, or open the claim link from your verification tweet.',
    'claim.loading': 'Loading session…',
    'claim.refresh': 'Refresh',
    'claim.steps.of': 'Step {N} of 4',

    'claim.apikey.eyebrow': 'API key issued',
    'claim.apikey.title': 'Save your key first',
    'claim.apikey.body':
      'This is the only time the full key is shown. Copy it now or download it as a file — you cannot recover it later.',
    'claim.apikey.noKey':
      'No API key found in this session. Sign in or open the claim link with ?apikey=…',
    'claim.apikey.warning':
      'If you lose this key you must regenerate from the console.',
    'claim.apikey.continue': 'I saved it — continue',
    'claim.apikey.saveFirst': 'Save the key to continue',
    'claim.apikey.injectEnv': 'Saved to your local XAPI_KEY env',

    'claim.tweet.eyebrow': 'Post the tweet',
    'claim.tweet.title': 'Tweet the verification code',
    'claim.tweet.code': 'Invite code',
    'claim.tweet.copy': 'Copy text',
    'claim.tweet.openX': 'Post on X',
    'claim.tweet.iPosted': 'I posted it — verify',
    'claim.tweet.back': 'Back',

    'claim.verify.eyebrow': 'Verify',
    'claim.verify.title': 'Paste the tweet URL',
    'claim.verify.placeholder': 'https://x.com/<you>/status/…',
    'claim.verify.submit': 'Verify and upgrade',
    'claim.verify.verifying': 'Verifying tweet…',
    'claim.verify.errorEmpty': 'Paste your tweet URL first',
    'claim.verify.errorNoSession': 'No claim session — refresh the page',

    'claim.done.eyebrow': 'Verified',
    'claim.done.title': 'You are upgraded',
    'claim.done.body':
      'Your X handle is linked. The account now has provider permissions and full balance access.',
    'claim.done.recharge': 'Add funds',
    'claim.done.setupMcp': 'Set up your MCP client',
    'claim.done.handle': 'Linked handle',

    // ---- Bind page (NEW: direct OAuth, no tweet required) ----
    'bind.crumb': 'Connect X',
    'bind.title': 'Connect your X account',
    'bind.subtitle':
      'A one-click X authorisation links your handle. Unlocks publishing APIs, the full balance dashboard, and provider analytics.',
    'bind.alreadyVerified': 'Already linked to {HANDLE}',
    'bind.continueX': 'Continue with X',
    'bind.skip': 'Skip for now',
    'bind.skipHint':
      'You can keep using xapi as a virtual account — connect later from the console.',
    'bind.signInFirst': 'Sign in or open a registration link first.',
    'bind.successToast': 'Linked successfully',
    'bind.mergedToast':
      'Merged into your existing account — your API key is now under it',
    'bind.errorToast': 'Linking failed',
    'bind.activityHint':
      'Want $1 free credit? Post a verification tweet on the activity page.',
    'bind.activityCta': 'Tweet for $1 credit →',
    'bind.preparing': 'Preparing X authorisation…',

    // ---- Activity page (NEW: tweet-for-reward promo, repurposed claim wizard) ----
    'activity.crumb': 'Activity',
    'activity.title': 'Tweet to claim $1 credit',
    'activity.subtitle':
      'Post a verification tweet on X to receive $1 of free credit, on top of your normal account balance.',
    'activity.reward.amount': '$1.00',
    'activity.reward.label': 'free credit',
    'activity.calc.title': 'What $1 unlocks',
    'activity.calc.twitter': 'Twitter API calls',
    'activity.calc.llm': 'LLM tokens',
    'activity.calc.search': 'Web searches',
    'activity.gateBody':
      'Sign in or register first to claim. The credit lands in the account you sign in with.',
    'activity.gateCta': 'Get started',
    'activity.bindGate.eyebrow': 'Bind X first',
    'activity.bindGate.body':
      'Only entity accounts (those with X linked) can claim this reward. One claim per account.',
    'activity.bindGate.cta': 'Bind X to continue',
    'activity.tweet.eyebrow': 'Step 1 · Post the tweet',
    'activity.tweet.code': 'Invite code',
    'activity.tweet.copy': 'Copy text',
    'activity.tweet.openX': 'Post on X',
    'activity.verify.eyebrow': 'Step 2 · Paste the URL',
    'activity.verify.placeholder': 'https://x.com/<you>/status/…',
    'activity.verify.submit': 'Verify and claim $1',
    'activity.verify.verifying': 'Verifying tweet…',
    'activity.done.title': 'Credit claimed!',
    'activity.done.body':
      '$1 has been added to your balance. Go ahead and try out an API.',
    'activity.done.console': 'Go to console',
    'activity.done.exploreApis': 'Browse APIs',
    'activity.alreadyClaimed': "You've already claimed this credit.",
    'activity.errorEmpty': 'Paste your tweet URL first',
    'activity.errorNoSession': 'Activity session missing — please sign in.',
    'activity.error.twitterBound':
      'This Twitter account has already claimed $1.',
    'activity.error.alreadyClaimed': "You've already claimed your $1.",
    'activity.error.tweetUsed':
      'This tweet has already been used to claim a reward.',

    'workspace.title': 'Your APIs',
    'workspace.subtitle':
      "APIs you've registered. Submit for review to list them publicly.",
    'workspace.import': 'Register via skill',
    'workspace.skillImport.title': 'Register via the xapi-provider skill',
    'workspace.skillImport.desc':
      'Let an AI agent onboard your API. Install the xapi-provider skill, then point your agent at your OpenAPI spec or API docs — it builds the service, configures billing, and submits it for review.',
    'workspace.skillImport.installLabel': 'Install the skill',
    'workspace.skillImport.promptLabel': 'Then tell your agent',
    'workspace.skillImport.promptExample':
      'Use the xapi-provider skill to register this OpenAPI on xapi, then publish it.',
    'workspace.skillImport.repo': 'View the skill repo',
    'workspace.skillImport.copy': 'Copy',
    'workspace.provideApi': 'Provide APIs',
    'workspace.section.active': 'Active',
    'workspace.section.activeDesc': 'Published — visible in marketplace',
    'workspace.section.review': 'In review',
    'workspace.section.reviewDesc': 'Pending admin approval',
    'workspace.section.sandbox': 'Sandbox',
    'workspace.section.sandboxDesc': 'Validated — ready to submit',
    'workspace.section.draft': 'Drafts',
    'workspace.section.draftDesc': 'Not yet submitted',
    'workspace.section.attention': 'Needs attention',
    'workspace.section.attentionDesc': 'Rejected or suspended',
    'workspace.empty': "You haven't registered any APIs yet.",
    'workspace.firstApi': 'Provide your first API',
    'workspace.submit': 'Submit',
    'workspace.routes': 'routes',

    // ---- MCP page ----
    'mcp.eyebrow': 'Model Context Protocol',
    'mcp.title': 'Connect your MCP client',
    'mcp.subtitle':
      'Pick your client, paste the config, restart. Your agent unlocks all {APIS} marketplace APIs ({TOOLS} tools) over a single endpoint. Per-tool access is gated by your API key permissions.',
    'mcp.stat.apis': 'Marketplace APIs',
    'mcp.stat.tools': 'Tools exposed',
    'mcp.stat.status': 'Server status',
    'mcp.stat.online': 'online',
    'mcp.endpoint.label': 'MCP endpoint',
    'mcp.endpoint.note':
      'HTTP transport. Authenticate by appending {QS} to the URL.',
    'mcp.auth.label': 'Auth · XAPI_KEY',
    'mcp.auth.manage': 'Manage',
    'mcp.auth.loaded': 'Loaded from your environment',
    'mcp.auth.injected':
      'Injected into the config on the right. Stored only in your browser.',
    'mcp.auth.noKey': 'No key loaded',
    'mcp.auth.placeholder':
      'Config will show {PH} placeholder. Set it once, every config below auto-fills.',
    'mcp.auth.set': 'Set XAPI_KEY',
    'mcp.sandbox.title': 'Sandbox keys',
    'mcp.sandbox.desc':
      'Only keys with {SANDBOX} enabled can call APIs in pre-launch state.',
    'mcp.sandbox.manage': 'Manage in Console',
    'mcp.config.cli': 'CLI command',
    'mcp.config.label': 'mcpServers config',
    'mcp.config.oneClick': 'One-click install',
    'mcp.config.copyCommand': 'Copy command',
    'mcp.config.copyConfig': 'Copy config',
    'mcp.config.placeholderHint':
      'Replace {PH} with a key from {LINK}, or set {KEY} on the left to auto-inject.',
    'mcp.config.consoleKeys': 'Console → Keys',
    'mcp.config.cliHint':
      'Run the command in your shell. Claude Code will pick up all xapi tools on next session.',
    'mcp.config.pasteHint':
      'Paste into your {NAME} MCP config and restart the client. The xapi server will register {TOOLS} tools.',

    // ---- Capabilities page ----
    'cap.eyebrow': 'Capabilities',
    'cap.title': 'Capability playground',
    'cap.subtitle':
      'Cross-API skills — each capability hides the routing and retry logic behind a single endpoint. Pick one, fill inputs, send a test call.',
    'cap.skills': 'skills',
    'cap.loading': 'Loading capabilities…',
    'cap.unavailable': 'Capabilities unavailable',
    'cap.unavailableBody':
      'Could not reach {URL}: {ERR}. Check the capability service is running, or set {OVR} in your env.',
    'cap.empty': 'No capabilities published yet.',
    'cap.routesThrough': 'routes through:',
    'cap.req': 'REQUEST',
    'cap.resp': 'RESPONSE',
    'cap.run': 'Run capability',
    'cap.abort': 'Abort',
    'cap.required': 'required',
    'cap.placeholder': 'Run the capability to see a response',
    'cap.routing': 'Routing through {APIS}…',
    'cap.payload': 'PAYLOAD · application/json',
    'cap.charged': 'charged',
    'cap.upstreamCall': 'upstream call',
    'cap.upstreamCalls': 'upstream calls',
    'cap.setKeyToSend': 'Set XAPI_KEY to send',

    // ---- Skill page ----
    'skill.eyebrow': 'Anthropic Skills',
    'skill.title': 'xapi as a skill',
    'skill.subtitle':
      'A {SKILL} is a portable tool manifest your agent fetches once and remembers — no per-project MCP wiring. One install command teaches Claude, Cursor, or any agentic IDE about every API in the marketplace.',
    'skill.subtitle.skillWord': 'skill',
    'skill.install.title': 'Install',
    'skill.unit.file': 'file',

    // ---- Skill page sections ----
    'skill.section.use': '01 · How to use',
    'skill.section.about': '02 · The skill itself',
    'skill.use.subtitle':
      'Run the install command in any agent CLI, or paste the prompt below into any chat. Both teach your agent the full xapi tool catalog.',
    'skill.about.subtitle':
      "What's actually inside the manifest your agent fetches.",
    'skill.about.fact.tools': 'Tools exposed',
    'skill.about.fact.size': 'Manifest size',
    'skill.about.fact.refresh': 'Refresh cadence',
    'skill.about.fact.refreshVal': 'on-demand',
    'skill.about.list.title': "What's in the manifest",
    'skill.about.list.l1':
      'Tool catalog — every marketplace API as a typed callable',
    'skill.about.list.l2': 'JSON Schema for each tool input + response',
    'skill.about.list.l3': 'Gateway endpoint + auth header convention',
    'skill.about.list.l4':
      'Pricing metadata (per-call / per-token / per-resource)',
    'skill.about.viewManifest': 'Open the live manifest',

    // Register — page chrome (sidebar + form header)
    'register.signin.eyebrow': 'Provider',
    'register.signin.title': 'Sign in to register an API',
    'register.signin.cta': 'Sign in',
    'register.crumb.workspace': 'Workspace',
    'register.crumb.edit': 'Edit',
    'register.crumb.new': 'New',
    'register.untitled': 'Untitled service',
    'register.host.direct': 'direct · catalog only',
    'register.host.pending': '— host pending —',
    'register.live': 'live',
    'register.nav.configure': 'Configure',
    'register.nav.tools': 'Tools',
    'register.nav.overview': 'Overview',
    'register.nav.overview.hint': 'Identity, host, visibility',
    'register.nav.origin': 'Origin & Auth',
    'register.nav.origin.hint': 'Upstream URL, auth, headers',
    'register.nav.origin.disabled': 'disabled in DIRECT mode',
    'register.nav.endpoints': 'Endpoints',
    'register.nav.endpoints.hint': '{N} routes',
    'register.tools.importOpenapi': 'Import OpenAPI',
    'register.tools.importOpenapi.busy': 'Parsing…',
    'register.tools.aiImport': 'AI import',
    'register.tools.deleteDraft': 'Delete draft',
    'register.header.title.edit': 'Edit service',
    'register.header.title.new': 'Register API service',
    'register.header.body.new':
      'Define the upstream, endpoints, and pricing. Drafts can be edited until you submit for review.',
    'register.header.body.edit.live.save':
      '{B} — metadata only (name, logo, host).',
    'register.header.body.edit.live.hotfix':
      '{B} — overwrite the live version in place.',
    'register.header.body.edit.live.release': '{B} — publish a new version.',
    'register.header.body.edit.notLive':
      'Update basic info, then submit for review when ready.',
    'register.action.create': 'Create draft',
    'register.action.create.busy': 'Creating…',
    'register.action.save': 'Update',
    'register.action.save.busy': 'Updating…',
    'register.action.hotfix': 'Update version',
    'register.action.hotfix.busy': 'Updating…',
    'register.action.release': 'Publish new version',
    'register.action.release.busy': 'Publishing…',
    'register.action.release.placeholder': 'Release notes…',
    'register.confirm.hotfix.title': 'Update current version?',
    'register.confirm.hotfix.body':
      'Applies ALL your unsaved edits (including removed endpoints) in place to the current version — consumers keep the same version number. Use it for fixes and small changes. Nothing is saved until you confirm.',
    'register.confirm.hotfix.ok': 'Update version',
    'register.confirm.release.title': 'Publish a new version?',
    'register.confirm.release.body':
      'Bumps the version number (e.g. v1 → v2) and saves ALL your unsaved edits as that new version. Consumers opt into the new contract; the old version keeps working. Use it for breaking changes.',
    'register.confirm.release.ok': 'Publish',
    'register.confirm.cancelBtn': 'Cancel',
    'register.action.review': 'Submit for review',
    'register.action.review.busy': 'Submitting…',
    'register.action.submit': 'Submit',
    'register.action.submit.busy': 'Submitting…',
    'register.action.resubmit': 'Resubmit for review',
    'register.action.resubmit.busy': 'Submitting…',
    'register.banner.draft':
      'Drafting — saves replace the working copy in place. Submit when ready.',
    'register.banner.sandbox':
      'In sandbox · awaiting admin review. Edits update the working copy in place — admin sees the latest on next review. Use a sandbox-flagged API key to test end-to-end.',
    'register.banner.pendingReview':
      'Running automated quality checks — usually a few seconds.',
    'register.banner.approved': 'Approved · publishing to marketplace…',
    'register.banner.active':
      'Live on marketplace. "Update version" applies your edits in place; "Publish new version" ships them as a new contract.',
    'register.banner.rejected':
      'Rejected by admin. Update the API and resubmit for review.',
    'register.banner.suspended':
      'Paused by an admin. Published revisions and the serving pointer are preserved. Contact an admin to resume the service; no review resubmission is required.',
    'register.unsavedChanges':
      '● Unsaved changes — edits & removed endpoints only take effect when you Update or Publish',
    'register.toast.created': 'API created',
    'register.toast.create.fail': 'Create failed',
    'register.toast.save': 'Updated',
    'register.toast.save.fail': 'Update failed',
    'register.toast.hotfix': 'Hotfix applied',
    'register.toast.hotfix.fail': 'Hotfix failed',
    'register.toast.release': 'New version released',
    'register.toast.release.fail': 'Release failed',
    'register.toast.review': 'Submitted for review',
    'register.toast.review.fail': 'Submit failed',
    'register.toast.delete': 'Draft deleted',
    'register.toast.delete.fail': 'Delete failed',
    'register.toast.fixFields': 'Please fix the highlighted fields',
    'register.toast.noVersion': 'No active version to hotfix',
    'register.toast.loadFail': 'Failed to load API',
    'register.confirm.review':
      'Submit this API for review? An admin will approve before it goes public.',
    'register.confirm.delete': 'Delete this draft? This cannot be undone.',
    'register.confirm.delete.title': 'Delete this draft?',
    'register.confirm.delete.ok': 'Delete',
    'register.confirm.review.title': 'Submit for review?',
    'register.confirm.review.ok': 'Submit',
    'register.tools.batchPrice': 'Batch pricing',
    'register.overview.about': 'About doc (optional)',
    'register.overview.about.hint':
      'Long-form Markdown shown on the marketplace detail page About tab. The short description above is still what market cards show.',
    'register.overview.about.starter': 'Insert starter',
    'register.overview.about.demo': 'Load full demo',
    'register.overview.about.replaceTitle': 'Replace current About content?',
    'register.overview.about.replaceBody':
      'This will overwrite the About doc you have written so far. This cannot be undone.',
    'register.overview.about.replaceOk': 'Replace',

    // Register — Overview section
    'register.overview.identity': 'Identity',
    'register.overview.identity.sub':
      'Shown in the marketplace and in MCP tool listings.',
    'register.overview.name': 'Display name',
    'register.overview.name.placeholder': 'e.g. Weather Pro',
    'register.overview.category': 'Category',
    'register.overview.description': 'Description',
    'register.overview.description.hint':
      'Short blurb for marketplace cards (1–2 lines). Long docs go under About.',
    'register.overview.description.placeholder': 'One-paragraph summary…',
    'register.overview.website': 'Website',
    'register.overview.website.hint':
      'Optional homepage shown on the marketplace detail page.',
    'register.overview.logoUrl': 'LOGO',
    'register.overview.logoUrl.hint': 'Square logo, JPG/PNG/GIF/WebP, up to 10 MB. Optional.',
    'logo.upload': 'Upload logo',
    'logo.replace': 'Replace logo',
    'logo.uploading': 'Uploading…',
    'logo.cancel': 'Cancel upload',
    'logo.remove': 'Remove logo',
    'logo.preview': 'Logo preview',
    'logo.url': 'Or paste an image URL',
    'logo.hint': 'Optional square logo. JPG, PNG, GIF or WebP, up to 10 MB. Drop an image onto the preview to upload.',
    'logo.pending': 'Wait for the logo upload to finish before saving.',
    'logo.uploaded': 'Logo uploaded. Save your changes to apply it.',
    'logo.error.type': 'Choose a JPG, PNG, GIF or WebP image.',
    'logo.error.empty': 'This file is empty. Choose another image.',
    'logo.error.size': 'Image must be 10 MB or smaller.',
    'logo.error.upload': 'Upload failed. Please try again.',
    'logo.error.timeout': 'Upload timed out. Please try again.',
    'logo.error.preview': 'Image could not be loaded. Check the URL or choose another image.',
    'register.overview.visibility': 'Visibility',
    'register.overview.visibility.public': 'Public',
    'register.overview.visibility.private': 'Private',
    'register.overview.access': 'Access mode',
    'register.overview.access.sub':
      'PROXY routes traffic through the xapi gateway. DIRECT lists the upstream as a catalog entry without proxying.',
    'register.overview.access.proxy.desc':
      'xapi acts as the gateway: auth, metering, billing, logs.',
    'register.overview.access.direct.desc':
      'Catalog only. Consumers call the upstream URL directly. No billing or auth.',
    'register.overview.rateLimit': 'User rate limit',
    'register.overview.rateLimit.sub':
      'Protect service capacity with a simple per-user quota.',
    'register.overview.rateLimit.off': 'No limit',
    'register.overview.rateLimit.off.hint':
      'No user-level quota is applied. Enable it to protect service capacity during traffic spikes.',
    'register.overview.rateLimit.on': 'Enable limit',
    'register.overview.rateLimit.requests': 'Maximum requests',
    'register.overview.rateLimit.requests.hint': 'Whole number from 1 to 1,000,000.',
    'register.overview.rateLimit.period': 'Window',
    'register.overview.rateLimit.second': 'Per second',
    'register.overview.rateLimit.minute': 'Per minute',
    'register.overview.rateLimit.hour': 'Per hour',
    'register.overview.rateLimit.custom': 'Every {seconds} seconds (existing)',
    'register.overview.rateLimit.toggle': 'Toggle user rate limit',
    'register.overview.rateLimit.status.on': 'Active',
    'register.overview.rateLimit.status.off': 'Off',
    'register.overview.rateLimit.rule': 'Quota rule',
    'register.overview.rateLimit.userPrefix': 'Each user may send at most',
    'register.overview.rateLimit.times': 'requests',
    'register.overview.rateLimit.preview': 'Effective policy',
    'register.overview.rateLimit.scope.user': 'Per user',
    'register.overview.rateLimit.scope.keys': 'Shared across API keys',
    'register.overview.rateLimit.scope.endpoints': 'Shared across endpoints',
    'register.overview.rateLimit.scope':
      'All API keys owned by the same user share this quota across every endpoint in the service.',
    'register.overview.rateLimit.direct':
      'User rate limits require PROXY mode. Switching to DIRECT clears this configuration.',
    'register.overview.host.label': 'Host prefix',
    'register.overview.host.hint':
      'Lowercase letters, numbers, hyphens. Becomes <prefix>{suffix}.',
    'host.checking': 'Checking availability…',
    'host.ok': '"{host}" is available',
    'host.err.TOO_SHORT': 'Host must be at least 3 characters',
    'host.err.INVALID_FORMAT':
      'Host may only contain lowercase letters, numbers and hyphens',
    'host.err.RESERVED': '"{host}" is a reserved word — please choose another',
    'host.err.CONTAINS_BLOCKED': 'Host contains a disallowed word',
    'host.err.TAKEN': '"{host}" is already taken — please choose another',
    'host.err.default': 'This host is not allowed',

    // Register — Origin & Auth
    'register.origin.upstream': 'Upstream origin',
    'register.origin.upstream.sub': 'Where requests are reverse-proxied to.',
    'register.origin.baseUrl': 'Base URL',
    'register.origin.baseUrl.hint':
      'The upstream service. e.g. https://api.acme.com',
    // Multi base URL environments
    'register.origin.env.add': 'Add base URL',
    'register.origin.env.default': 'Default base URL',
    'register.origin.env.extra':
      'Additional named base URLs. Name each one, then pick it per endpoint from the dropdown in front of the path. Endpoints with no pick use the default.',
    'register.origin.env.name.ph': 'staging',
    'register.origin.env.url.ph': 'https://staging.api.com',
    'register.origin.env.error':
      'Name must be letters/digits/-/_, and the URL must be valid.',
    // Endpoint group + path env menu
    'register.endpoint.field.group': 'Group',
    'register.endpoint.field.group.hint':
      'Endpoints with the same group fold together in the list (empty = ungrouped).',
    'register.endpoint.field.group.placeholder': 'e.g. User / Billing',
    'register.endpoint.path.placeholder': '/v1/your-endpoint',
    'register.endpoint.path.envMenu': 'Pick upstream environment',
    'register.endpoint.path.envBtn':
      'Upstream environment — pick which Base URL this endpoint uses',
    'register.endpoint.path.defaultHint': 'the default Base URL',
    'register.endpoint.path.noMatch': 'No matching environment',
    // Create-project modal
    'createProject.title': 'Create API project',
    'createProject.name': 'Project name',
    'createProject.name.ph': 'e.g. Weather API',
    'createProject.desc': 'Description',
    'createProject.desc.ph': 'One line on what this project offers',
    'createProject.type': 'Type',
    'createProject.template': 'Template',
    'createProject.hint':
      'After creating, configure the upstream URL, endpoints and billing in the workspace. Upstream base URL supports multiple environments ({{staging}} etc.).',
    'createProject.cancel': 'Cancel',
    'createProject.submit': 'Create draft',
    'createProject.creating': 'Creating…',
    'createProject.nameRequired': 'Please enter a project name',
    'createProject.created': 'Draft created',
    'createProject.failed': 'Create failed',
    'createProject.tpl.blank': 'Blank project',
    'createProject.tpl.blank.desc':
      'Start from scratch; add endpoints in the workspace',
    'createProject.tpl.rest-crud': 'REST CRUD',
    'createProject.tpl.rest-crud.desc':
      'Standard CRUD (5 endpoints), grouped by resource',
    'createProject.tpl.read-only-data': 'Read-only data API',
    'createProject.tpl.read-only-data.desc':
      'List + detail (two GET endpoints)',
    'register.origin.auth': 'Authentication',
    'register.origin.auth.sub':
      'How xapi authenticates to your upstream. The secret is stored encrypted server-side.',
    'register.origin.auth.NONE': 'No auth',
    'register.origin.auth.BEARER': 'Bearer token',
    'register.origin.auth.HEADER': 'API key (header)',
    'register.origin.auth.QUERY': 'API key (query)',
    'register.origin.auth.headerName': 'Header name',
    'register.origin.auth.queryName': 'Query parameter name',
    'register.origin.auth.bearer': 'Bearer token',
    'register.origin.auth.secret': 'Secret value',
    'register.origin.auth.secret.hint': 'Stored encrypted. Rotated on demand.',
    'register.origin.privateHeaders': 'Private headers',
    'register.origin.privateHeaders.sub':
      'Extra headers attached to every upstream request (server-side, never exposed to the client).',
    'register.origin.privateHeaders.add': 'Add header',
    'register.origin.privateHeaders.empty': 'No private headers yet',
    'register.origin.privateHeaders.col.name': 'Header name',
    'register.origin.privateHeaders.col.value': 'Value',
    'register.origin.directNotice.title':
      'Origin & Auth not used in DIRECT mode',
    'register.origin.directNotice.body':
      'Consumers will call your upstream URL directly. xapi does not proxy or meter requests. Switch to {PROXY} mode in Overview if you want billing.',

    // Async tasks — register page
    'register.endpoint.async.label': 'Async mode',
    'register.endpoint.async.hint':
      'Enable when a single call routinely takes longer than ~30s. Clients receive a task_id and poll /v1/tasks/{id} for the result.',
    'register.endpoint.async.toggleHintOff':
      'Synchronous: clients wait for the response in a single HTTP round-trip.',
    'register.endpoint.async.toggleHintOn':
      'Async: server returns a task_id; clients poll /v1/tasks/{id} until the job finishes.',
    'register.endpoint.async.optionalLegacy':
      'Legacy "client opt-in" mode is preserved on this endpoint. Toggle off then on to switch to async-only.',
    'register.endpoint.async.opt.none': 'Sync only',
    'register.endpoint.async.opt.optional': 'Sync + async',
    'register.endpoint.async.opt.required': 'Async only',
    'register.endpoint.async.hint.optional':
      'Caller opts in with X-XAPI-Async: true for async; otherwise synchronous. Both modes supported.',
    'register.endpoint.async.mode.NONE': 'Synchronous',
    'register.endpoint.async.mode.OPTIONAL': 'Async optional',
    'register.endpoint.async.mode.REQUIRED': 'Async required',
    'register.endpoint.async.warn.oauth':
      'OAuth-bound endpoints cannot use async mode.',
    'register.endpoint.async.warn.streaming':
      'Streaming and async are mutually exclusive.',
    'register.endpoint.async.lockedByPolling':
      'Async mode is locked to Required by Upstream Polling strategy.',
    // V2 task strategy
    // ── Endpoint editor: tabs / general fields (used to be hardcoded EN) ─
    'register.endpoint.tab.general': 'General',
    'register.endpoint.tab.request': 'Request',
    'register.endpoint.tab.body': 'Body',
    'register.endpoint.tab.responses': 'Responses',
    'register.endpoint.tab.model': 'Models',
    'register.endpoint.tab.billing': 'Billing',
    'register.endpoint.panel.streaming': 'Streaming',
    'register.endpoint.panel.streaming.sub':
      'Enable Server-Sent Events (SSE) or raw streaming responses.',
    'register.endpoint.field.supportsStreaming': 'Supports streaming',
    'register.endpoint.field.streamFormat': 'Stream format',
    'register.endpoint.streaming.on': 'Streaming on',
    'register.endpoint.streaming.off': 'Streaming off',
    'register.endpoint.field.displayName': 'Display name',
    'register.endpoint.field.displayName.placeholder': 'e.g. Get forecast',
    'register.endpoint.field.billingType': 'Billing type',
    'register.endpoint.field.billingType.directHint':
      'Forced to PER_CALL in DIRECT mode',
    'register.endpoint.field.billingType.pollingHint':
      'UPSTREAM_POLLING supports PER_CALL, PER_TOKEN or DYNAMIC',
    'register.endpoint.field.billingType.opt.perCall': 'Per call (flat USD)',
    'register.endpoint.field.billingType.opt.perToken': 'Per token (LLM)',
    'register.endpoint.field.billingType.opt.perResource':
      'Per resource (response.count)',
    'register.endpoint.field.billingType.opt.dynamic': 'Dynamic (rule engine)',
    'register.endpoint.field.description': 'Description',
    'register.endpoint.field.description.hint':
      'Shown in the API detail page and SDK docs.',
    'register.endpoint.field.description.placeholder':
      'What this endpoint returns…',
    'register.endpoint.panel.basics': 'Basics',
    'register.endpoint.panel.behavior': 'Behavior',
    'register.endpoint.panel.visibility': 'Visibility & access',
    'register.endpoint.field.hidden': 'Hide from public detail page',
    'register.endpoint.field.hidden.hint':
      "Endpoint stays callable but isn't listed publicly.",
    'register.endpoint.field.hidden.on': 'Hidden',
    'register.endpoint.field.hidden.off': 'Visible',
    'register.endpoint.field.oauth': 'Caller OAuth provider',
    'register.endpoint.field.oauth.hint':
      'Optional. Require the caller to authorize via this OAuth provider.',
    'register.endpoint.field.oauth.none': 'None (no OAuth required)',
    // Billing-pill short labels used by the batch dialog
    'register.endpoint.billing.short.perCall': 'per call',
    'register.endpoint.billing.short.perToken': 'per token',
    'register.endpoint.billing.short.perResource': 'per resource',

    // ── Endpoint editor: Request tab (path / header / query / private headers) ─
    'register.endpoint.request.pathParams.title': 'Path parameters',
    'register.endpoint.request.pathParams.subtitle':
      'Auto-extracted from path placeholders like :id or {id}.',
    'register.endpoint.request.pathParams.empty':
      'No path parameters detected.',
    'register.endpoint.request.pathParams.emptySub':
      'Add one by writing :name or {name} in the path.',
    'register.endpoint.request.headers.title': 'Header parameters',
    'register.endpoint.request.headers.add': 'Add header',
    'register.endpoint.request.headers.empty': 'No header parameters.',
    'register.endpoint.request.query.title': 'Query parameters',
    'register.endpoint.request.query.add': 'Add query',
    'register.endpoint.request.query.empty': 'No query parameters.',
    'register.endpoint.request.privateHeaders.title':
      'Endpoint private headers',
    'register.endpoint.request.privateHeaders.subtitle':
      'Extra upstream headers for this endpoint only. Merged on top of service-level private headers.',
    'register.endpoint.request.privateHeaders.add': 'Add header',
    'register.endpoint.request.privateHeaders.empty':
      'No endpoint private headers.',

    // ── Endpoint editor: shared table column headers ─
    'register.endpoint.col.name': 'Name',
    'register.endpoint.col.example': 'Example',
    'register.endpoint.col.type': 'Type',
    'register.endpoint.col.description': 'Description',
    'register.endpoint.col.required': 'Required',
    'register.endpoint.col.key': 'Key',
    'register.endpoint.col.value': 'Value',

    // ── Endpoint editor: Body tab ─
    'register.endpoint.body.type': 'Body type',
    'register.endpoint.body.empty': 'No request body.',
    'register.endpoint.body.emptySub':
      "GET / DELETE typically don't carry a body.",
    'register.endpoint.body.jsonExample': 'JSON example',
    'register.endpoint.body.jsonExample.hint':
      'Used as both the documented schema and the testing payload.',
    'register.endpoint.body.formFields': 'Form fields',
    'register.endpoint.body.formFields.hint':
      'Key/value rows. Used as the documented schema and the testing payload.',
    'register.endpoint.body.formFields.hintFile':
      'Key/value rows. Set a row Type to File for binary uploads (uploaded in the test console).',
    'register.endpoint.body.noFields': 'No fields yet.',
    'register.endpoint.body.fileUpload': '〔File · uploaded during test〕',
    'register.endpoint.body.addField': 'Add field',

    // ── Endpoint editor: Responses tab ─
    'register.endpoint.responses.add': 'Add example',
    'register.endpoint.responses.statusCode': 'Status code',
    'register.endpoint.responses.label': 'Label',
    'register.endpoint.responses.contentType': 'Content type',
    'register.endpoint.responses.body': 'Body',
    'register.endpoint.responses.empty': 'No response selected.',
    'register.endpoint.responses.emptySub': 'Click + Add example to start.',

    // ── Endpoint editor: Billing — per call / per resource ─
    'register.endpoint.billing.perCall.cost': 'Cost per call (USD)',
    'register.endpoint.billing.perCall.cost.hint':
      'Charged on every successful invocation.',
    'register.endpoint.billing.perCall.share': 'Your share',
    'register.endpoint.billing.perCall.share.hint':
      'Per-call billing → 80% to provider, 20% platform fee.',
    'register.endpoint.billing.perResource.cost': 'Cost per resource (USD)',
    'register.endpoint.billing.perResource.maxResources':
      'Estimated max resources / call',
    'register.endpoint.billing.perResource.maxResources.hint':
      'Used for the pre-deduction safety check.',
    'register.endpoint.billing.perResource.countPath':
      'Resource count JSON path',
    'register.endpoint.billing.perResource.countPath.hint':
      'e.g. "data.length" or "meta.total". The gateway reads this from the response to bill the actual count.',

    // ── Endpoint editor: Token JSON paths (PER_TOKEN) ─
    'register.endpoint.token.paths.title': 'Token JSON paths',
    'register.endpoint.token.paths.overrideSub':
      'Override the default paths for the {SPEC} protocol (leave blank to use defaults).',
    'register.endpoint.token.paths.customSub':
      'Where to find token counts in the upstream response. Pick a preset or customize.',
    'register.endpoint.token.paths.collapse': 'Collapse',
    'register.endpoint.token.inputPath': 'Input tokens path',
    'register.endpoint.token.outputPath': 'Output tokens path',
    'register.endpoint.token.modelPathResponse': 'Model path (response)',
    'register.endpoint.token.modelPathRequest': 'Model path (request)',
    'register.endpoint.token.cacheWritePath': 'Cache write path',
    'register.endpoint.token.cachePath.hint':
      'Optional. Anthropic / OpenRouter prompt caching.',
    'register.endpoint.token.cacheReadPath': 'Cache read path',
    'register.endpoint.token.cacheIncluded': 'Cache included in input count',
    'register.endpoint.token.cacheIncluded.hint':
      'Whether the upstream input / prompt_tokens count already includes cache-hit tokens. OpenAI family = included (system deducts before billing); Anthropic = not included (cache billed separately). Misconfiguring this double-charges or under-discounts the cache.',
    'register.endpoint.token.cacheIncluded.yes': 'Yes — already included',
    'register.endpoint.token.cacheIncluded.no': 'No — counted separately',
    'register.endpoint.token.cacheIncluded.autoBy':
      'Auto-determined by the {SPEC} protocol · ',
    'register.endpoint.token.cacheIncluded.autoIncluded':
      "This protocol's input/prompt_tokens already includes cache tokens; the system deducts them before billing",
    'register.endpoint.token.cacheIncluded.autoSeparate':
      "This protocol's input_tokens excludes cache; cache is billed separately",

    // ── Endpoint editor: Model pricing (PER_TOKEN) ─
    'register.endpoint.pricing.editJsonTitle':
      'Edit as JSON (per 1M tokens, paste-from-docs friendly)',
    'register.endpoint.pricing.addModel': 'Add model',
    'register.endpoint.pricing.cancel': 'Cancel',
    'register.endpoint.pricing.apply': 'Apply',
    'register.endpoint.pricing.subtitle':
      'Per 1M tokens. Add one row per upstream model. Use "default" as a fallback.',
    'register.endpoint.pricing.title': 'Model pricing',
    'register.endpoint.pricing.loadingEditor': 'Loading editor…',
    'register.endpoint.pricing.hintPrefix':
      'Hint: prices are USD per 1M tokens (e.g. ',
    'register.endpoint.pricing.hintMid': ' = $3 / 1M tokens). Optional: ',
    'register.endpoint.pricing.hintSuffix': '.',
    'register.endpoint.pricing.table.model': 'Model',
    'register.endpoint.pricing.table.input': 'Input /1M',
    'register.endpoint.pricing.table.output': 'Output /1M',
    'register.endpoint.pricing.table.cacheWrite': 'Cache write /1M',
    'register.endpoint.pricing.table.cacheRead': 'Cache read /1M',
    'register.endpoint.pricing.modelName.placeholder':
      'default / gpt-4o / claude-3-opus',
    'register.endpoint.pricing.tiers.title': 'Volume tiers (optional)',
    'register.endpoint.pricing.tiers.add': 'Add tier',
    'register.endpoint.pricing.tiers.empty': 'No volume tiers.',
    'register.endpoint.pricing.tiers.threshold': '≥ tokens',
    'register.endpoint.pricing.tiers.thresholdPlaceholder':
      'input tokens threshold',
    'register.endpoint.pricing.tiers.inputPlaceholder': 'input /1M',
    'register.endpoint.pricing.tiers.outputPlaceholder': 'output /1M',

    'register.endpoint.taskStrategy.label': 'Execution strategy',
    'register.endpoint.taskStrategy.hint':
      'Worker Blocking holds an HTTP connection while the upstream runs. Upstream Polling submits the job, releases the connection, and polls the upstream for completion — required for long-running providers like video generation.',
    'register.endpoint.taskStrategy.opt.workerBlocking':
      'Worker Blocking (V1, default)',
    'register.endpoint.taskStrategy.opt.upstreamPolling':
      'Upstream Polling (V2, for long jobs)',
    'register.endpoint.taskStrategy.upstreamProvider': 'Upstream provider',
    'register.endpoint.taskStrategy.upstreamProvider.placeholder':
      '— select a provider —',
    'register.endpoint.taskStrategy.fixedHold': 'Fixed hold amount (USD)',
    'register.endpoint.taskStrategy.fixedHold.hint':
      'Amount reserved per task (max ${MAX}). Leave blank to use provider default (PER_TOKEN) or auto-derive from costPerCall (PER_CALL).',
    'register.endpoint.taskStrategy.fixedHold.autoPerCall':
      'auto from costPerCall',

    // Endpoints section (master/detail header + filter + side-bar chips)
    'register.endpoints.title': 'Endpoints · {N}',
    'register.endpoints.add': 'Add endpoint',
    'register.endpoints.filter': 'Filter endpoints',
    'register.endpoints.none': 'No endpoints match.',
    'register.endpoints.unnamed': '(unnamed)',
    'register.endpoints.aiMapped': '{N} mapped',
    'register.endpoints.aiPending': '{N} pending',
    'register.endpoint.removeTitle': 'Remove endpoint',
    'register.endpoint.selectHint': 'Select an endpoint.',

    // AI Spec — endpoint tab
    'register.endpoint.tab.aiSpec': 'AI Spec',

    // AI Spec — detection banner (above tabs)
    'register.ai.banner.active': 'Marked as AI endpoint',
    'register.ai.banner.activeVia': '(via {BY})',
    'register.ai.banner.configure': 'Configure mappings →',
    'register.ai.banner.lowSuggest':
      "This path doesn't match a known AI spec, but PER_TOKEN billing suggests it may serve",
    'register.ai.banner.lowYes': 'Yes, treat as {SPEC}',
    'register.ai.banner.none':
      'Not detected as an AI endpoint. If it serves AI models, click here →',
    'register.ai.banner.noneCta': '+ Configure as AI endpoint',
    'register.ai.toast.detected': 'Detected as {SPEC} — "AI Spec" tab opened',

    // AI Spec — tab content
    'register.ai.interfaceSpec': 'Interface spec',
    'register.ai.interfaceSpec.sub': 'Which AI protocol this endpoint speaks.',
    'register.ai.interfaceSpec.detectedBy': 'Detected by: {BY}.',
    'register.ai.notAiEndpoint': 'Not an AI endpoint',
    'register.ai.notAiEndpointTitle': 'Mark as not an AI endpoint',
    'register.ai.spec.field': 'Spec',
    'register.ai.modelMappings': 'Model mappings',
    'register.ai.modelMappings.subPerToken':
      'Upstream model names auto-pulled from Per-token pricing rows. Pick each canonical to enable routing.',
    'register.ai.modelMappings.subPerCall':
      'List the upstream model names this endpoint serves. Pick a canonical for each to enable routing.',
    'register.ai.modelMappings.add': '+ Add model',
    'register.ai.modelMappings.errCatalog':
      'Failed to load canonical catalog: {MSG}',
    'register.ai.modelMappings.errSuggest':
      'Suggest service is unavailable; you can still pick manually from the full catalog dropdown. ({MSG})',
    'register.ai.modelMappings.emptyPerToken':
      'No model rows yet. Add some on the Billing → Per-token pricing panel; they show up here.',
    'register.ai.modelMappings.emptyPerCall':
      'No models. Click "+ Add model" to start.',
    'register.ai.modelMappings.col.upstream': 'Upstream model name',
    'register.ai.modelMappings.col.canonical': 'Canonical',
    'register.ai.modelMappings.pending': '— pending (no canonical) —',
    'register.ai.modelMappings.optgroup.suggested': 'Suggested',
    'register.ai.modelMappings.optgroup.all': 'All canonical models',
    'register.ai.modelMappings.topSuggestion': 'Top suggestion:',
    'register.ai.modelMappings.placeholderSynced':
      '(synced from pricing — click to override)',
    'register.ai.modelMappings.titleSynced':
      'Auto-synced from Per-token pricing. Type to override; this row will no longer follow pricing changes.',
    'register.ai.modelMappings.removeTitle': 'Remove',

    // AI Spec — pre-submit pending dialog
    'register.pending.title': '{N} unmapped model{S} found',
    'register.pending.body':
      "These models will be saved as pending and won't be routable until an admin confirms a canonical match. You can also click Cancel and assign them now in the AI Spec tab.",
    'register.pending.col.endpoint': 'Endpoint',
    'register.pending.col.spec': 'Spec',
    'register.pending.col.model': 'Model name',
    'register.pending.back': 'Go back & fix',
    'register.pending.submit': 'Submit with pending',

    // Async tasks — api detail
    'apidetail.tag.async': 'Async',
    'apidetail.tag.asyncRequired': 'Async only',
    'apidetail.tryit.async.toggle': 'Async mode',
    'apidetail.tryit.async.toggleHint': 'Submit as task, poll for result',
    'apidetail.free.title': 'Register an account to use free APIs',
    'apidetail.free.readyTitle': 'This API is free to use',
    'apidetail.free.signin': 'Use social sign-in to register or sign in, then create an API key in Console → API Keys and set it as XAPI_KEY. A Quick Start key alone does not unlock free APIs.',
    'apidetail.free.signinAction': 'Sign up / Sign in',
    'apidetail.free.complete': 'Your current account has not completed registration. Link your X account to unlock free APIs. You can keep using the same API key after linking.',
    'apidetail.free.completeAction': 'Link X to complete registration',
    'apidetail.free.existingAction': 'Sign in to an existing account',
    'apidetail.free.setKey': 'Your account can use free APIs. Create or select an API key in Console → API Keys, then set it as XAPI_KEY in Environment before sending a request.',
    'apidetail.free.setKeyAction': 'Set XAPI_KEY',
    'apidetail.free.switchKey': 'The API key used for this request belongs to an account that has not completed registration. Set XAPI_KEY to a key from your registered account, then try again.',
    'apidetail.free.switchKeyAction': 'Change XAPI_KEY',
    'apidetail.free.keysAction': 'Manage API keys',
    'apidetail.tryit.async.required': 'This endpoint runs in async-only mode.',
    'apidetail.tryit.async.submitted': 'Task submitted',
    'apidetail.tryit.async.polling': 'Polling task…',
    'apidetail.tryit.async.viewInConsole': 'View in Console',
    'apidetail.tryit.async.cancel': 'Stop polling',
    'apidetail.tryit.async.expiresIn': 'expires in',
    'apidetail.tryit.async.longJobHint':
      'Long jobs (e.g. video generation) typically take 60–120s. Polling continues automatically.',

    // Async tasks — console tab
    'console.nav.tasks': 'Async tasks',
    'console.tasks.title': 'Async tasks',
    'console.tasks.subtitle':
      'Long-running API calls submitted with X-XAPI-Async.',
    'console.tasks.signinSubtitle': 'Sign in to view your async tasks.',
    'console.tasks.empty': 'No tasks yet.',
    'console.tasks.emptyHint':
      'Try the Async toggle inside Try-It on any async-capable endpoint.',
    'console.tasks.keyPicker': 'API key',
    'console.tasks.filter.all': 'All',
    'console.tasks.filter.inProgress': 'In progress',
    'console.tasks.filter.completed': 'Completed',
    'console.tasks.filter.failed': 'Failed',
    'console.tasks.status.pending': 'Pending',
    'console.tasks.status.processing': 'Processing',
    'console.tasks.status.succeeded': 'Succeeded',
    'console.tasks.status.failed': 'Failed',
    'console.tasks.status.expired': 'Expired',
    'console.tasks.col.task': 'Task',
    'console.tasks.col.endpoint': 'Endpoint',
    'console.tasks.col.status': 'Status',
    'console.tasks.col.created': 'Created',
    'console.tasks.col.duration': 'Duration',
    'console.tasks.col.cost': 'Cost',
    'console.tasks.cost.pending': 'On completion',
    'console.tasks.cost.heldHint':
      'Held ${amount} during processing, settled to actual on completion',
    'console.tasks.col.key': 'Key',
    'console.tasks.detail.timing': 'Timing',
    'console.tasks.detail.calling': 'Endpoint',
    'console.tasks.detail.apiKey': 'API key',
    'console.tasks.detail.cost': 'Cost',
    'console.tasks.detail.result': 'Result',
    'console.tasks.detail.error': 'Error',
    'console.tasks.detail.fileExpired': 'File no longer available',
    'console.tasks.detail.download': 'Download',
    'console.tasks.detail.openImage': 'Open full image',
    'console.tasks.detail.copyId': 'Copy task id',
    'console.tasks.loadMore': 'Load more',
    'console.tasks.refresh': 'Refresh',
    'console.tasks.autoRefresh': 'Auto-refreshing while tasks are running',

    // ── Skill marketplace ──
    'skills.eyebrow': 'Skill Marketplace',
    'skills.title': 'Reusable agent workflows for xapi',
    'skills.subtitle':
      'Browse reviewed skill packages that explain how agents should combine xapi capabilities, prompts, dependencies, and usage examples.',
    'skills.baseSkill': 'xAPI base skill',
    'skills.creatorConsole': 'Creator console',
    'skills.search': 'Search by name, slug, description',
    'skills.loadError': 'Failed to load skills',
    'skills.empty.title': 'No published skills yet',
    'skills.empty.body':
      'Once reviewers approve submissions, published skills will appear here with install commands and dependency notes.',
    'skills.empty.submit': 'Submit the first skill',
    'skills.card.noDesc': 'No description yet.',
    'skills.card.deps': '{n} deps',
    'skills.cat.all': 'All',
    'skills.cat.social': 'Social',
    'skills.cat.search': 'Search',
    'skills.cat.crypto': 'Crypto',
    'skills.cat.media': 'Media',
    'skills.cat.data': 'Data',
    'skills.cat.automation': 'Automation',
    'skills.cat.developer': 'Developer',
    'skills.cat.research': 'Research',
    'skills.detail.back': 'Back to marketplace',
    'skills.detail.install': 'Install',
    'skills.detail.copyInstall': 'Copy install command',
    'skills.detail.unavailable': 'Skill unavailable',
    'skills.detail.notFound': 'Skill not found',
    'skills.detail.openMarket': 'Open marketplace',
    'skills.detail.noReadme': 'No README content published.',
    'skills.detail.deps': 'Dependencies',
    'skills.detail.noDeps': 'No explicit xapi dependency declared.',
    'skills.detail.examples': 'Examples',
    'skills.detail.release': 'Release',
    'skills.detail.owner': 'Creator',
    'skills.detail.repository': 'Repository',
    'skills.detail.installMetric': 'Unique copy intents',
    'skills.detail.permissions': 'Permission hints',
    'skills.detail.permissionWrites': 'External writes',
    'skills.detail.permissionCredits': 'May spend credits',
    'skills.detail.permissionPersonal': 'Personal data',
    'skills.creator.eyebrow': 'Creator Console',
    'skills.creator.title': 'Submit a skill package',
    'skills.creator.subtitle':
      'Start from a GitHub URL, folder, or zip containing SKILL.md; metadata and version are parsed first, then you confirm and submit.',
    'skills.creator.marketplace': 'Marketplace',
    'skills.creator.apiKeyNote':
      'API-key sessions can submit skills only when this key has skill:submit enabled.',
    'skills.creator.entityNote':
      'Skill submission requires a verified entity account or an API key with skill:submit enabled.',
    'skills.creator.signInTitle': 'Sign in to submit skills',
    'skills.creator.signInBody':
      'Skill creation needs an authenticated entity account because submissions create review and publish audit records.',
    'skills.creator.signIn': 'Sign in',
    'skills.creator.step1': '1. Choose package',
    'skills.creator.name': 'Name',
    'skills.creator.slug': 'Slug',
    'skills.creator.description': 'Description',
    'skills.creator.categories': 'Categories, comma separated',
    'skills.creator.tags': 'Tags, comma separated',
    'skills.creator.createDraft': 'Create draft',
    'skills.creator.step2': '2. Confirm metadata',
    'skills.creator.draft': 'Skill draft',
    'skills.creator.selectSkill': 'Select a skill',
    'skills.creator.packageZip': 'Package zip',
    'skills.creator.uploadZip': 'Upload zip',
    'skills.creator.githubUrl': 'GitHub URL',
    'skills.creator.importGitHub': 'Import GitHub',
    'skills.creator.parseGitHub': 'Parse GitHub',
    'skills.creator.parseFolder': 'Parse folder',
    'skills.creator.parseZip': 'Parse zip',
    'skills.creator.packageParsed': 'Parsed package',
    'skills.creator.sourceHint':
      'Start with a GitHub URL when available; folder and zip uploads are still supported for local packages.',
    'skills.creator.or': 'or',
    'skills.creator.folderTab': 'Folder',
    'skills.creator.chooseFolder': 'Choose a skill folder',
    'skills.creator.multiSkill':
      'This folder contains multiple skills. Upload one skill at a time — choose a folder with a single SKILL.md.',
    'skills.creator.noSkillFound': 'No SKILL.md found in the selected folder.',
    'skills.creator.filesInSkill': 'Files in this skill',
    'skills.creator.skillMdRoot': 'SKILL.md at root',
    'skills.creator.uploadFolder': 'Upload skill',
    'skills.creator.missing': 'Missing',
    'skills.creator.validatedVersion': 'Validated version',
    'skills.creator.noValidated': 'No validated package selected',
    'skills.creator.metadataWaiting': 'Waiting for package parsing',
    'skills.creator.metadataParsing': 'Parsing package',
    'skills.creator.metadataLocked':
      'Metadata becomes editable after the package is validated.',
    'skills.creator.parsedPackageStatus': 'Package parsed: {version}',
    'skills.creator.version': 'Version',
    'skills.creator.slugChecking': 'Checking slug availability…',
    'skills.creator.slugInvalid':
      'Slug must be 3-64 chars: lowercase letters, numbers, and hyphens.',
    'skills.creator.slugAvailable': 'Slug {slug} is available.',
    'skills.creator.slugOwnedByYou':
      'Slug {slug} is already yours ({status}); you can submit a new version.',
    'skills.creator.slugTaken':
      'Slug {slug} is already reserved by {owner} ({status}). Choose another slug.',
    'skills.creator.slugReserved':
      'Slug {slug} is reserved. Choose another slug.',
    'skills.creator.slugOwnerUnknown': 'another user',
    'skills.creator.reviewerNote': 'Reviewer note',
    'skills.creator.reviewerNotePh': 'Optional context for reviewers.',
    'skills.creator.submitReview': 'Submit review',
    'skills.creator.submitPackage': 'Submit for review',
    'skills.creator.mySkills': 'My skills',
    'skills.creator.mySkillsSub':
      'Latest versions and submissions for this account.',
    'skills.creator.refresh': 'Refresh',
    'skills.creator.colSkill': 'Skill',
    'skills.creator.colStatus': 'Status',
    'skills.creator.colVersions': 'Versions',
    'skills.creator.colSubmission': 'Recent submission',
    'skills.creator.colUpdated': 'Updated',
    'skills.creator.noDrafts': 'No drafts yet.',
    'skills.creator.submissionProgress': 'Submission progress',
    'skills.creator.stageSubmitted': 'Submitted',
    'skills.creator.stageAutoReview': 'Automated review',
    'skills.creator.stageHumanReview': 'Human review',
    'skills.creator.stagePublish': 'GitHub publish',
    'skills.creator.notSubmitted': 'Not submitted',
    'skills.creator.waiting': 'Waiting',
    'skills.creator.notStarted': 'Not started',
    'skills.creator.noReviewResult': 'No result yet',
    'skills.creator.noPublishJob': 'Created after approval',
    'skills.creator.openCi': 'Open GitHub check',
    'skills.creator.openCommit': 'Open commit',
    'skills.status.draft': 'Draft',
    'skills.status.submitted': 'Submitted',
    'skills.status.in_review': 'In review',
    'skills.status.auto_passed': 'Auto review passed',
    'skills.status.auto_failed': 'Auto review failed',
    'skills.status.pending': 'Pending',
    'skills.status.pending_human': 'Waiting for human review',
    'skills.status.approved': 'Approved',
    'skills.status.needs_changes': 'Needs changes',
    'skills.status.rejected': 'Rejected',
    'skills.status.publishing': 'Publishing',
    'skills.status.published': 'Published',
    'skills.status.queued': 'Queued',
    'skills.status.running': 'Pushing to GitHub',
    'skills.status.verifying': 'Running GitHub checks',
    'skills.status.waiting_pr_merge': 'Waiting for PR merge',
    'skills.status.success': 'Published',
    'skills.status.failed': 'Publish failed',
    'skills.status.blocked': 'Publish blocked',
    // ===== Workers console (workers.jsx, WorkersWorkspace, billing, monitoring) =====
    'wk.id.label': 'Worker ID',
    'wk.id.hint': 'xAPI instance identifier, used for API, CLI and log lookups. Not an API key.',
    'wk.id.copy': 'Copy',
    'wk.id.copied': 'Copied',
    'wk.id.copyAria': 'Copy Worker ID',
    'wk.id.copiedStatus': 'Worker ID copied',
    'wk.id.copyFailed': 'Clipboard unavailable — select the Worker ID above and copy it manually.',
    'wk.h.bindings': 'Bindings',
    'wk.h.noBindings': 'No resources bound',
    'wk.url.copy': 'Copy URL',
    'wk.url.copyFallback': 'Copy fallback URL',
    'wk.url.copied': 'Copied',
    'wk.url.none': 'No public endpoint',
    'wk.url.fallback': 'Fallback route',
    'wk.url.provisioning': 'DNS/TLS provisioning',
    'wk.s.rateUnknown': 'rate basis unknown',
    'wk.l.privacy': 'Request bodies and query strings are never captured.',
    'wk.resUse.kv_namespace': 'Low-latency config, cache and session indexes.',
    'wk.resUse.d1_database': 'Relational SQL data. You own the schema and migrations.',
    'wk.resUse.r2_bucket': 'Files and generated output. Empty it before deleting.',
    'wk.resUse.durable_object': 'Strongly consistent per-object state and coordination.',
    'wk.resUse.queue': 'At-least-once async delivery. Make handlers idempotent.',
    'wk.resUse.workflow': 'Durable multi-step runs, retried step by step.',
    'wk.tab.health': 'Health',
    'wk.tab.resources': 'Resources',
    'wk.tab.spend': 'Spend',
    'wk.tab.logs': 'Logs',
    'wk.tab.settings': 'Settings',
    'wk.env': 'Environment',
    'wk.refresh': 'Refresh',
    'wk.refreshing': 'Refreshing…',
    'wk.visit': 'Open',
    'wk.range.1h': 'Last hour',
    'wk.range.24h': 'Last 24 hours',
    'wk.range.7d': 'Last 7 days',
    'wk.deployed': 'Deployed',
    'wk.notDeployed': 'Not deployed',
    'wk.unavailable': 'Data unavailable',
    'wk.h.availability': 'Availability',
    'wk.h.availabilityNote': 'Requests without 5xx',
    'wk.h.success': 'Success rate',
    'wk.h.successNote': '2xx/3xx; 4xx counted as failed',
    'wk.h.p95': 'P95 latency',
    'wk.h.p50Note': 'P50 {value}',
    'wk.h.requests': 'Requests',
    'wk.h.requestsNote': 'Observed window',
    'wk.h.sampledNote': '{count} sampled rows',
    'wk.h.errors': '5xx errors',
    'wk.h.errorsNote': '{count} runtime exceptions',
    'wk.h.release': 'Current release',
    'wk.h.releaseNote': 'Running for {age}',
    'wk.h.noRelease': 'No active release',
    'wk.h.spendToday': 'Spend today',
    'wk.h.budgetLeft': 'Budget left',
    'wk.h.budgetNote': 'Daily limit {limit}',
    'wk.h.traffic': 'Traffic & stability',
    'wk.h.noTraffic': 'No telemetry yet',
    'wk.h.legendOk': 'No 5xx',
    'wk.h.legendErr': 'Contains 5xx',
    'wk.h.lastTelemetry': 'Last telemetry {age} ago',
    'wk.h.endpoints': 'Endpoints',
    'wk.h.endpointsNote': 'Top 8 by traffic',
    'wk.h.col.endpoint': 'Endpoint',
    'wk.h.col.requests': 'Requests',
    'wk.h.col.4xx': '4xx',
    'wk.h.col.5xx': '5xx',
    'wk.h.col.p95': 'P95',
    'wk.h.noEndpoints': 'No endpoint data in this window',
    'wk.h.domain': 'DNS / TLS',
    'wk.h.retryDomain': 'Retry',
    'wk.r.none': 'No resources in {env}',
    'wk.r.usage': 'Usage',
    'wk.r.spend': 'Spend',
    'wk.r.location': 'Location',
    'wk.r.logs': 'Logs',
    'wk.r.delete': 'Delete',
    'wk.r.r2Continue': 'Continue initialization',
    'wk.r.r2Retry': 'Retry creation',
    'wk.r.r2Pending': 'R2 initialization is pending. Continue initialization to check progress; the existing bucket and funds are retained.',
    'wk.r.r2Review': 'R2 event delivery needs investigation. The bucket is retained; contact support rather than creating another bucket.',
    'wk.r.r2RefreshFailed': 'Could not refresh resources. Refresh the page before retrying the same binding.',
    'wk.r.back': 'All resources',
    'wk.r.tab.monitor': 'Monitoring',
    'wk.r.tab.spend': 'Spend',
    'wk.r.tab.logs': 'Logs',
    'wk.r.noUsage': 'No usage reported for this resource',
    'wk.r.noSpend': 'No charges attributed to this resource',
    'wk.r.logsHint': 'Worker logs are not filtered by resource. Search the binding name to narrow results.',
    'wk.r.openLogs': 'Open Worker logs',
    'wk.r.metric': 'Metric',
    'wk.r.thisPeriod': 'This period',
    'wk.s.balance': 'Spendable balance',
    'wk.s.byResource': 'Spend by resource',
    'wk.s.ledger': 'Charges',
    'wk.s.noLedger': 'No charges in this snapshot',
    'wk.s.col.time': 'Time',
    'wk.s.col.item': 'Item',
    'wk.s.col.qty': 'Quantity',
    'wk.s.col.price': 'Unit price',
    'wk.s.col.amount': 'Amount',
    'wk.s.noBreakdown': 'No resource spend reported yet',
    'wk.l.calls': 'Calls',
    'wk.l.logs': 'Logs',
    'wk.l.filter': 'Filter path, request or trace…',
    'wk.l.outcome.all': 'All',
    'wk.l.outcome.success': 'Success',
    'wk.l.outcome.error': 'Error',
    'wk.l.empty': 'No {view} yet',
    'wk.l.noMatch': 'No events match the current filters',
    'wk.l.sampled': 'High-volume events are sampled by Cloudflare',
    'wk.set.deploy': 'Deployments',
    'wk.set.schedules': 'Scheduled tasks',
    'wk.set.env': 'Environment settings',
    'wk.set.budget': 'Daily budget (USD)',
    'wk.set.save': 'Save',
    'wk.res.KV_NAMESPACE': 'KV namespace',
    'wk.res.D1_DATABASE': 'D1 database',
    'wk.res.R2_BUCKET': 'R2 bucket',
    'wk.res.DURABLE_OBJECT': 'Durable Object',
    'wk.res.QUEUE': 'Queue',
    'wk.res.WORKFLOW': 'Workflow',
    'wk.m.WORKER_REQUEST': 'Requests',
    'wk.m.WORKER_CPU_MS': 'CPU time',
    'wk.m.KV_READ': 'Reads',
    'wk.m.KV_WRITE': 'Writes',
    'wk.m.KV_DELETE': 'Deletes',
    'wk.m.KV_LIST': 'Lists',
    'wk.m.KV_STORAGE_GB_HOUR': 'Storage',
    'wk.m.D1_ROWS_READ': 'Rows read',
    'wk.m.D1_ROWS_WRITTEN': 'Rows written',
    'wk.m.D1_STORAGE_GB_HOUR': 'Storage',
    'wk.m.R2_CLASS_A_OPERATION': 'Write ops',
    'wk.m.R2_CLASS_B_OPERATION': 'Read ops',
    'wk.m.R2_IA_CLASS_A_OPERATION': 'Write ops (IA)',
    'wk.m.R2_IA_CLASS_B_OPERATION': 'Read ops (IA)',
    'wk.m.R2_IA_RETRIEVAL_BYTE': 'Retrieved (IA)',
    'wk.m.R2_STORAGE_GB_DAY': 'Storage',
    'wk.m.DO_REQUEST': 'Requests',
    'wk.m.DO_ROWS_READ': 'Rows read',
    'wk.m.DO_ROWS_WRITTEN': 'Rows written',
    'wk.m.DO_DURATION_GB_SECOND': 'Duration',
    'wk.m.DO_STORAGE_GB_HOUR': 'Storage',
    'wk.m.QUEUE_OPERATION': 'Operations',
    'wk.m.WORKFLOW_INVOCATION': 'Runs',
    'wk.m.WORKFLOW_STEP': 'Steps',
    'wk.m.WORKFLOW_CPU_MS': 'CPU time',
    'wk.m.WORKFLOW_STORAGE_GB_HOUR': 'Storage',
  },

  zh: {
    'api.referencePrices.title': '模型参考价格',
    'api.referencePrices.explanation': '按上游实际费用 × {ratio} 收费。以下为美元/百万 tokens 的参考单价，最终金额按响应费用结算。',
    'api.referencePrices.model': '模型',
    'api.referencePrices.input': '输入 / 百万 tokens',
    'api.referencePrices.output': '输出 / 百万 tokens',
    'api.referencePrices.updated': '参考价更新时间',
    'api.referencePrices.expand': '查看全部 {count} 个模型',
    'api.referencePrices.collapse': '收起',
    'api.referencePrices.rules': '查看计费规则',
    'api.referencePrices.official': 'OpenRouter 官方参考价（输入 / 输出）',
    'api.referencePrices.audioInput': '输入', 'api.referencePrices.audioOutput': '输出',
    'api.referencePrices.characterExplanation': '现有字符费率与官方参考价分别展示，最终金额遵循现有计费规则。',
    'api.referencePrices.audioUnknownExplanation': '官方参考单价按各行所示单位展示，实际收费请查看计费规则。',
    'api.referencePrices.audioExplanation': '参考单价按各行所示单位展示，按上游实际费用 × {ratio} 结算；单位不明确时不报价。',
    'api.referencePrices.unknownExplanation': '以下为美元/百万 tokens 的官方参考价，实际收费请查看计费规则。',
    // ===== 收益 / 提现 (earnings.jsx) =====
    'earnings.title': '我的收益',
    'earnings.loading': '加载中…',
    'earnings.loadError': '收益数据加载失败，请重试。',
    'earnings.wd.REQUESTED': '待审批',
    'earnings.wd.APPROVED': '待出款',
    'earnings.wd.QUEUED': '出款中',
    'earnings.wd.BROADCAST': '出款中',
    'earnings.wd.CONFIRMED': '已到账',
    'earnings.wd.REJECTED': '已驳回',
    'earnings.wd.FAILED': '失败',
    'earnings.wd.CANCELED': '已撤销',
    'earnings.earn.PENDING': '待结算',
    'earnings.earn.SETTLED': '已结算',
    'earnings.gate.loginTitle': '登录后查看收益与提现',
    'earnings.gate.loginCta': '去登录',
    'earnings.gate.notProviderTitle': '你还不是服务商',
    'earnings.gate.notProviderDesc':
      '把你的 API 发布到市场，被调用后产生的收益会出现在这里，可提现。',
    'earnings.gate.notProviderCta': '发布 API',
    'earnings.blocked.title': '提现已被限制',
    'earnings.blocked.reasonSep': '：{reason}',
    'earnings.blocked.body':
      '。当前无法发起提现申请，请通过电报群联系管理员处理。',
    'earnings.blocked.contact': '联系管理员 ↗',
    'earnings.withdrawable': '可提现收益',
    'earnings.totalNet': '总收益（净）',
    'earnings.btn.blocked': '提现已限制',
    'earnings.btn.collapse': '收起',
    'earnings.btn.withdraw': '提现',
    'earnings.btn.blockedTitle': '提现已被限制，请联系管理员',
    'earnings.settledTotal': '已结算累计 {amt}（= 可提现来源）',
    'earnings.pendingMore': ' · 另有 {amt} 待结算，结算后自动转入可提现',
    'earnings.onlyProvider': ' · 仅服务商 API 收益可提现，不含充值 / 赠送余额',
    'earnings.minSuffix': ' · 单笔最低 {amt}',
    'earnings.maxSuffix': ' · 最高 {amt}',
    'earnings.periodEnd': '。',
    'earnings.form.amountLabel': '提现金额 (USDC)',
    'earnings.form.all': '全部',
    'earnings.form.amountHint': '可提现 {amt}',
    'earnings.form.addrLabel': '目标地址 (Base / EVM)',
    'earnings.form.addrHintEmpty': '接收 USDC 的 EVM 钱包地址（{network}）。',
    'earnings.form.addrValid': '✓ 有效地址：{addr}',
    'earnings.form.addrInvalid':
      '✕ 不是有效的 EVM 地址（需 0x 开头、共 42 位、校验和正确）。',
    'earnings.form.next': '下一步：核对信息',
    'earnings.form.cancel': '取消',
    'earnings.confirm.title': '请核对提现信息',
    'earnings.confirm.amount': '提现金额',
    'earnings.confirm.toAddress': '到账地址',
    'earnings.confirm.network': '网络 / 币种',
    'earnings.confirm.remaining': '到账后剩余',
    'earnings.confirm.warning':
      '⚠️ 链上出款不可逆。地址错误将导致资金永久丢失且无法找回 —— 请再次确认到账地址与你的钱包完全一致。提交后还需经人工审批才会出款。',
    'earnings.confirm.submit': '确认无误，提交提现',
    'earnings.confirm.submitting': '提交中…',
    'earnings.confirm.back': '返回修改',
    'earnings.network.baseMainnet': 'Base 主网',
    'earnings.network.baseSepolia': 'Base Sepolia 测试网',
    'earnings.err.amountGt0': '金额必须大于 0',
    'earnings.err.minPerTx': '单笔最低 {amt}',
    'earnings.err.maxPerTx': '单笔最高 {amt}',
    'earnings.err.exceed': '超出可提现收益',
    'earnings.toast.enterAmount': '请输入提现金额',
    'earnings.toast.invalidAddr': '目标地址不是有效的 EVM 地址',
    'earnings.toast.submitted': '提现已提交，等待审批',
    'earnings.toast.submitFail': '提交失败',
    'earnings.toast.canceled': '已撤销',
    'earnings.toast.cancelFail': '撤销失败',
    'earnings.tab.detail': '收益明细',
    'earnings.tab.records': '提现记录',
    'earnings.stat.monthly': '本月收益',
    'earnings.stat.pending': '待结算',
    'earnings.stat.pendingHint': '结算后转入可提现',
    'earnings.settlementNote': '待结算收益由结算任务处理，完成后转为已结算；到账时间可能有所延迟。',
    'earnings.stat.totalCalls': '总调用次数',
    'earnings.stat.activeCallers': '活跃调用者',
    'earnings.detailNote':
      '收益为扣除平台费后的净额。账号默认费率为 {pct}%，单服务可覆盖；每条明细展示实际抽成。',
    'earnings.noEarnings':
      '还没有收益记录 —— 你的 API 被调用并产生计费后，每一笔分成都会列在这里。',
    'earnings.col.api': 'API',
    'earnings.col.gross': '毛收入',
    'earnings.col.fee': '抽成',
    'earnings.col.net': '净收入',
    'earnings.col.status': '状态',
    'earnings.col.time': '时间',
    'earnings.pageTotal': '本页合计',
    'earnings.noWithdrawals': '还没有提现记录',
    'earnings.record.failedRequeued': '上次出款失败已退回待审批：{reason}',
    'earnings.record.rejectReason': '驳回理由：{reason}',
    'earnings.record.rejectNoNote': '（管理员未填写）',
    'earnings.record.failReason': '失败原因：{reason}',
    'earnings.record.cancel': '撤销',
    'nav.discover': '市场',
    'nav.mcp': 'MCP',
    'nav.skill': 'Skill',
    'nav.ai': 'AI 中转',
    'nav.sandbox': 'Sandbox',
    'nav.playground': 'Playground',
    'nav.capabilities': '能力',
    'nav.domains': '域名',
    'nav.workspace': '工作区',
    'nav.activity': '活动',
    'nav.review': '审核',
    'nav.menu': '菜单',
    'nav.account': '账户',
    'nav.referral': '推荐返佣',
    'nav.skillHub': 'Skill 市场',

    'auth.logIn': '登录',
    'auth.signOut': '退出登录',
    'auth.getStarted': '开始使用',
    'auth.dashboard': '控制台',
    'auth.getApiKey': '获取 API Key',

    'auth.login.title': '开始使用',
    'auth.login.subtitle':
      '登录后即可开始使用',
    'auth.login.quickstart': '快速开始（立即领取密钥）',
    'auth.login.quickstart.busy': '创建中…',
    'auth.login.twitter': '通过 Twitter 继续',
    'auth.login.twitter.busy': '正在跳转…',
    'auth.login.divider': '或使用已有密钥登录',
    'auth.login.apiKey.label': 'API Key',
    'auth.login.apiKey.placeholder': 'sk-…',
    'auth.login.apiKey.submit': '使用密钥登录',
    'auth.login.apiKey.busy': '登录中…',
    'auth.login.apiKey.required': '请先填入 API Key',
    'auth.login.referral.add': '+ 添加推荐码',
    'auth.login.referral.label': '推荐码（可选）',
    'auth.login.referral.placeholder': 'ref_…',

    'auth.wizard.eyebrow': '账号已创建',
    'auth.wizard.title': '保存你的 API Key',
    'auth.wizard.body':
      '此密钥{ONLY}请立即复制或下载——一旦丢失，账号将无法找回。',
    'auth.wizard.body.only': '仅展示一次。',
    'auth.wizard.warn':
      '请像对待密码一样对待它。服务端只保存加盐哈希，xapi.to 任何人都无法替你恢复。',
    'auth.wizard.keyLabel': '你的新 API Key',
    'auth.wizard.copy': '复制到剪贴板',
    'auth.wizard.download': '下载 .env',
    'auth.wizard.saved': '密钥已保存——连接 X 即可发布 API。',
    'auth.wizard.gateHint': '完成复制或下载后即可继续。',
    'auth.wizard.continueX': '通过 X 继续',
    'auth.wizard.continueX.busy': '正在准备 X 授权…',
    'auth.wizard.skip': '暂时跳过',
    'auth.wizard.footnote':
      '你可以继续以虚拟账号使用 xapi，稍后再去控制台绑定。',

    'topbar.search': '搜索 API、端点、文档…',
    'topbar.invite': '邀请赚返佣',
    'topbar.balance': '余额',
    'topbar.topUp': '充值',
    'topbar.rechargeBalance': '充值余额（用于调用 API）',
    'topbar.withdrawableEarnings': '可提现收益（API 收入）',
    'topbar.earnings': '收益 / 提现',
    'topbar.console': '控制台',
    'topbar.apiKeys': 'API 密钥',
    'topbar.myApis': '我的 API',
    'topbar.bindEmail': '绑定邮箱',
    'topbar.emailSettings': '邮箱',
    'topbar.emailUnbound': '邮箱未绑定',
    // 邮箱绑定
    'email.modal.title': '绑定邮箱',
    'email.modal.changeTitle': '更换邮箱',
    'email.modal.emailLabel': '邮箱地址',
    'email.modal.emailHint': '绑定邮箱用于账户找回与接收重要通知。',
    'email.modal.sendCode': '发送验证码',
    'email.modal.sending': '发送中…',
    'email.modal.codeLabel': '验证码',
    'email.modal.codeSentTo': '我们已向 {email} 发送 6 位验证码。',
    'email.modal.verify': '验证并绑定',
    'email.modal.verifying': '验证中…',
    'email.modal.resend': '重新发送',
    'email.modal.resendIn': '重新发送 ({s}s)',
    'email.modal.changeEmail': '更换邮箱',
    'email.modal.cancel': '取消',
    'email.modal.invalidEmail': '请输入有效的邮箱地址',
    'email.modal.invalidCode': '请输入 6 位验证码',
    'email.modal.sendFailed': '验证码发送失败',
    'email.modal.verifyFailed': '验证失败',
    'email.modal.success': '邮箱绑定成功',
    'email.reminder.title': '绑定你的邮箱',
    'email.reminder.body':
      '为账户加一层保障：绑定邮箱以开启账户找回并接收重要通知。',
    'email.reminder.later': '稍后再说',
    'email.reminder.bind': '立即绑定',

    'portal.hero.badge': 'xAPI 协议：AI 代理的通用层',
    'portal.hero.title': 'XAPI：AI 代理的 API 平台',
    'portal.hero.description':
      '一次付费，解锁所有 API —— 网页搜索、Twitter、Reddit、TikTok、短信、AI 模型和加密货币数据。',
    'portal.hero.descriptionMobile':
      '一把密钥调用所有 API —— 搜索、社交、短信、大模型、链上数据。',
    'portal.hero.exploreApis': '探索 API',
    // 首页 Version-A —— 分栏 hero（服务商流程）+ 数据条 + 4 业务大块。
    'portal.hero.becomeProvider': '成为服务商',
    'portal.hero.aDesc':
      '一次付费，调用所有 API —— 网页搜索、Twitter、AI 模型、加密数据；或上架你自己的 API，坐收每一次 Agent 调用。',
    'portal.hero.tl1': '让 Agent 学会上架',
    'portal.hero.tlb1': '把这行粘到 Cursor / Claude 等任意 Agent：',
    'portal.hero.tl2': '描述你的接口',
    'portal.hero.tlb2': '丢一份文档给 Agent，它自动注册并配好端点：',
    'portal.hero.tl3': '提交即上线赚钱',
    'portal.hero.tlb3': 'Agent 建服务、跑测试、提交审核，通过后按调用计费：',
    'portal.feat.stat.calls': '累计调用',
    'portal.feat.stat.apis': 'API · 端点',
    'portal.feat.stat.providers': '服务商',
    'portal.feat.stat.uptime': '在线率',
    'portal.feat.b1.chip': '用户',
    'portal.feat.b1.title': '一个密钥，一次付费，调用所有 API',
    'portal.feat.b1.desc':
      '一个网关、一个密钥、统一付费，接入市场上的各类 API。涵盖 AI、搜索、社交和加密数据，为 Agent 原生设计。',
    'portal.feat.b1.cta': '获取 API Key',
    'portal.feat.b2.chip': 'API 提供商',
    'portal.feat.b2.title': '让你的 API 持续创造收入',
    'portal.feat.b2.desc':
      '上架一次，每次调用都能获得收入。实时结算，随时提现。',
    'portal.feat.b2.cta': '成为服务商',
    'portal.feat.b3.title': '通过 Skill，让任何 Agent 在几分钟内接入 xAPI',
    'portal.feat.b3.desc':
      '安装 xAPI Skill，让 Agent 使用一个密钥自动发现、认证并调用各类 API。',
    'portal.feat.b3.cta': '了解 Skill',
    'portal.feat.b4.chip': 'AI',
    'portal.feat.b4.title': '一个端点，调用所有 AI 模型',
    'portal.feat.b4.desc':
      '跨服务商获得更优价格和智能路由，无需为不同模型分别接入。',
    'portal.feat.b4.cta': 'xAPI AI Router',
    'portal.feat.skill.line': '注册 api.example.com',
    'portal.feat.skill.ok': '已建服务 · 12 端点 · 已上线',

    'portal.howto.title': '如何使用 xAPI',
    'portal.howto.steps': '3 步 · ~60 秒',
    'portal.howto.s1.title': '让 Agent 学会 xAPI Skill',
    'portal.howto.s1.body': '粘贴到 Cursor / Claude 等任意 Agent：',
    'portal.howto.s1.cmd': '安装 xAPI skill: npx skills add xapi-labs/xapi-cli',
    'portal.howto.s2.title': '提供 API Key',
    'portal.howto.s2.body': '粘贴下面这句话，Agent 会自动帮你注册并拿到 Key：',
    'portal.howto.s2.example': '帮我注册一个 xAPI 账号，邀请码 {CODE}',
    'portal.howto.s3.title': '直接开跑',
    'portal.howto.s3.body': 'Agent 自动挑选合适的 API 并调用。例如：',
    'portal.howto.s3.example': '抓一下 Elon Musk 最新的 10 条推文',
    'portal.howto.skill': '什么是 Skill？',
    'portal.howto.orMcp': '或使用 MCP',

    'portal.popular.title': '精选生产级 API',
    'portal.popular.viewAll': '查看全部',
    'portal.popular.empty.title': '市场中还没有已发布的 API',

    // ---- Portal hero terminal (3-step CLI demo) ----
    'portal.kicker': '通用 API 网关',
    'portal.heroTerm.s1.title': '让你的 Agent 学会 xAPI Skill',
    'portal.heroTerm.s2.title': '设置 API Key',
    'portal.heroTerm.s3.title': '看一次真实调用',
    'portal.heroTerm.s3.userMsg': '查询 elonmusk 最新的 3 条推文',
    'portal.heroTerm.s3.respLine1':
      '{ "user": "elonmusk", "count": 3, "tweets": [',
    'portal.heroTerm.s3.respLine2':
      '  { "id":"1789…", "text":"raptor 3 hot fire", "likes":412300 },',
    'portal.heroTerm.s3.respLine3':
      '  { "id":"1789…", "text":"interesting times", "likes":156800 } ]',

    // ---- Portal sections ----
    'portal.section01.eyebrow': 'API 市场',
    'portal.section01.live': '已上线 {N} 个',
    'portal.section01.titleA': '生产级 API',
    'portal.section01.titleB': 'Agent 即用。',
    'portal.section01.viewAll': '查看全部',
    'portal.section01.empty.title': '暂无已发布的 API',
    'portal.section01.empty.body':
      '市场正在等待第一个 API。如果你是服务商，立即注册 —— 审核与首次调用通常在 24 小时内完成。',
    'portal.section01.empty.register': '注册 API',

    'portal.section02.eyebrow': '我们提供什么',
    'portal.section02.titleA': '一个 API Key',
    'portal.section02.titleB': '调用所有 API，按次付费。',

    'portal.cap.01.kicker': '路由',
    'portal.cap.01.titleA': '一把密钥。',
    'portal.cap.01.titleB': '所有 API。',
    'portal.cap.01.body':
      '内置 MCP 服务从单一端点解析任意工具 —— Claude、Cursor、自研 Agent —— 指向 mcp.xapi.to 即可。',
    'portal.cap.01.fact.live': '在线',
    'portal.cap.01.fact.tools': '500+ 各类接口',
    'portal.cap.01.fact.tail': 'MCP 就绪',

    'portal.cap.02.kicker': '计量',
    'portal.cap.02.titleA': '单跳代理。',
    'portal.cap.02.titleB': '约 30ms 开销。',
    'portal.cap.02.body':
      '边缘网关与上游同区部署。鉴权、计量、计费在同一次 TCP 往返中完成 —— 没有额外跳转。',
    'portal.cap.02.fact.head': 'p50',
    'portal.cap.02.fact.tail': '流式与非流式',

    'portal.cap.03.kicker': '计费',
    'portal.cap.03.titleA': '按次付费。',
    'portal.cap.03.titleB': '稳定币结算。',
    'portal.cap.03.body':
      '一份余额覆盖网关上所有 API。按次、按 token 或按资源 —— 转发前原子扣费,无月度门槛。',
    'portal.cap.03.fact.head': '起价',
    'portal.cap.03.fact.tail': 'USDC / USDT / USD',

    'portal.cap.04.kicker': '收益',
    'portal.cap.04.titleA': '服务商保留',
    'portal.cap.04.titleB': '80%。',
    'portal.cap.04.body':
      '上传 OpenAPI 规范，设定定价，点击发布。xapi 处理鉴权、计量、计费——每一次调用你都保留 80%。',
    'portal.cap.04.fact.share': '80% 分成',

    'portal.section03.eyebrow': '能力',
    'portal.section03.titleA': '所有能力',
    'portal.section03.titleB': 'Agent 即刻调用。',
    'portal.section03.summary':
      '一个网关覆盖 Agent 实际会跑的工作流——获取、补全、调用、生成、结算。',
    'portal.section03.point1': 'AI / 搜索 / 社交 / 链上',
    'portal.section03.point2': '按次 / token / 图片 / 免费',
    'portal.section03.point3': '鉴权 / 日志 / 配额 / 发现',
    'portal.section03.visualLive': '实时',
    'portal.section03.visualConnecting': '连接中…',
    'portal.section03.actionsCount': '{N} 个能力在线',
    'portal.section03.colCategory': '分类',
    'portal.section03.colActions': '能力数',
    'portal.section03.loading': '加载中…',
    'portal.section03.more': '+{N} 更多',

    'portal.audiences.eyebrow': '两种角色',
    'portal.audiences.titleA': '为调用方,',
    'portal.audiences.titleB': '也为服务商。',
    'portal.audiences.consumer.chip': '用户 / 调用方',
    'portal.audiences.consumer.title': '浏览并调用任何 API',
    'portal.audiences.consumer.desc':
      '一个网关、一把 key,调用市场里所有 API 与能力,Agent 开箱即用。',
    'portal.audiences.consumer.p1': '一把 key,统一网关',
    'portal.audiences.consumer.p2': '按调用计费,不锁定',
    'portal.audiences.consumer.p3': 'AI · 搜索 · 社交 · 链上',
    'portal.audiences.consumer.cta': '获取 API Key',
    'portal.audiences.provider.chip': '服务商',
    'portal.audiences.provider.title': '让你的 API 成为 Agent 的收入',
    'portal.audiences.provider.desc':
      '上架一次,赚取每一次调用。计费、鉴权、路由、打包全托管。',
    'portal.audiences.provider.p1': '实时结算,随时提现',
    'portal.audiences.provider.p2': '几分钟,API 变 Agent Skill',
    'portal.audiences.provider.p3': '网关托管计费与鉴权',
    'portal.audiences.provider.cta': '成为服务商',
    'portal.cta.eyebrow': '随时开始',
    'portal.cta.titleA': '获取 API Key',
    'portal.cta.titleB': '或者发布你的第一个端点。',
    'portal.cta.register': '注册 API',

    'portal.getstarted.eyebrow': '快速开始',
    'portal.getstarted.titleA': '两步',
    'portal.getstarted.titleB': '让 Agent 调用真实 API。',
    'portal.getstarted.s1.title': '安装 Skill',
    'portal.getstarted.s1.body':
      '一个 Agent Skill,让你的 AI 自动加载 xapi 全量能力清单。兼容 Claude Code、Cursor、Codex CLI、Gemini CLI 以及任何 MCP 客户端。',
    'portal.getstarted.s1.bodyMobile':
      '为 Claude Code、Cursor、Codex 等任意 MCP Agent 加载 xapi 能力清单。',
    'portal.getstarted.s1.cmd': 'npx skills add xapi-labs/xapi-cli',
    'portal.getstarted.s1.note': 'xAPI Skill 开源在 GitHub：{LINK}',
    'portal.getstarted.s2.title': '开始使用',
    'portal.getstarted.s2.body':
      '输入 /xapi 加上你的诉求,Skill 自动选择合适的 action、补全参数,经 xapi 网关路由,按你的 Key 计费。',
    'portal.getstarted.s2.bodyMobile':
      '输入 /xapi + 需求，Skill 自动选 action，按 Key 计费。',
    'portal.getstarted.s2.ex1.cmd': '/xapi 搜索 @elonmusk 本周关于 AI 的推文',
    'portal.getstarted.s2.ex2.cmd': '/xapi 谷歌搜索「2026 年 AI Agent 框架」',
    'portal.getstarted.s2.ex3.cmd': '/xapi 租一个美国手机号注册 OpenAI',
    // Skill 页 —— 分角色上手引导（消费者 / 服务商）
    'skill.role.consumer': '消费者',
    'skill.role.provider': '服务商',
    'skill.role.subConsumer': '让你的 Agent 调用任意 API —— 一把 Key，按 Key 计费。',
    'skill.role.subProvider': '让你的 Agent 直接注册、定价、管理你自己的 API。',
    'skill.provider.install.cmd': 'npx skills add xapi-labs/xapi-provider',
    'skill.provider.s1.body':
      '服务商 Skill 让你的 Agent 直接在 CLI 里注册、定价、管理你在 xapi 上的 API。兼容 Claude Code、Cursor、Codex CLI、Gemini CLI 及任何 MCP 客户端。',
    'skill.provider.s1.note': 'xAPI Provider Skill 开源在 GitHub：{LINK}',
    'skill.provider.s2.body':
      '输入 /xapi 加上你要发布的内容，Skill 会搭好服务、设置定价并送审。',
    'skill.provider.s2.ex1.cmd': '/xapi 把 api.weather.com 注册成一个服务',
    'skill.provider.s2.ex2.cmd': '/xapi 给我的 weather 接口定价 $0.002/次',
    'skill.provider.s2.ex3.cmd': '/xapi 提交我的 API 进入审核',
    'skill.c.keystep.title': '拿到 API Key 并交给 Agent',
    'skill.p.keystep.title': '注册授权 API Key 并交给 Agent',
    'skill.scopes.more': '展开全部权限',
    'skill.scopes.less': '收起',
    'skill.title.consumer': '让你的 Agent 调用任意 API',
    'skill.title.provider': '让你的 Agent 发布你的 API',
    'skill.quickref': '快速开始',
    'skill.quickref.install': '安装',
    'skill.quickref.repo': '开源：{LINK}',
    'skill.quickref.worksWith': '兼容客户端',
    // — 消费者引导 —
    'skill.c.s1.title': '拿到 API Key',
    'skill.c.s1.body': '注册后系统自动生成你的 Key（sk-…）—— 调用就按它计费。在控制台里获取，或首次使用时让 Skill 帮你跑 `xapi register` 自动建号。',
    'skill.c.s1.cta': '获取 API Key',
    'skill.c.s2.title': '安装 Skill',
    'skill.c.s2.body': '一个 Agent Skill，让你的 AI 自动加载 xapi 全量能力清单。兼容 Claude Code、Cursor、Codex CLI、Gemini CLI 及任何 MCP 客户端。',
    'skill.c.s2.note': 'xAPI Skill 开源在 GitHub：{LINK}',
    'skill.c.s3.title': '把 Key 交给 Agent',
    'skill.c.s3.body': '首次调用时把 sk-… Key 粘进 Agent 对话 —— shell 环境变量到不了另一个进程里的 LLM。若直接用 CLI，可 `xapi config set apiKey=sk-…`。',
    'skill.c.s4.title': '开始调用',
    'skill.c.s4.body': '输入 /xapi 加上你的诉求，Skill 自动选 action、补参数，经 xapi 网关路由，按你的 Key 计费。',
    'skill.c.s4.ex1': '/xapi 搜索 @elonmusk 本周关于 AI 的推文',
    'skill.c.s4.ex2': '/xapi 谷歌搜索「2026 年 AI Agent 框架」',
    'skill.c.s4.ex3': '/xapi 租一个美国手机号注册 OpenAI',
    // — 服务商引导 —
    'skill.p.s1.title': '注册并授权 API Key',
    'skill.p.s1.body': '需要实体账号 —— 用 X 登录。然后打开 控制台 → API Keys → 权限，勾选下面这些 scope（默认全关，缺 scope 时 agent 会收到 403）。',
    'skill.p.s1.cta': '打开 API Keys',
    'skill.p.s1.scopesLabel': '服务商 Skill 需要的权限',
    'skill.p.s1.scope.create': 'service:create —— 注册 / 创建服务',
    'skill.p.s1.scope.read': 'service:read —— 读服务与审核报告',
    'skill.p.s1.scope.update': 'service:update —— 编辑契约',
    'skill.p.s1.scope.version': 'version:create —— 新大版本 / 工作修订',
    'skill.p.s1.scope.publish': 'service:publish —— 送审 / 上线',
    'skill.p.s2.title': '安装 Provider Skill',
    'skill.p.s2.body': '服务商 Skill 教会你的 Agent 完整生命周期：注册 → 配置 → 自动审核 → 发布 → 版本 → 回滚。兼容任何 MCP 客户端。',
    'skill.p.s2.note': 'xAPI Provider Skill 开源在 GitHub：{LINK}',
    'skill.p.s3.title': '把 Key 交给 Agent',
    'skill.p.s3.body': '把带 scope 的 sk-… Key 交给 Agent，然后直接描述要发布什么 —— Skill 帮你搞定 OpenAPI、定价和审核流程。',
    'skill.p.s4.title': '上线并赚钱',
    'skill.p.s4.body': '注册你的 OpenAPI spec、提交自动审核，发布后每次调用都能获得收入。实时结算，随时提现。',
    'skill.p.s4.ex1': '/xapi 把这份 openapi.json 注册成一个服务',
    'skill.p.s4.ex2': '/xapi 给我的 weather 接口定价 $0.002/次',
    'skill.p.s4.ex3': '/xapi 提交我的 API 审核，然后发布',
    'portal.getstarted.cli.title': '安装 CLI',
    'portal.getstarted.cli.badge': '可选',
    'portal.getstarted.cli.body':
      '在 Skill 之外直接调用 xapi:CI、脚本、临时管道都可。同一套鉴权、同一份用量,不依赖编辑器。',
    'portal.getstarted.cli.cmd': 'npm i -g xapi-to',
    'portal.getstarted.cli.note':
      '安装后用 `npx xapi-to list` 浏览能力，或在任何 shell 里 `xapi-to run twitter.search --q "@elonmusk AI"` 直接调用。',
    'portal.getstarted.stay.title': '保持关注',
    'portal.getstarted.stay.body':
      '新能力、新供应方、价格调整每周更新。关注 @xapi_to 推特账号，获取更新日志和事故复盘。',
    'portal.getstarted.stay.cta': '关注 @xapi_to 推特账号',
    'portal.getstarted.expand': '展开',
    'portal.getstarted.collapse': '收起',

    'portal.footer.copyright': '© 2026 xAPI',
    'portal.footer.twitter': 'X (推特)',
    'portal.footer.telegram': 'Telegram',
    'portal.footer.github': 'GitHub',

    // ---- AI 中转站 营销页 ----
    'ai.kicker': 'ai 模型网关',
    'ai.hero.eyebrow': '一个密钥 · 所有模型',
    'ai.hero.titleA': '一个 API Key',
    'ai.hero.titleB': '所有模型 · 所有协议。',
    'ai.hero.description':
      '任何 AI 请求都指向 https://ai.xapi.to —— 一个 Key，最优价格，在各上游之间智能路由。',
    'ai.hero.descriptionMobile':
      'Anthropic 兼容的智能路由 —— 一个 Key，自动选优 + 故障切换。',
    'ai.hero.cta.primary': '获取 API Key',
    'ai.hero.cta.secondary': '查看文档',
    'ai.hero.cta.models': '查看全部模型',
    'ai.hero.baseUrl.label': 'ANTHROPIC_BASE_URL',
    'ai.hero.term.userMsg': '重构这个函数',
    'ai.hero.term.baseUrlNote': '唯一要改的一行',
    'ai.hero.term.cc.note': '# Claude Code → xapi：改一个环境变量，零改码',
    'ai.hero.term.codex.then': '# 然后启动',
    'ai.hero.term.copy': '复制',
    'ai.hero.term.copied': '已复制',
    'ai.hero.term.routeLabel': '自动路由 · 选最优上游',
    'ai.hero.term.cheapestHealthy': '（健康且最便宜）',
    'ai.hero.term.healthy': '健康',
    'ai.hero.term.save': '省 {P}%',
    'ai.hero.term.metric1.k': 'TTFT',
    'ai.hero.term.metric2.k': 'tokens',
    'ai.hero.term.metric3.k': '消耗',

    // 数据条 —— Section 01
    'ai.stats.providers': '已对接 {N} 家上游中转',
    'ai.stats.calls': '近 30 天 {N} 次调用',
    'ai.stats.models': '{N}+ 模型 · 实时定价',
    'ai.stats.compat': '原生 OpenAI · Claude · Responses',

    // Section 02 —— 真实定价
    'ai.catalog.eyebrow': '实时定价',
    'ai.catalog.categoryFilter': '模型类型',
    'ai.catalog.category.chat': '聊天', 'ai.catalog.category.decisions': '决策', 'ai.catalog.category.tts': '语音合成', 'ai.catalog.category.stt': '语音转写',
    'ai.catalog.actualCost': '估算 · 按实际用量', 'ai.catalog.dynamicCost': '动态计费',
    'ai.catalog.titleA': '真实价格',
    'ai.catalog.titleB': '按上游 · 按模型。',
    'ai.catalog.summary':
      '查看聊天与决策模型，价格按每百万 token 展示。动态报价按上游费率和配置的溢价估算，实际费用按请求用量结算。',
    'ai.catalog.colUpstream': '上游',
    'ai.catalog.colModel': '模型',
    'ai.catalog.colFamily': '厂商',
    'ai.catalog.colIn': '输入',
    'ai.catalog.colOut': '输出',
    'ai.catalog.colInUpstream': '输入',
    'ai.catalog.colOutUpstream': '输出',
    'ai.catalog.colNote': '备注',
    'ai.catalog.colProviders': '上游数',
    'ai.catalog.colCalls': '调用量 / 30D',
    'ai.catalog.tokensVia': '{N} tokens',
    'ai.catalog.upstreamsTitle': '上游渠道 · 价格升序',
    'ai.catalog.noUpstreams': '暂无上游明细',
    'ai.catalog.viewCards': '卡片',
    'ai.catalog.viewTable': '表格',
    'ai.catalog.viewList': '排行',
    'ai.catalog.via': '来自',
    'ai.catalog.in': '输入',
    'ai.catalog.out': '输出',
    'ai.catalog.upstreamsN': '{N} 个上游',
    'ai.catalog.filterAll': '全部',
    'ai.catalog.vendorMore': '+{N} 更多',
    'ai.catalog.vendorLess': '收起',
    'ai.catalog.allHint': '去重模型 · 单上游最优组合价',
    'ai.catalog.bestVia': '来自 {U}',
    'ai.catalog.official': '官方价',
    'ai.catalog.discountHint': 'OpenRouter 官方价',
    'ai.catalog.search': '搜索模型 / 厂商',
    'ai.catalog.showAll': '查看全部模型 →',
    'ai.catalog.shownCount': '显示 {X} / {Y}',
    'ai.catalog.noMatch': '没有匹配的模型',

    // Section 03 —— 我们的优势
    'ai.cap.eyebrow': '我们的价值',
    'ai.cap.titleA': '为什么用 xapi 中转',
    'ai.cap.titleB': '而不是自己拼。',
    'ai.cap.01.kicker': '覆盖广',
    'ai.cap.01.title': '所有厂商，所有模型。',
    'ai.cap.01.body':
      'OpenAI、Anthropic、Google、DeepSeek、Meta、Alibaba、xAI、Mistral —— 80+ 模型藏在同一个端点后。新模型上线，加 model id 即可，业务代码完全不变。',
    'ai.cap.01.fact': '80+ 模型 · 9 个厂商家族',
    'ai.cap.02.kicker': '稳定性',
    'ai.cap.02.title': '多上游池，自动切换。',
    'ai.cap.02.body':
      '每个模型同时挂 2~4 个独立上游。某家限流或 5xx，下一家立即接管 —— 业务侧从来看不到故障。',
    'ai.cap.02.fact': '99.97% 成功率 · 自动切换',
    'ai.cap.03.kicker': '速度',
    'ai.cap.03.title': '边缘热连接池。',
    'ai.cap.03.body':
      '每个上游都维持热连接，热路径上没有冷启动 TLS。流式直通透传，网关额外开销 ~20ms，全程零缓冲。',
    'ai.cap.03.fact': 'p50 TTFT ~190ms · 流式 SSE',
    'ai.cap.04.kicker': '计费',
    'ai.cap.04.title': '按 token 付费，稳定币结算。',
    'ai.cap.04.body':
      '没有月度门槛、没有席位费。一次充值，所有模型按 1M token 单价 + 5% 网关费计费。USDC / USDT / USD 任选。',
    'ai.cap.04.fact': '无门槛 · 5% 网关费',

    // Section 04 —— 上游池
    'ai.upstream.eyebrow': '上游池 · 实时',
    'ai.upstream.titleA': '{N} 家在线中转',
    'ai.upstream.titleB': '合并成一个上游池。',
    'ai.upstream.summary':
      '聚合网关 ai.xapi.to 在背后调度的真实上游中转。当你调 /v1/messages 时，网关会按你选的策略挑一个最优上游。如果需要 OpenAI Chat Completions 或其他协议，也可以直连具体上游的 subdomain（例如 skyapi.ai.xapi.to/v1/chat/completions）。下面的数字来自 marketplace 实时数据。',
    'ai.upstream.callsLabel': '调用',
    'ai.upstream.empty.title': '暂无 AI 类目服务',
    'ai.upstream.empty.body':
      '当前 marketplace 还没有 AI 类目的服务。新的中转商上架后会自动出现在这里。',

    // 多协议原生支持
    'ai.compat.eyebrow': '多协议原生',
    'ai.compat.titleA': '智能路由，',
    'ai.compat.titleB': '直连上游。',
    'ai.compat.body':
      'Anthropic SDK 指向 https://ai.xapi.to 即获得自动路由 + 故障切换。需要 OpenAI Chat Completions 或 Responses 时，OpenAI SDK 指向具体上游的 subdomain —— 同一把 Key、同一份账单、不同协议。',
    'ai.compat.bullet1': 'ai.xapi.to/v1/messages —— 聚合 Anthropic 路由',
    'ai.compat.bullet2':
      'ai.xapi.to/{quality|speed|cost}/v1/messages —— 指定策略',
    'ai.compat.bullet3': '{upstream}.ai.xapi.to —— 直连上游用其它协议',
    'ai.compat.codeTitle': 'python · anthropic SDK（聚合）+ openai SDK（直连）',

    // 终极 CTA
    'ai.cta.eyebrow': '随时开始',
    'ai.cta.titleA': '获取 AI Key',
    'ai.cta.titleB': '60 秒内调用任意模型。',
    'ai.cta.primary': '获取 API Key',
    'ai.cta.secondary': '查看文档',

    // ---- Category labels ----
    'category.all': '全部',
    'category.aiModels': 'AI 模型',
    'category.aiServices': 'AI 服务',
    'category.social': '社交',
    'category.crypto': '加密货币',
    'category.data': '数据',
    'category.dataAnalysis': '数据分析',
    'category.search': '搜索',
    'category.media': '媒体',
    'category.devTools': '开发工具',
    'category.sandbox': '沙箱',
    'category.publicUtils': '通用工具',
    'category.infrastructure': '基础设施',

    // ---- Capability groups ----
    // 分组标签直接使用后端 meta.category 原值，这里只保留未分类时的回落文案。
    'cap.group.uncategorized': '未分类',

    'market.eyebrow': 'API 市场',
    'market.title': '发现 API',
    'market.searchPlaceholder': '搜索 500+ 各类接口',
    'market.results': '个结果',
    'market.category': '分类',
    'market.billing': '计费',
    'market.status': '状态',
    'market.verifiedOnly': '仅已认证',
    'market.filters': '筛选',
    'market.hideFilters': '收起筛选',
    'market.showFilters': '显示筛选',
    'market.applyFilters': '完成',
    'market.billing.perCall': '按次计费',
    'market.billing.perToken': '按 token 计费',
    'market.billing.freeTier': '免费',
    'market.sort.popular': '最受欢迎',
    'market.sort.new': '最近添加',
    'market.sort.latency': '最低延迟',
    'market.loadingApis': '加载 API 中…',
    'market.empty.title': '暂无已发布的 API',
    'market.empty.body':
      '市场目前为空。如果你是服务商，可以注册第一个 API，从首次调用开始获得收益。',
    'market.empty.register': '注册 API',
    'market.noMatch': '没有匹配的 API。换个分类或搜索词试试。',
    'market.favAdd': '收藏',
    'market.favRemove': '取消收藏',
    'market.col.name': '名称',
    'market.col.category': '分类',
    'market.col.endpoints': '端点',
    'market.col.calls': '调用量',
    'market.col.p95': 'p95',
    'market.col.uptime': '可用率',
    'market.callsSuffix': '次调用',

    'apiDetail.tab.try': '试用',
    'apiDetail.tab.endpoints': '端点',
    'apiDetail.tab.docs': '文档',
    'apiDetail.tab.changelog': '更新日志',
    'apiDetail.uptime': '可用率',
    'apiDetail.p95Latency': 'P95 延迟',
    'apiDetail.totalCalls': '总调用',
    'apiDetail.starred': '已收藏',
    'apiDetail.star': '收藏',
    'apiDetail.useThisApi': '使用此 API',
    'apiDetail.send': '发送请求',
    'apiDetail.abort': '中止请求',
    'apiDetail.setApiKey': '设置 XAPI_KEY 后发送',
    'apiDetail.queryParams': '查询参数',
    'apiDetail.pathParams': '路径参数',
    'apiDetail.headers': '请求头',
    'apiDetail.body': '请求体',
    'apiDetail.response': '响应',
    'apiDetail.exampleResponse': '示例响应',

    'common.copy': '复制',
    'common.copied': '已复制',
    'common.cancel': '取消',
    'common.confirm': '确认',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.save': '保存',
    'common.close': '关闭',
    'common.expand': '展开',
    'common.loading': '加载中…',
    'common.next': '下一页',
    'common.prev': '上一页',
    'common.page': '第',
    'common.signIn': '登录',
    'common.add': '添加',
    'common.remove': '移除',
    'common.refresh': '刷新',
    'common.actions': '操作',
    'common.name': '名称',
    'common.status': '状态',
    'common.created': '创建时间',
    'common.updated': '更新时间',
    'common.description': '描述',
    'common.lastDay': '最近 24 小时',
    'common.last7d': '最近 7 天',
    'common.last30d': '最近 30 天',
    'common.last90d': '最近 90 天',
    'common.retry': '重试',

    'console.nav.overview': '概览',
    'console.nav.keys': 'API 密钥',
    'console.nav.env': '环境变量',
    'console.nav.usage': '使用与日志',
    'console.nav.aiLogs': 'AI 调用日志',
    'console.nav.sandbox': 'Sandbox 实例',
    'console.nav.workers': 'Worker 实例',
    "console.keys.permissions.groups.workers.title": "Worker",
    "console.keys.permissions.workersRead.label": "读取 Worker",
    "console.keys.permissions.workersRead.desc": "查看 Worker、部署、资源、日志和账单",
    "console.keys.permissions.workersWrite.label": "管理 Worker",
    "console.keys.permissions.workersWrite.desc": "创建、部署、暂停、恢复和删除 Worker；包含读取权限",
    "console.keys.workers.access": "Worker 实例范围",
    "console.keys.workers.all": "账户下全部 Worker",
    "console.keys.workers.own": "仅此 Key 创建的 Worker",
    "console.keys.workers.selected": "自己创建的 + 指定 Worker",
    "console.keys.workers.accessNote": "该范围会同时限制列表、部署、资源、Secret、日志和删除操作。",
    "console.keys.workers.empty": "当前账户暂无可额外授权的 Worker。",
    "console.keys.workers.updated": "Worker 实例范围已更新",
    "console.keys.workers.failed": "Worker 实例范围更新失败",
    'console.nav.billing': '账单',
    'console.nav.referral': '推荐返佣',
    'console.nav.settings': '设置',
    'console.nav.workspace': '工作区',
    'console.nav.register': '注册 API',
    'console.nav.earnings': '收益 / 提现',
    'console.nav.admin': '审核队列',
    'console.nav.providerHeading': '服务商',
    'console.nav.analytics': '分析',

    // Sandbox 实例日志（components/SandboxLogs.jsx）—— 消费者 /sandbox 页与
    // console 的 Sandbox tab 共用。
    'sandbox.log.tab.operations': '操作记录',
    'sandbox.log.tab.events': '状态时间线',
    'sandbox.log.tab.segments': '计费明细',
    'sandbox.log.tab.periods': '结算账单',
    'sandbox.log.empty.operations': '暂无操作记录。',
    'sandbox.log.empty.events': '暂无状态变更记录。',
    'sandbox.log.empty.segments': '暂无计费段。',
    'sandbox.log.empty.periods': '暂无已结算账单。',
    'sandbox.log.loading': '加载中…',
    'sandbox.log.command': '命令',
    'sandbox.log.input': '入参',
    'sandbox.log.stdout': '标准输出',
    'sandbox.log.stderr': '标准错误',
    'sandbox.log.truncated': '输出已截断，仅保留头部',
    'sandbox.log.error': '失败原因',
    'sandbox.log.exit': '退出码',
    'sandbox.log.reason': '原因',
    'sandbox.log.col.state': '状态',
    'sandbox.log.col.kind': '类型',
    'sandbox.log.col.window': '时间段',
    'sandbox.log.col.duration': '时长',
    'sandbox.log.col.amount': '金额',
    'sandbox.log.col.settle': '结算',
    'sandbox.log.segOpen': '计费中',
    'sandbox.log.segTotal': '计费段合计',
    'sandbox.log.totalCost': '实例累计',
    'sandbox.log.periodTotal': '结算账单合计',
    'sandbox.log.loadAll': '加载完整历史（{shown} / {total}）',

    'console.aiLogs.title': 'AI 调用日志',
    'console.aiLogs.subtitle':
      '按次展示 AI 接口调用明细（含图像 / 视频等非 token 计费）。',
    'console.aiLogs.signinSubtitle': '登录后查看你的 AI 调用历史。',
    'console.aiLogs.chartTitle': '每日调用次数',
    'console.aiLogs.empty': '所选时间范围内暂无 AI 调用。',
    'console.aiLogs.loadMore': '加载更多',
    'console.aiLogs.noMore': '没有更多了',
    'console.aiLogs.stat.calls': '调用次数',
    'console.aiLogs.stat.inputTokens': '输入 token',
    'console.aiLogs.stat.outputTokens': '输出 token',
    'console.aiLogs.stat.cost': '总花费',
    'console.aiLogs.stat.providerLatency': '平均首响',
    'console.aiLogs.stat.throughput': '平均吞吐',
    'console.aiLogs.filter.allKeys': '全部 API 密钥',
    'console.aiLogs.filter.allProviders': '全部服务商',
    'console.aiLogs.filter.allModels': '全部模型',
    'console.aiLogs.filter.allEndpoints': '全部接口',
    'console.aiLogs.filter.allOutcomes': '全部结果',
    'console.aiLogs.filter.success': '成功 (2xx)',
    'console.aiLogs.filter.errors': '失败 (4xx/5xx)',
    'console.aiLogs.origin.USER': '账户',
    'console.aiLogs.origin.REQUEST': '请求',
    'console.aiLogs.origin.UPSTREAM': '上游',
    'console.aiLogs.origin.GATEWAY': '网关',
    'console.aiLogs.col.time': '时间',
    'console.aiLogs.col.apiKey': 'API 密钥',
    'console.aiLogs.col.provider': '服务商',
    'console.aiLogs.col.model': '模型',
    'console.aiLogs.col.input': '输入',
    'console.aiLogs.col.output': '输出',
    'console.aiLogs.col.cache': '缓存',
    'console.aiLogs.col.latency': '首响',
    'console.aiLogs.col.cost': '花费',
    'console.aiLogs.col.speed': '速度',
    'console.aiLogs.col.status': '状态',
    'console.aiLogs.drawer.title': 'AI 调用详情',
    'console.aiLogs.drawer.tokens': 'Token 数',
    'console.aiLogs.drawer.cacheRead': '缓存命中',
    'console.aiLogs.drawer.cacheCreate': '缓存写入',
    'console.aiLogs.drawer.total': '合计',
    'console.aiLogs.drawer.timing': '延迟',
    'console.usage.drawer.title': '调用详情',
    'console.aiLogs.drawer.providerLatency': 'Provider 首响',
    'console.aiLogs.drawer.generation': '生成',
    'console.aiLogs.drawer.gatewayOverhead': '网关开销',
    'console.aiLogs.drawer.connect': '连接 (DNS+TCP+TLS)',
    'console.aiLogs.drawer.wait': '上游等待',
    'console.aiLogs.drawer.reused': '复用',
    'console.aiLogs.drawer.reusedConn': '连接已复用 (keep-alive)',
    'console.aiLogs.drawer.nonStream': '非流式响应 (无生成阶段)',
    'console.aiLogs.drawer.responseTime': '总耗时',
    'console.aiLogs.tag.stream': '流式',
    'console.aiLogs.tag.sync': '同步',
    'console.aiLogs.tag.streamTitle': '流式响应（分块，含生成阶段）',
    'console.aiLogs.tag.syncTitle': '非流式响应（整包返回，无生成阶段）',
    'console.aiLogs.speed.nonStreamHint':
      '非流式响应 — tok/s 仅在流式调用可计算',
    'console.aiLogs.speed.noData': '无吞吐量数据',
    'console.endpoint.drawer.title': '端点 · 平均阶段耗时',
    'console.endpoint.drawer.subtitle':
      '基于近期 {N} 次调用的平均拆分（连接段合并显示，单次调用可查日志 drawer 看 DNS/TCP/TLS 细分）',
    'console.aiLogs.drawer.streamDuration': '流式耗时',
    'console.aiLogs.drawer.upstreamModel': '上游模型',
    'console.aiLogs.drawer.billing': '计费',
    'console.aiLogs.drawer.request': '请求',
    'console.aiLogs.drawer.endpoint': '端点',
    'console.aiLogs.drawer.error': '错误',
    'console.aiLogs.drawer.errorOrigin': '责任方',
    'console.aiLogs.drawer.errorCode': '错误码',
    'console.aiLogs.drawer.errorMessage': '错误信息',

    'console.overview.title': '概览',
    'console.overview.welcome': '{USER}，欢迎回来',
    'console.overview.subtitle': '你在{RANGE}的活动概览。',
    'console.overview.signinSubtitle': '登录以查看你的真实使用情况。',
    'console.overview.stat.balance': '余额',
    'console.overview.stat.spent': '已消费',
    'console.overview.stat.calls': '调用次数',
    'console.overview.stat.apiKeys': 'API 密钥',
    'console.overview.callsTitle': '调用 · {RANGE}',
    'console.overview.activityTitle': '近期活动',
    'console.overview.dailySpend': '每日消费',
    'console.overview.topApis': '常用 API · {RANGE}',
    'console.overview.topApisCount': '个 API',
    'console.overview.topApisEmpty': '该时间段内还没有 API 调用。',
    'console.overview.loadErr': '部分统计数据加载失败。',
    'console.overview.vsPrev': '较上一周期',
    'console.overview.allApis': '全部 API',
    'console.overview.col.time': '时间',
    'console.overview.col.api': 'API 服务',
    'console.overview.col.endpoint': '端点',
    'console.overview.col.path': '方法与路径',
    'console.overview.col.status': '状态',
    'console.overview.col.cost': '费用',
    'console.overview.col.key': 'API 密钥',
    'console.overview.empty': '还没有调用 —— 通过 Try-It 或 SDK 发起一次。',

    "console.keys.permissions.groups.runtime.title": "托管智能体",
    "console.keys.permissions.groups.runtime.note": "这是普通 API 密钥的一项权限，不会改变密钥类型。",
    "console.keys.permissions.runtime.label": "使用 XNT Cloud",
    "console.keys.permissions.runtime.desc": "进入托管智能体环境，访问当前账户的会话与沙箱。建议使用独立密钥并设置消费上限。",
    "console.keys.permissions.groups.sandbox.title": "沙箱",
    "console.keys.permissions.sandbox.label": "沙箱调用",
    "console.keys.permissions.sandbox.desc": "通过 MCP 查看并调用自己尚未上线的沙箱 API。",
    "console.keys.permissions.groups.agent.title": "智能体管理",
    "console.keys.permissions.groups.agent.note": "允许智能体使用此密钥，以编程方式管理 API 服务。",
    "console.keys.permissions.serviceCreate.label": "创建 / 注册",
    "console.keys.permissions.serviceCreate.desc": "创建或注册新的 API 服务",
    "console.keys.permissions.serviceRead.label": "读取",
    "console.keys.permissions.serviceRead.desc": "读取你的服务、版本与审核报告",
    "console.keys.permissions.serviceUpdate.label": "更新",
    "console.keys.permissions.serviceUpdate.desc": "修改版本契约（端点、计费、上游）",
    "console.keys.permissions.versionCreate.label": "新建版本",
    "console.keys.permissions.versionCreate.desc": "新建大版本或拉出工作副本",
    "console.keys.permissions.servicePublish.label": "发布",
    "console.keys.permissions.servicePublish.desc": "提交审核并上线（仍走自动审核）",
    "console.keys.permissions.serviceRollback.label": "回滚",
    "console.keys.permissions.serviceRollback.desc": "回滚或切换默认大版本",
    "console.keys.permissions.serviceDelete.label": "删除",
    "console.keys.permissions.serviceDelete.desc": "下架或删除服务",
    "console.keys.permissions.groups.skills.title": "技能市场",
    "console.keys.permissions.groups.skills.note": "使用此密钥上传、更新或提交 xAPI 技能。",
    "console.keys.permissions.skillRead.desc": "读取你的技能、提交状态与技能规范。",
    "console.keys.permissions.skillCreate.label": "创建",
    "console.keys.permissions.skillCreate.desc": "预留技能标识并创建草稿。",
    "console.keys.permissions.skillUpdate.label": "上传 / 更新",
    "console.keys.permissions.skillUpdate.desc": "解析 GitHub、文件夹或 zip，并写入新版本",
    "console.keys.permissions.skillSubmit.label": "提交审核",
    "console.keys.permissions.skillSubmit.desc": "提交技能包进入自动审查与人工审查流程。",
    "console.keys.permissions.count": "{n} 项",
    "console.keys.permissions.none": "未授权",
    "console.keys.permissions.manage": "管理",
    "console.keys.permissions.title": "权限",
    "console.keys.permissions.intro": "控制此 API 密钥的操作权限。默认全部关闭，按需开启；标注「高危」的权限允许智能体直接改动你的线上服务。",
    "console.keys.permissions.highRisk": "高危",
    "console.keys.permissions.settingsDesc": "托管智能体、沙箱调用与程序化管理权限",
    "console.keys.limit.zero": "$0 · 设置上限后可用",
    "console.keys.loadFailed": "加载密钥失败",
    "console.keys.updateFailed": "更新失败",
    "console.keys.limit.updated": "消费上限已更新",
    "console.keys.limit.failed": "消费上限更新失败",
    "console.keys.ip.updated": "IP 白名单已更新",
    "console.keys.ip.failed": "IP 白名单更新失败",
    "console.keys.deleted": "密钥已删除",
    "console.keys.deleteFailed": "删除失败",
    "console.keys.oauth.connected": "服务商已连接",
    "console.keys.oauth.connectFailed": "服务商连接失败",
    "console.keys.oauth.pickFirst": "请先选择服务商",
    "console.keys.oauth.noUrl": "未返回授权链接",
    "console.keys.oauth.startFailed": "无法发起 OAuth 授权",
    "console.keys.oauth.revoked": "已解除绑定",
    "console.keys.oauth.revokeFailed": "解除绑定失败",
    "console.keys.signInKey": "登录密钥",
    "console.keys.created": "密钥已创建，请立即复制保存",
    "console.keys.createFailed": "创建密钥失败",
    "console.keys.verifyRequired": "请通过 X 验证身份后管理 API 密钥。",
    "console.keys.sandboxEnabled": "沙箱已启用",
    "console.keys.sandboxDisabled": "沙箱已禁用",
    "console.keys.deleteConfirm": "确定删除密钥「{name}」？此操作无法撤销。",
    "console.keys.oauth.provider": "服务商",
    "console.keys.delete": "删除密钥",
    "console.keys.verifyCreate": "通过 X 验证身份以创建密钥",
    "console.keys.signedOut": "你尚未登录。",
    "console.keys.signIn": "前往登录",
    "console.keys.notFound": "此账户下未找到密钥。",
    "console.keys.loading": "正在加载密钥…",
    "console.keys.empty": "暂无 API 密钥。",
    "console.keys.createFirst": "创建第一把密钥",
    "console.keys.verify": "通过 X 验证",
    "console.keys.create": "创建密钥",
    "console.keys.totalCalls": "总调用次数",
    "console.keys.spent": "已消费",
    "console.keys.usage": "调用次数",
    "console.keys.active": "已启用",
    "console.keys.oauth.revoking": "正在解除绑定…",
    "console.keys.verifyHint": "你正在使用此密钥登录。绑定 X 账户并验证身份后，即可创建更多密钥、设置消费上限或发布 API。",
    "console.keys.readOnlyBanner": "当前使用 API 密钥登录，仅可查看。通过 X 验证身份后，即可创建或删除密钥、修改消费上限。",
    "console.keys.limit.of": "上限 ${amount}",
    "console.keys.oauth.via": "通过 {provider}",
    "console.keys.oauth.revokeTitle": "解除与 {provider} 的绑定？",
    "console.keys.oauth.revokeDesc": "已保存的令牌将被销毁。依赖 {provider} 的调用会失败，直到你重新连接。",
    "console.keys.nameRequired": "请输入名称",
    "console.keys.createTitle": "创建 API 密钥",
    "console.keys.namePlaceholder": "例如：生产环境",
    "console.keys.createHint": "创建密钥后，可配置消费上限、权限与服务商连接。",
    "console.keys.reveal.title": "密钥「{name}」已创建",
    "console.keys.reveal.copyNow": "请立即复制此密钥。",
    "console.keys.reveal.warning": "为保障安全，此密钥仅显示一次。系统仅保存哈希值，之后无法再次查看。",
    "console.keys.reveal.label": "你的新 API 密钥",
    "console.keys.reveal.envDesc": "创建密钥时设置",
    "console.keys.reveal.envUpdated": "XAPI_KEY 环境变量已更新",
    "console.keys.reveal.useEnv": "设为 XAPI_KEY",
    "console.keys.reveal.saved": "我已保存",
    "console.keys.saving": "正在保存…",
    "console.keys.lastUsed.never": "从未使用",
    "console.keys.lastUsed.now": "刚刚",
    "console.keys.lastUsed.minutes": "{n} 分钟前",
    "console.keys.lastUsed.hours": "{n} 小时前",
    "console.keys.lastUsed.days": "{n} 天前",

    'console.keys.readOnlyLogin': 'API 密钥登录（只读）',
    'console.keys.readOnlyCapabilities': '你可以调用 API、充值和浏览市场。通过 X 验证身份后，即可管理密钥、查看详细用量或发布 API。',
    'console.keys.title': 'API 密钥',
    'console.keys.subtitle':
      '为每把密钥管理消费上限、权限、IP 白名单及第三方连接。',
    'console.keys.signinSubtitle': '登录以管理你的密钥。',
    'console.keys.readOnlySubtitle':
      'API 密钥登录为只读模式 —— 你只能看到当前登录使用的密钥，无法创建或撤销。请通过 X 验证身份解锁完整管理。',
    'console.keys.createNew': '新建密钥',
    'console.keys.manage': '管理设置',
    'console.keys.done': '完成',

    'console.keys.col.name': '名称',
    'console.keys.col.key': '密钥',
    'console.keys.col.connect': 'OAuth 绑定',
    'console.keys.col.limit': '使用上限',
    'console.keys.col.ipAllowlist': 'IP 白名单',
    'console.keys.col.features': '功能',
    'console.keys.col.lastUsed': '最近使用',
    'console.keys.settings.title': '密钥设置',
    'console.keys.ip.title': 'IP 白名单',
    'console.keys.ip.desc': '仅允许指定公网 IPv4/IPv6 地址或 CIDR 网段调用此密钥。',
    'console.keys.ip.unrestricted': '不限制',
    'console.keys.ip.entries': '{n} 条',
    'console.keys.ip.hint': '每行输入一个 IP 或 CIDR；留空表示允许任意来源 IP。',
    'console.keys.ip.placeholder': '203.0.113.10\n2001:db8:1234::/64',
    'console.keys.ip.save': '保存白名单',
    'console.keys.flag.sandbox.label': '沙盒模式',
    'console.keys.flag.sandbox.desc':
      '可在公共 MCP 与浏览器 Playground 中被调用',
    'console.keys.flag.provider.label': '允许注册 API',
    'console.keys.flag.provider.desc':
      '允许此密钥通过 /agent/register-api-service 接口注册新 API 服务',

    'console.keys.limit.title': '使用上限',
    'console.keys.limit.desc':
      '此密钥累计可消费的额度。达到上限后会停止接受调用；只能通过手动重置或充值恢复，不会按月自动重置。',
    'console.keys.limit.noLimit': '无上限',
    'console.keys.limit.perMonth': '累计',
    'console.keys.limit.edit': '编辑',

    'console.keys.oauth.title': 'OAuth 绑定',
    'console.keys.oauth.activeCount': '{n} 个生效',
    'console.keys.oauth.availableCount': '{n} 个可选',
    'console.keys.oauth.desc':
      '使用此密钥发起的调用会自动注入匹配的令牌；密文始终保存在保险箱中。',
    'console.keys.oauth.empty': '尚未绑定任何 OAuth 服务。',
    'console.keys.oauth.connect': '连接服务商',
    'console.keys.oauth.addMore': '继续添加',
    'console.keys.oauth.addProvider': '添加服务商',
    'console.keys.oauth.legacyHint':
      '此密钥创建于 OAuth 功能上线之前。可点击右侧"启用 OAuth"补充派生公钥，或新建一把密钥。',
    'console.keys.oauth.enableCta': '启用 OAuth',
    'console.keys.oauth.enableTitle': '为此密钥启用 OAuth',
    'console.keys.oauth.enableNote':
      '请粘贴一次明文 API 密钥。我们会派生 X25519 公钥后只存公钥那一半；明文从不落盘。',
    'console.keys.oauth.enablePlaintextLabel': '明文 API 密钥',
    'console.keys.oauth.enablePlaintextHint':
      '仅用于一次派生。如已遗失明文，请改用新建密钥。',
    'console.keys.oauth.enabling': '启用中…',
    'console.keys.oauth.enableConfirm': '启用',
    'console.keys.oauth.enableSuccess': '该密钥已启用 OAuth',
    'console.keys.oauth.enableFail': '启用失败',
    'console.keys.oauth.manageWithBindings': '管理设置 · 已绑定 {n} 个 OAuth',

    'console.oauth.dialog.title': '连接 {provider}',
    'console.oauth.dialog.toKey': '绑定到密钥',
    'console.oauth.dialog.consentNote':
      '即将跳转到 {provider} 的授权页面。令牌加密存储，仅在使用此密钥的调用中注入上游。',
    'console.oauth.dialog.requestedScopes': '申请的权限',
    'console.oauth.dialog.basicTitle': '基础权限',
    'console.oauth.dialog.requiredHint': '必选',
    'console.oauth.dialog.advancedTitle': '高级权限',
    'console.oauth.dialog.scopesTitle': '权限',
    'console.oauth.dialog.optionalHint': '可选',
    'console.oauth.dialog.selectAll': '全选',
    'console.oauth.dialog.deselectAll': '取消全选',
    'console.oauth.dialog.cancel': '取消',
    'console.oauth.dialog.opening': '正在打开…',
    'console.oauth.dialog.continue': '继续授权',
    'console.oauth.dialog.back': '返回',
    'console.oauth.dialog.pickProviderTitle': '选择服务商',
    'console.oauth.dialog.noAvailableProviders':
      '暂无可绑定的服务商 —— 当前密钥已绑定所有启用的服务商。',
    'console.oauth.dialog.alreadyConnectedTitle': '已连接 {provider}',
    'console.oauth.dialog.bound': '已绑定',
    'console.oauth.dialog.grantedScopes': '已授权权限',
    'console.oauth.dialog.modifyHint':
      '还有 {n} 项权限可申请 —— 点击"修改权限"扩展授权。',
    'console.oauth.dialog.modifyScopes': '修改权限',
    'console.oauth.dialog.reauthorize': '重新授权',
    'console.oauth.dialog.close': '关闭',
    'console.oauth.revoke': '解除绑定',
    'console.keys.limit.usedNoLimit': '已消费 · 无上限',

    // API health card
    'console.health.title': '接口健康状态',
    'console.health.score24h': '24h 健康值',
    'console.health.avgLatency24h': '24h 平均耗时',
    'console.health.lastCheck': '最近活跃',
    'console.health.refresh': '刷新',
    'console.health.trend24h': '24 小时趋势',
    'console.health.trend30d': '30 天趋势',
    'console.health.status.healthy': '健康',
    'console.health.status.available': '可用',
    'console.health.status.degraded': '部分不可用',
    'console.health.status.down': '基本不可用',
    'console.health.status.unknown': '暂无数据',
    'console.health.endpoints.title': '端点列表',
    'console.health.endpoints.sort.worst': '最差优先',
    'console.health.endpoints.sort.calls': '调用量',
    'console.health.endpoints.sort.path': '路径 A→Z',
    'console.health.endpoints.empty': '没有端点',

    'console.env.title': '环境变量',
    'console.env.subtitle':
      '本地变量，注入到 Try-It、MCP 配置和代码示例中。仅存储在浏览器，xapi 服务器永远不会接收这些值。',

    'console.usage.title': '使用与日志',
    'console.usage.subtitle': '你的所有调用，实时记录。',
    'console.usage.subtitleN': '总计 {N} 次调用',
    'console.usage.signinSubtitle': '登录以查看调用历史。',

    'console.billing.title': '账单',
    'console.billing.subtitle': '余额、充值和交易记录。',
    'console.billing.signinSubtitle': '登录以查看账单。',
    'console.billing.balance': '当前余额',
    'console.billing.balanceHint': '可用于 API 消费',
    'console.billing.recharged': '累计充值',
    'console.billing.spent': '累计消费',
    'console.billing.transactions': '交易记录',
    'console.billing.manualLabel': '一次性支付',
    'console.billing.manualTitle': '手动充值',
    'console.billing.manualDescription': '选择金额，使用银行卡或加密钱包充值。',
    'console.billing.manualMethods': '银行卡 · Base USDC · BSC 稳定币',
    'console.billing.manualAction': '立即充值',
    'console.billing.manualClose': '收起充值表单',
    'console.billing.autoDiscoveryAction': '自动充值设置',
    'topup.amount': '充值金额（USD）',
    'topup.paymentMethod': '支付方式',
    'topup.card': '银行卡',
    'topup.cardHint': 'Visa、Mastercard、Amex · 无额外手续费',
    'topup.useCrypto': '使用数字货币',
    'topup.cardDescription': '使用银行卡安全充值，即充即用。',
    'topup.cryptoDescription': '使用钱包中的稳定币充值。',
    'topup.cardMethods': 'Visa · Mastercard · Amex',
    'topup.cryptoMethods': 'Base USDC · BSC 稳定币',
    'topup.cryptoNetwork': '网络与币种',
    'topup.baseHint': '使用钱包支付 · x402 链上结算',
    'topup.bsc': 'BSC 稳定币',
    'topup.bscHint': 'U、USD1、USDT、USDC · B402 结算',
    'topup.bscToken': 'BSC 代币',
    'topup.youPay': '应付',
    'topup.credited': '计入余额',
    'topup.wallet': '钱包',
    'topup.notConnected': '未连接',
    'topup.disconnect': '断开连接',
    'topup.connecting': '连接中…',
    'topup.connectWallet': '连接钱包',
    'topup.walletHint': '选择用于签名和支付的钱包',
    'topup.noWallet': '未检测到钱包。请安装受支持的钱包后继续。',
    'topup.verifying': '正在向后端确认支付结果…',
    'topup.awaitingWallet': '等待钱包确认…',
    'topup.openingCheckout': '正在打开支付页面…',
    'topup.payWith': '使用 {asset} 支付',
    'topup.connectToPay': '连接钱包并支付',
    'topup.continue': '前往 Stripe 支付',
    'autoRecharge.label': 'Stripe · 自动充值',
    'autoRecharge.cardTitle': '自动充值',
    'autoRecharge.cardDescription': '余额低于设定值时，用已授权的银行卡自动补充。',
    'autoRecharge.title': '余额不足时自动补充',
    'autoRecharge.description': '余额低于设定阈值时，系统可向你授权的银行卡发起一笔指定金额的美元充值。你可以随时关闭。',
    'autoRecharge.enabled': '已启用',
    'autoRecharge.disabled': '未启用',
    'autoRecharge.loading': '正在读取自动充值设置…',
    'autoRecharge.unavailable': '自动充值暂不可用。当前仍可使用一次性充值。',
    'autoRecharge.stripeUnavailable': 'Stripe 自动充值暂不可用。已有授权状态如下；如需撤销，仍可关闭自动充值。',
    'autoRecharge.attempt': '最近一次自动充值：${amount}，{state}。',
    'autoRecharge.attempt.pending': '处理中',
    'autoRecharge.attempt.canceled': '扣款开始前已取消',
    'autoRecharge.attempt.succeeded': '已成功入账',
    'autoRecharge.attempt.failed': '扣款失败，自动充值已暂停',
    'autoRecharge.attempt.partially_reversed': '已部分冲正',
    'autoRecharge.attempt.reversed': '已全部冲正',
    'autoRecharge.loadError': '无法确认自动充值状态，设置暂不可编辑。',
    'autoRecharge.retry': '重试',
    'autoRecharge.setupCancel': '银行卡设置已取消，自动充值保持关闭。请重新确认银行卡和规则并授权后，再启用自动充值。',
    'autoRecharge.setupPending': '已从 Stripe 返回，正在等待银行卡设置确认。请稍后刷新状态。',
    'autoRecharge.suspendedHelp': '退款或争议后，自动充值已暂停。请联系客服处理后再启用。',
    'autoRecharge.pendingPaymentHelp': '上一笔扣款仍待核对，暂时无法重新启用自动充值。',
    'autoRecharge.card': '用于自动扣款的银行卡',
    'autoRecharge.noCard': '尚未添加银行卡',
    'autoRecharge.cardHint': '请先填写下方金额并勾选授权，再添加银行卡。',
    'autoRecharge.flowIntro': '先设定触发余额和单次充值金额，再授权银行卡。保存银行卡本身不会启用自动扣款。',
    'autoRecharge.ruleStep': '充值规则',
    'autoRecharge.ruleHint': '设定余额低于多少时触发，以及每次充值多少。',
    'autoRecharge.rulePreview': '余额低于 ${threshold} 时，充值 ${amount}。',
    'autoRecharge.cardStep': '扣款银行卡',
    'autoRecharge.cardStepHint': 'Stripe 安全保存一张用于自动扣款的银行卡。',
    'autoRecharge.consentStep': '核对并授权',
    'autoRecharge.setupNext': '从 Stripe 返回后，还需在这里核对设置并启用自动充值。',
    'autoRecharge.setupReady': '银行卡已保存。请核对规则，重新确认授权，然后启用自动充值。',
    'autoRecharge.addCardNext': '前往 Stripe 添加银行卡',
    'autoRecharge.currentRule': '当前规则',
    'autoRecharge.manageIntro': '自动充值已启用，当前按以下规则和银行卡扣款。',
    'autoRecharge.readyIntro': '充值规则和银行卡已准备好，确认授权后即可启用自动充值。',
    'autoRecharge.activateConsent': '我授权 xAPI 在余额低于 ${threshold} 时，向此银行卡扣款 ${amount}。',
    'autoRecharge.editRule': '修改充值规则',
    'autoRecharge.editRuleHint': '只需修改触发余额或单次充值金额。',
    'autoRecharge.editRuleConsent': '我授权 xAPI 在余额低于 ${threshold} 时，按下方条款向已保存银行卡扣款 ${amount}。',
    'autoRecharge.authorizationLimits': '两次尝试至少间隔 15 分钟，每 24 小时最多 3 次；余额再次低于阈值时可能再次扣款，可随时关闭。',
    'autoRecharge.changeCardWarning': '开始更换银行卡时，自动充值会暂停。即使取消更换，也需要返回此处重新授权并启用。',
    'autoRecharge.changeCardInactiveHint': '在 Stripe 选择新银行卡后，返回此处授权并启用自动充值。',
    'autoRecharge.changeCardConsent': '我同意通过 Stripe 为当前 ${threshold} / ${amount} 规则保存新银行卡，并知晓开始更换即暂停自动充值，取消或完成后均需重新授权启用。',
    'autoRecharge.changeCardNext': '前往 Stripe 更换银行卡',
    'autoRecharge.backToOverview': '返回当前设置',
    'autoRecharge.fullTerms': '查看完整授权说明',
    'autoRecharge.changeCardHint': '如需更换银行卡，请先在下方重新确认授权，再前往 Stripe 设置。',
    'autoRecharge.failureHelp': '最近一次自动扣款失败。请检查银行卡和授权状态后再尝试。',
    'autoRecharge.refreshCard': '刷新银行卡状态',
    'autoRecharge.expiry': '有效期 {month}/{year}',
    'autoRecharge.addCard': '添加银行卡',
    'autoRecharge.changeCard': '更换银行卡',
    'autoRecharge.threshold': '余额低于（USD）',
    'autoRecharge.topUp': '每次充值（USD）',
    'autoRecharge.invalidAmounts': '请输入 1 至 100 美元、最多两位小数；每次充值金额必须高于余额阈值。',
    'autoRecharge.consent': '我同意通过 Stripe 保存所选银行卡，并授权 xAPI 在账户余额低于上述阈值时，向该卡发起每笔上述金额的非定期扣款；两次尝试至少间隔 15 分钟，每 24 小时最多尝试 3 次。余额再次低于阈值时可能再次扣款。成功付款会计入 xAPI 余额。我可随时在此关闭；扣款失败时可能需要重新验证银行卡。',
    'autoRecharge.enable': '启用自动充值',
    'autoRecharge.save': '保存自动充值设置',
    'autoRecharge.disable': '关闭自动充值',
    'autoRecharge.refresh': '刷新状态',
    'autoRecharge.busy': '处理中…',
    'autoRecharge.saved': '自动充值设置已保存。',
    'autoRecharge.disabledMessage': '自动充值已关闭。',
    'autoRecharge.unconfirmed': '无法确认设置是否生效，请刷新状态后再试。',
    'autoRecharge.saveError': '保存失败，请重试。',
    'autoRecharge.setupError': '无法打开 Stripe 设置页面。',
    'autoRecharge.setupUrlError': '未收到有效的 Stripe 支付方式设置链接。',
    'autoRecharge.compactEnabled': '余额低于 ${threshold} 时，向已保存银行卡充值 ${amount}。',
    'autoRecharge.compactDisabled': '设置余额阈值、每次充值金额和用于自动扣款的银行卡。',
    'autoRecharge.expand': '设置自动充值',
    'autoRecharge.manage': '管理自动充值',
    'autoRecharge.collapse': '收起设置',

    'console.referral.title': '推荐返佣',
    'console.referral.subtitle':
      '你邀请的用户每次调用，你都永久赚取其平台抽成的 {pct}%。',
    'console.referral.signinSubtitle': '登录以查看推荐数据。',
    'console.referral.ledger.usage': '用量返佣',
    'console.referral.ledger.recharge': '充值返佣（历史）',
    'console.referral.ledger.frozen': '旧账本 · 只读',
    'console.referral.ledger.usageEmpty':
      '暂无用量返佣。被邀请人产生消费后约每 10 分钟结算一次。',
    'console.referral.ledger.loadMore': '加载更多',

    'console.settings.title': '设置',
    'console.settings.subtitle': '账户、账单、安全。',
    'console.settings.signinSubtitle': '登录以查看账户。',

    'console.analytics.title': '服务商分析',
    'console.analytics.subtitle': '所有已发布 API 的收益、流量与调用方分布。',
    'console.analytics.signinSubtitle': '登录以查看你的服务商分析数据。',
    'console.analytics.gateBody':
      '该面板仅对已通过认证的服务商开放。先申请加入服务商白名单、发布 API 并产生调用，再回到这里查看数据。',
    'console.analytics.gateCta': '申请成为服务商',
    'console.analytics.empty':
      '暂无收益数据 —— 你的 API 必须上线并被调用后，统计才会出现在这里。',
    'console.analytics.stat.totalNet': '累计收益',
    'console.analytics.stat.rangeNet': '{RANGE}收益',
    'console.analytics.stat.pending': '待结算',
    'console.analytics.stat.settled': '已结算',
    'console.analytics.stat.totalCalls': '累计调用',
    'console.analytics.stat.rangeCalls': '{RANGE}调用',
    'console.analytics.stat.todayCalls': '今日调用',
    'console.analytics.stat.uniqueCallers': '近 30 天活跃调用方',
    'console.analytics.stat.totalApis': '上线 API 数',
    'console.analytics.stat.platformFee': '平台抽成',
    'console.analytics.trend.title': '收益趋势',
    'console.analytics.trend.peak': '峰值日',
    'console.analytics.trend.avg': '日均',
    'console.analytics.apis.title': 'API 列表',
    'console.analytics.apis.subtitle':
      '按 API 项目拆解 —— 毛收入、平台抽成、净收益、流量曲线。',
    'console.analytics.apis.col.api': 'API',
    'console.analytics.apis.col.calls': '调用量',
    'console.analytics.apis.col.gross': '毛收入',
    'console.analytics.apis.col.fees': '抽成',
    'console.analytics.apis.col.net': '净收益',
    'console.analytics.apis.col.callers': '调用方',
    'console.analytics.apis.col.latency': '延迟',
    'console.analytics.apis.col.success': '成功率',
    'console.analytics.history.title': '收益明细',
    'console.analytics.history.subtitle':
      '逐次调用的收益分成记录。「待结算」会在平台对账后变为「已结算」。',
    'console.analytics.history.col.date': '时间',
    'console.analytics.history.col.api': 'API',
    'console.analytics.history.col.gross': '毛收入',
    'console.analytics.history.col.fee': '抽成',
    'console.analytics.history.col.net': '净收益',
    'console.analytics.history.col.status': '状态',
    'console.analytics.history.status.pending': '待结算',
    'console.analytics.history.status.settled': '已结算',
    'console.analytics.history.empty': '该范围内没有收益记录。',
    'console.analytics.history.totals':
      '当前范围：毛 {GROSS} · 抽成 {FEES} · 净 {NET}',
    'console.analytics.scope.all': '全部 API',
    'console.analytics.scope.label': '查看',
    'console.analytics.endpoints.title': '端点',
    'console.analytics.endpoints.subtitle': '所选 API 的端点级流量明细。',
    'console.analytics.endpoints.col.endpoint': '端点',
    'console.analytics.endpoints.col.calls': '调用量',
    'console.analytics.endpoints.col.cost': '毛收入',
    'console.analytics.endpoints.col.callers': '调用方',
    'console.analytics.endpoints.col.latency': '延迟',
    'console.analytics.endpoints.col.success': '成功率',
    'console.analytics.endpoints.empty': '该范围内所选 API 端点没有流量。',

    // ---- Claim wizard ----
    'claim.crumb': '认领账户',
    'claim.title': '升级到完整账户',
    'claim.subtitle':
      '发布一条带验证码的推文以绑定 X 账号。绑定后可发布 API、查看完整余额面板和服务商分析。',
    'claim.alreadyVerified': '已验证为 {HANDLE}',
    'claim.goToConsole': '前往控制台',
    'claim.signInFirst': '请先登录，或从验证推文中打开认领链接。',
    'claim.loading': '加载会话中…',
    'claim.refresh': '刷新',
    'claim.steps.of': '步骤 {N} / 4',

    'claim.apikey.eyebrow': '已签发 API 密钥',
    'claim.apikey.title': '请先保存你的密钥',
    'claim.apikey.body':
      '完整密钥仅在此次显示。立即复制或下载到本地，丢失后无法恢复。',
    'claim.apikey.noKey':
      '本次会话中没有 API 密钥。请登录或使用带 ?apikey=… 的认领链接。',
    'claim.apikey.warning': '密钥丢失后只能从控制台重新生成。',
    'claim.apikey.continue': '我已保存 — 继续',
    'claim.apikey.saveFirst': '请先保存密钥再继续',
    'claim.apikey.injectEnv': '已写入本地 XAPI_KEY 环境变量',

    'claim.tweet.eyebrow': '发布推文',
    'claim.tweet.title': '发布带验证码的推文',
    'claim.tweet.code': '邀请码',
    'claim.tweet.copy': '复制文本',
    'claim.tweet.openX': '到 X 发布',
    'claim.tweet.iPosted': '已发布 — 去验证',
    'claim.tweet.back': '返回',

    'claim.verify.eyebrow': '验证',
    'claim.verify.title': '粘贴推文链接',
    'claim.verify.placeholder': 'https://x.com/<你>/status/…',
    'claim.verify.submit': '验证并升级',
    'claim.verify.verifying': '验证推文中…',
    'claim.verify.errorEmpty': '请先粘贴推文链接',
    'claim.verify.errorNoSession': '缺少认领会话 — 请刷新页面',

    'claim.done.eyebrow': '已验证',
    'claim.done.title': '账户升级成功',
    'claim.done.body':
      'X 账号已绑定，账户现在拥有服务商权限和完整余额访问能力。',
    'claim.done.recharge': '去充值',
    'claim.done.setupMcp': '设置 MCP 客户端',
    'claim.done.handle': '绑定账号',

    // ---- Bind page ----
    'bind.crumb': '绑定 X',
    'bind.title': '绑定你的 X 账号',
    'bind.subtitle':
      '一键 X 授权即可绑定。绑定后可发布 API、查看完整余额面板和服务商分析。',
    'bind.alreadyVerified': '已绑定 {HANDLE}',
    'bind.continueX': '使用 X 继续',
    'bind.skip': '稍后再说',
    'bind.skipHint':
      '你可以以虚拟账号身份继续使用 xapi，之后从控制台再来绑定。',
    'bind.signInFirst': '请先登录或打开注册链接。',
    'bind.successToast': '绑定成功',
    'bind.mergedToast': '已合并到主账号 —— 当前 API Key 已转入主账号名下',
    'bind.errorToast': '绑定失败',
    'bind.activityHint': '想要 $1 免费额度?发推文即可领取。',
    'bind.activityCta': '发推领取 $1 →',
    'bind.preparing': '正在准备 X 授权…',

    // ---- Activity page ----
    'activity.crumb': '活动',
    'activity.title': '发推领取 $1 额度',
    'activity.subtitle':
      '在 X 上发布一条验证推文，即可获得 $1 免费额度，叠加在你的账户余额上。',
    'activity.reward.amount': '$1.00',
    'activity.reward.label': '免费额度',
    'activity.calc.title': '$1 能干什么',
    'activity.calc.twitter': '次 Twitter API 调用',
    'activity.calc.llm': 'LLM tokens',
    'activity.calc.search': '次网页搜索',
    'activity.gateBody': '先登录或注册，奖励会发放到你登录的账户。',
    'activity.gateCta': '开始使用',
    'activity.bindGate.eyebrow': '请先绑定 X',
    'activity.bindGate.body':
      '活动仅限实体账户（已绑定 X）参与，每个账户仅可领取一次。',
    'activity.bindGate.cta': '去绑定 X',
    'activity.tweet.eyebrow': '第 1 步 · 发布推文',
    'activity.tweet.code': '邀请码',
    'activity.tweet.copy': '复制文本',
    'activity.tweet.openX': '到 X 发布',
    'activity.verify.eyebrow': '第 2 步 · 粘贴 URL',
    'activity.verify.placeholder': 'https://x.com/<你>/status/…',
    'activity.verify.submit': '验证并领取 $1',
    'activity.verify.verifying': '正在验证推文…',
    'activity.done.title': '$1 已发放',
    'activity.done.body': '$1 已加到你的账户余额，去试调一个 API 吧。',
    'activity.done.console': '前往控制台',
    'activity.done.exploreApis': '浏览 API',
    'activity.alreadyClaimed': '你已经领取过该额度。',
    'activity.errorEmpty': '请先粘贴推文链接',
    'activity.errorNoSession': '缺少活动会话 — 请先登录。',
    'activity.error.twitterBound': '该推特用户已经领取过 1 刀',
    'activity.error.alreadyClaimed': '你已经领取过 1 刀',
    'activity.error.tweetUsed': '该推文已被用于领取过奖励',

    'workspace.title': '我的 API',
    'workspace.subtitle': '你已注册的 API。提交审核以公开发布到市场。',
    'workspace.import': '使用 skill 导入',
    'workspace.skillImport.title': '用 xapi-provider skill 导入',
    'workspace.skillImport.desc':
      '让 AI agent 帮你把 API 接入 xapi。安装 xapi-provider skill 后，把你的 OpenAPI 规范或 API 文档交给 agent —— 它会自动建服务、配置计费、并提交审核。',
    'workspace.skillImport.installLabel': '安装 skill',
    'workspace.skillImport.promptLabel': '然后对你的 agent 说',
    'workspace.skillImport.promptExample':
      '用 xapi-provider skill 把这个 OpenAPI 注册到 xapi 并发布。',
    'workspace.skillImport.repo': '查看 skill 仓库',
    'workspace.skillImport.copy': '复制',
    'workspace.provideApi': '发布 API',
    'workspace.section.active': '已上线',
    'workspace.section.activeDesc': '已发布 —— 在市场可见',
    'workspace.section.review': '审核中',
    'workspace.section.reviewDesc': '等待管理员审批',
    'workspace.section.sandbox': '沙箱',
    'workspace.section.sandboxDesc': '已通过校验 · 待提交上线',
    'workspace.section.draft': '草稿',
    'workspace.section.draftDesc': '尚未提交',
    'workspace.section.attention': '需要处理',
    'workspace.section.attentionDesc': '已被驳回或暂停',
    'workspace.empty': '你还没有注册任何 API。',
    'workspace.firstApi': '发布你的第一个 API',
    'workspace.submit': '提交',
    'workspace.routes': '个端点',

    // ---- MCP page ----
    'mcp.eyebrow': '模型上下文协议',
    'mcp.title': '接入你的 MCP 客户端',
    'mcp.subtitle':
      '选择客户端、粘贴配置、重启即可。Agent 通过单一端点解锁全部 {APIS} 个市场 API（共 {TOOLS} 个工具），各工具的访问权限由你的 API Key 控制。',
    'mcp.stat.apis': '市场 API',
    'mcp.stat.tools': '暴露工具数',
    'mcp.stat.status': '服务状态',
    'mcp.stat.online': '在线',
    'mcp.endpoint.label': 'MCP 端点',
    'mcp.endpoint.note': 'HTTP 传输。在 URL 末尾追加 {QS} 进行鉴权。',
    'mcp.auth.label': '鉴权 · XAPI_KEY',
    'mcp.auth.manage': '管理',
    'mcp.auth.loaded': '已从环境变量加载',
    'mcp.auth.injected': '已自动注入右侧配置中。仅保存在你本地浏览器。',
    'mcp.auth.noKey': '尚未加载密钥',
    'mcp.auth.placeholder':
      '配置中将显示 {PH} 占位符。设置一次后，下方所有配置自动填充。',
    'mcp.auth.set': '设置 XAPI_KEY',
    'mcp.sandbox.title': '沙箱密钥',
    'mcp.sandbox.desc': '只有启用了 {SANDBOX} 的密钥才能调用预上线状态的 API。',
    'mcp.sandbox.manage': '前往控制台管理',
    'mcp.config.cli': 'CLI 命令',
    'mcp.config.label': 'mcpServers 配置',
    'mcp.config.oneClick': '一键安装',
    'mcp.config.copyCommand': '复制命令',
    'mcp.config.copyConfig': '复制配置',
    'mcp.config.placeholderHint':
      '将 {PH} 替换为来自 {LINK} 的密钥，或在左侧设置 {KEY} 实现自动注入。',
    'mcp.config.consoleKeys': '控制台 → 密钥',
    'mcp.config.cliHint':
      '在 shell 中运行该命令。下次会话时 Claude Code 会自动加载所有 xapi 工具。',
    'mcp.config.pasteHint':
      '粘贴到 {NAME} 的 MCP 配置中并重启客户端，xapi 服务会注册 {TOOLS} 个工具。',

    // ---- Capabilities page ----
    'cap.eyebrow': '能力',
    'cap.title': '能力实验场',
    'cap.subtitle':
      '跨 API 技能 —— 每个能力都把路由和重试逻辑藏在一个端点之后。挑一个、填好输入、发起测试调用。',
    'cap.skills': '个技能',
    'cap.loading': '加载能力中…',
    'cap.unavailable': '能力服务不可用',
    'cap.unavailableBody':
      '无法访问 {URL}：{ERR}。请检查能力服务是否运行，或在环境变量中设置 {OVR}。',
    'cap.empty': '尚未发布任何能力。',
    'cap.routesThrough': '路由经过：',
    'cap.req': '请求',
    'cap.resp': '响应',
    'cap.run': '执行能力',
    'cap.abort': '中止',
    'cap.required': '必填',
    'cap.placeholder': '执行能力以查看响应',
    'cap.routing': '正在经由 {APIS} 路由…',
    'cap.payload': '请求体 · application/json',
    'cap.charged': '已计费',
    'cap.upstreamCall': '次上游调用',
    'cap.upstreamCalls': '次上游调用',
    'cap.setKeyToSend': '设置 XAPI_KEY 后发送',

    // ---- Skill page ----
    'skill.eyebrow': 'Anthropic Skills',
    'skill.title': 'xapi 作为 Skill 接入',
    'skill.subtitle':
      '{SKILL} 是一份可移植的工具清单 —— Agent 拉取一次后永久记住，无需逐项目配置 MCP。一条安装命令，让 Claude、Cursor 或任意 agentic IDE 学会市场上的所有 API。',
    'skill.subtitle.skillWord': 'Skill',
    'skill.install.title': '安装',
    'skill.unit.file': '个文件',

    // ---- Skill page sections ----
    'skill.section.use': '01 · 如何使用',
    'skill.section.about': '02 · Skill 本身',
    'skill.use.subtitle':
      '在任意 Agent CLI 里运行安装命令，或把下方提示词粘贴到聊天框。两种方式都会让你的 Agent 拿到完整的 xapi 工具目录。',
    'skill.about.subtitle': 'Agent 拉取的 manifest 里究竟有什么。',
    'skill.about.fact.tools': '工具数',
    'skill.about.fact.size': '清单体积',
    'skill.about.fact.refresh': '刷新频率',
    'skill.about.fact.refreshVal': '按需',
    'skill.about.list.title': 'Manifest 内容',
    'skill.about.list.l1':
      '工具目录 —— 市场上每个 API 都映射为一个带类型的可调用工具',
    'skill.about.list.l2': '每个工具的输入与响应 JSON Schema',
    'skill.about.list.l3': '网关端点与鉴权头约定',
    'skill.about.list.l4': '计费元数据（按次 / 按 token / 按资源）',
    'skill.about.viewManifest': '查看在线 manifest',

    // 注册页 — 页面框架（侧栏 + 表单顶栏）
    'register.signin.eyebrow': '服务提供方',
    'register.signin.title': '请先登录后再注册 API',
    'register.signin.cta': '登录',
    'register.crumb.workspace': '工作区',
    'register.crumb.edit': '编辑',
    'register.crumb.new': '新建',
    'register.untitled': '未命名服务',
    'register.host.direct': '直连 · 仅作目录展示',
    'register.host.pending': '— 待生成域名 —',
    'register.live': '已上线',
    'register.nav.configure': '配置',
    'register.nav.tools': '工具',
    'register.nav.overview': '概览',
    'register.nav.overview.hint': '名称、域名、可见性',
    'register.nav.origin': '上游与鉴权',
    'register.nav.origin.hint': '上游 URL、鉴权、私有头',
    'register.nav.origin.disabled': 'DIRECT 模式下不可用',
    'register.nav.endpoints': '接口',
    'register.nav.endpoints.hint': '{N} 个路由',
    'register.tools.importOpenapi': '导入 OpenAPI',
    'register.tools.importOpenapi.busy': '解析中…',
    'register.tools.aiImport': 'AI 导入',
    'register.tools.deleteDraft': '删除草稿',
    'register.header.title.edit': '编辑服务',
    'register.header.title.new': '注册 API 服务',
    'register.header.body.new':
      '配置上游、接口与计费。提交审核之前都可以编辑草稿。',
    'register.header.body.edit.live.save':
      '{B} —— 仅元信息（名称、Logo、域名）。',
    'register.header.body.edit.live.hotfix': '{B} —— 直接覆盖当前线上版本。',
    'register.header.body.edit.live.release': '{B} —— 发布新版本。',
    'register.header.body.edit.notLive': '更新基础信息后，准备就绪可提交审核。',
    'register.action.create': '创建草稿',
    'register.action.create.busy': '创建中…',
    'register.action.save': '更新',
    'register.action.save.busy': '更新中…',
    'register.action.hotfix': '更新当前版本',
    'register.action.hotfix.busy': '更新中…',
    'register.action.release': '发布新版本',
    'register.action.release.busy': '发布中…',
    'register.action.release.placeholder': '版本说明…',
    'register.confirm.hotfix.title': '更新当前版本？',
    'register.confirm.hotfix.body':
      '把你当前所有未保存的改动(含已移除的端点)原地写入当前版本 —— 消费者的版本号不变,适合修 bug 和小改动。在你确认之前,什么都不会保存。',
    'register.confirm.hotfix.ok': '确认更新',
    'register.confirm.release.title': '发布新版本？',
    'register.confirm.release.body':
      '版本号递增(如 v1 → v2),把你当前所有未保存的改动保存为这个新版本。消费者需主动切到新契约,旧版本继续可用,适合不兼容的变更。',
    'register.confirm.release.ok': '确认发布',
    'register.confirm.cancelBtn': '取消',
    'register.action.review': '提交审核',
    'register.action.review.busy': '提交中…',
    'register.action.submit': '提交',
    'register.action.submit.busy': '提交中…',
    'register.action.resubmit': '重新提交审核',
    'register.action.resubmit.busy': '提交中…',
    'register.banner.draft':
      '草稿中 —— 保存会原地替换工作副本。准备好后提交审核。',
    'register.banner.sandbox':
      '在沙箱 · 等待审核。编辑会原地更新工作副本,管理员下次查看时看到的就是最新版。用沙箱 API key 端到端验证。',
    'register.banner.pendingReview': '正在跑自动质量检查 —— 通常几秒钟。',
    'register.banner.approved': '已审核通过 · 正在上线…',
    'register.banner.active':
      '已在市场上线。小改原地用「更新当前版本」,不兼容变更用「发布新版本」。',
    'register.banner.rejected': '审核未通过。修改后重新提交审核。',
    'register.banner.suspended':
      '服务已由管理员暂停。已发布修订和 serving 指针均会保留；请联系管理员恢复，无需重新提交审核。',
    'register.unsavedChanges':
      '● 有未保存的改动 —— 删除/修改需点「更新当前版本」或「发布新版本」才会生效',
    'register.toast.created': 'API 已创建',
    'register.toast.create.fail': '创建失败',
    'register.toast.save': '已更新',
    'register.toast.save.fail': '更新失败',
    'register.toast.hotfix': '热修复已应用',
    'register.toast.hotfix.fail': '热修复失败',
    'register.toast.release': '新版本已发布',
    'register.toast.release.fail': '发布失败',
    'register.toast.review': '已提交审核',
    'register.toast.review.fail': '提交失败',
    'register.toast.delete': '草稿已删除',
    'register.toast.delete.fail': '删除失败',
    'register.toast.fixFields': '请先修正高亮的字段',
    'register.toast.noVersion': '当前没有可热修复的版本',
    'register.toast.loadFail': '加载 API 失败',
    'register.confirm.review':
      '提交此 API 进行审核？管理员审批通过后才会公开。',
    'register.confirm.delete': '删除此草稿？操作不可撤销。',
    'register.confirm.delete.title': '删除此草稿？',
    'register.confirm.delete.ok': '删除',
    'register.confirm.review.title': '提交审核？',
    'register.confirm.review.ok': '提交',
    'register.tools.batchPrice': '批量价格设置',
    'register.overview.about': 'About 文档（可选）',
    'register.overview.about.hint':
      '这篇 Markdown 会出现在市场详情页的 About tab。市场卡片仍使用上方的短描述。',
    'register.overview.about.starter': '插入模板',
    'register.overview.about.demo': '加载完整示例',
    'register.overview.about.replaceTitle': '替换当前 About 内容？',
    'register.overview.about.replaceBody':
      '这将覆盖你已撰写的 About 文档，操作不可撤销。',
    'register.overview.about.replaceOk': '替换',

    // 注册页 — 概览
    'register.overview.identity': '基本信息',
    'register.overview.identity.sub': '会展示在市场和 MCP 工具列表中。',
    'register.overview.name': '展示名称',
    'register.overview.name.placeholder': '例如：Weather Pro',
    'register.overview.category': '分类',
    'register.overview.description': '描述',
    'register.overview.description.hint':
      '市场卡片短文案（1–2 行）。长文档请写到「About 文档」子页。',
    'register.overview.description.placeholder': '一段话简介…',
    'register.overview.website': '官网',
    'register.overview.website.hint': '可选，展示在市场详情页外链。',
    'register.overview.logoUrl': 'LOGO',
    'register.overview.logoUrl.hint': '方形 LOGO，JPG/PNG/GIF/WebP，最大 10 MB。选填。',
    'logo.upload': '上传 LOGO',
    'logo.replace': '更换 LOGO',
    'logo.uploading': '正在上传…',
    'logo.cancel': '取消上传',
    'logo.remove': '移除 LOGO',
    'logo.preview': 'LOGO 预览',
    'logo.url': '或粘贴图片链接',
    'logo.hint': '选填，建议使用方形图片。支持 JPG、PNG、GIF、WebP，最大 10 MB，也可拖放图片到预览区域上传。',
    'logo.pending': 'LOGO 正在上传，请完成后再保存。',
    'logo.uploaded': 'LOGO 已上传，保存更改后生效。',
    'logo.error.type': '请选择 JPG、PNG、GIF 或 WebP 图片。',
    'logo.error.empty': '文件为空，请选择其他图片。',
    'logo.error.size': '图片不能超过 10 MB。',
    'logo.error.upload': '上传失败，请重试。',
    'logo.error.timeout': '上传超时，请重试。',
    'logo.error.preview': '图片加载失败，请检查链接或更换图片。',
    'register.overview.visibility': '可见性',
    'register.overview.visibility.public': '公开',
    'register.overview.visibility.private': '私有',
    'register.overview.access': '接入模式',
    'register.overview.access.sub':
      'PROXY 经 xapi 网关转发；DIRECT 仅作目录展示，不经网关。',
    'register.overview.access.proxy.desc':
      'xapi 作为网关：鉴权、计量、计费、日志。',
    'register.overview.access.direct.desc':
      '仅做目录。调用方直连上游 URL，不计费、不鉴权。',
    'register.overview.rateLimit': '用户频率限制',
    'register.overview.rateLimit.sub':
      '用简单的用户级配额保护服务容量。',
    'register.overview.rateLimit.off': '不限流',
    'register.overview.rateLimit.off.hint':
      '当前不限制单个用户的调用频率；需要保护服务容量时可启用。',
    'register.overview.rateLimit.on': '启用限流',
    'register.overview.rateLimit.requests': '最大请求数',
    'register.overview.rateLimit.requests.hint': '请输入 1 到 1,000,000 之间的整数。',
    'register.overview.rateLimit.period': '限流周期',
    'register.overview.rateLimit.second': '每秒',
    'register.overview.rateLimit.minute': '每分钟',
    'register.overview.rateLimit.hour': '每小时',
    'register.overview.rateLimit.custom': '每 {seconds} 秒（已有配置）',
    'register.overview.rateLimit.toggle': '启用或关闭用户频率限制',
    'register.overview.rateLimit.status.on': '已启用',
    'register.overview.rateLimit.status.off': '未启用',
    'register.overview.rateLimit.rule': '配额规则',
    'register.overview.rateLimit.userPrefix': '每位用户最多',
    'register.overview.rateLimit.times': '次请求',
    'register.overview.rateLimit.preview': '当前策略',
    'register.overview.rateLimit.scope.user': '按用户独立计数',
    'register.overview.rateLimit.scope.keys': '所有 API Key 共享',
    'register.overview.rateLimit.scope.endpoints': '所有服务端点共享',
    'register.overview.rateLimit.scope':
      '同一用户持有的所有 API Key 共享额度，且此服务下的所有端点共同计数。',
    'register.overview.rateLimit.direct':
      '用户频率限制仅支持 PROXY 模式；切换为 DIRECT 时会清除此配置。',
    'register.overview.host.label': '域名前缀',
    'register.overview.host.hint':
      '小写字母、数字、连字符。最终为 <prefix>{suffix}。',
    'host.checking': '正在检查可用性…',
    'host.ok': '"{host}" 可用',
    'host.err.TOO_SHORT': '域名前缀至少需要 3 个字符',
    'host.err.INVALID_FORMAT': '域名前缀只能包含小写字母、数字和连字符',
    'host.err.RESERVED': '"{host}" 是保留词，请换一个',
    'host.err.CONTAINS_BLOCKED': '域名前缀包含不允许的词',
    'host.err.TAKEN': '"{host}" 已被占用，请换一个',
    'host.err.default': '该域名前缀不被允许',

    // 注册页 — 上游与鉴权
    'register.origin.upstream': '上游来源',
    'register.origin.upstream.sub': '请求会被反向代理到这里。',
    'register.origin.baseUrl': 'Base URL',
    'register.origin.baseUrl.hint': '上游服务，例如 https://api.acme.com',
    // 多上游环境
    'register.origin.env.add': '添加 Base URL',
    'register.origin.env.default': '默认 Base URL',
    'register.origin.env.extra':
      '其他命名 Base URL —— 起个名字，端点就能在 path 左侧的下拉里选它；不选则走默认 Base URL。',
    'register.origin.env.name.ph': 'staging',
    'register.origin.env.url.ph': 'https://staging.api.com',
    'register.origin.env.error': '名称只能含字母/数字/-/_，URL 必须合法',
    // 端点分组 + path 环境菜单
    'register.endpoint.field.group': '分组',
    'register.endpoint.field.group.hint':
      '同名归为一组，在端点列表里折叠展示（留空 = 未分组）',
    'register.endpoint.field.group.placeholder': '如 User / Billing',
    'register.endpoint.path.placeholder': '/v1/your-endpoint',
    'register.endpoint.path.envMenu': '选择上游环境',
    'register.endpoint.path.envBtn': '上游环境 —— 选择该端点走哪个 Base URL',
    'register.endpoint.path.defaultHint': '默认 Base URL',
    'register.endpoint.path.noMatch': '无匹配环境',
    // 弹窗创建项目
    'createProject.title': '创建 API 项目',
    'createProject.name': '项目名称',
    'createProject.name.ph': '如：Weather API',
    'createProject.desc': '项目介绍',
    'createProject.desc.ph': '一句话描述这个项目提供什么能力',
    'createProject.type': '类型',
    'createProject.template': '模板方案',
    'createProject.hint':
      '创建后进入 workspace 配置上游地址、接口与计费。上游 base URL 支持多环境（{{staging}} 等）。',
    'createProject.cancel': '取消',
    'createProject.submit': '创建草稿',
    'createProject.creating': '创建中…',
    'createProject.nameRequired': '请填写项目名称',
    'createProject.created': '草稿已创建',
    'createProject.failed': '创建失败',
    'createProject.tpl.blank': '空白项目',
    'createProject.tpl.blank.desc': '从零开始，进 workspace 后自行添加接口',
    'createProject.tpl.rest-crud': 'REST CRUD',
    'createProject.tpl.rest-crud.desc': '标准增删改查 5 接口，按资源分组',
    'createProject.tpl.read-only-data': '只读数据 API',
    'createProject.tpl.read-only-data.desc': '列表 + 详情两个 GET 接口',
    'register.origin.auth': '鉴权',
    'register.origin.auth.sub':
      'xapi 如何对上游鉴权。Secret 在服务端加密保存。',
    'register.origin.auth.NONE': '不鉴权',
    'register.origin.auth.BEARER': 'Bearer Token',
    'register.origin.auth.HEADER': 'API Key（请求头）',
    'register.origin.auth.QUERY': 'API Key（Query）',
    'register.origin.auth.headerName': '请求头名称',
    'register.origin.auth.queryName': 'Query 参数名',
    'register.origin.auth.bearer': 'Bearer Token',
    'register.origin.auth.secret': 'Secret 值',
    'register.origin.auth.secret.hint': '加密存储，可随时轮换。',
    'register.origin.privateHeaders': '私有请求头',
    'register.origin.privateHeaders.sub':
      '附加到每个上游请求的额外请求头（仅服务端使用，不暴露给客户端）。',
    'register.origin.privateHeaders.add': '添加请求头',
    'register.origin.privateHeaders.empty': '暂无私有请求头',
    'register.origin.privateHeaders.col.name': '请求头名称',
    'register.origin.privateHeaders.col.value': '值',
    'register.origin.directNotice.title': 'DIRECT 模式不需要配置上游与鉴权',
    'register.origin.directNotice.body':
      '调用方将直接访问你的上游 URL。xapi 不参与转发或计费。如果需要计费，请在「概览」中切换为 {PROXY} 模式。',

    // 异步任务 — 注册页
    'register.endpoint.async.label': '异步模式',
    'register.endpoint.async.hint':
      '当单次调用通常超过 ~30 秒时启用。客户端会拿到 task_id 并通过 /v1/tasks/{id} 轮询结果。',
    'register.endpoint.async.toggleHintOff':
      '同步：客户端在一次 HTTP 请求内等待响应返回。',
    'register.endpoint.async.toggleHintOn':
      '异步：服务端立即返回 task_id，客户端通过 /v1/tasks/{id} 轮询直到任务完成。',
    'register.endpoint.async.optionalLegacy':
      '本接口保留了旧版「客户端自选」模式。关掉再打开开关即可切换为强制异步。',
    'register.endpoint.async.opt.none': '仅同步',
    'register.endpoint.async.opt.optional': '同步 + 异步',
    'register.endpoint.async.opt.required': '仅异步',
    'register.endpoint.async.hint.optional':
      '调用方带 X-XAPI-Async: true 走异步，否则同步；两种都支持。',
    'register.endpoint.async.mode.NONE': '同步',
    'register.endpoint.async.mode.OPTIONAL': '可选异步',
    'register.endpoint.async.mode.REQUIRED': '强制异步',
    'register.endpoint.async.warn.oauth': 'OAuth 绑定的接口不支持异步模式。',
    'register.endpoint.async.warn.streaming':
      'streaming 与异步互斥，不能同时启用。',
    'register.endpoint.async.lockedByPolling':
      '选择「上游轮询」策略后，异步模式被锁定为「强制异步」。',
    // V2 任务策略
    // ── 端点编辑器：tab / 基础字段（之前是硬编码英文）─
    'register.endpoint.tab.general': '基础',
    'register.endpoint.tab.request': '请求',
    'register.endpoint.tab.body': '请求体',
    'register.endpoint.tab.responses': '响应',
    'register.endpoint.tab.model': '模型',
    'register.endpoint.tab.billing': '计费',
    'register.endpoint.panel.streaming': '流式',
    'register.endpoint.panel.streaming.sub':
      '开启 SSE(Server-Sent Events)或原始流式响应。',
    'register.endpoint.field.supportsStreaming': '支持流式',
    'register.endpoint.field.streamFormat': '流式格式',
    'register.endpoint.streaming.on': '流式 · 开',
    'register.endpoint.streaming.off': '流式 · 关',
    'register.endpoint.field.displayName': '显示名称',
    'register.endpoint.field.displayName.placeholder': '例如：获取天气预报',
    'register.endpoint.field.billingType': '计费类型',
    'register.endpoint.field.billingType.directHint':
      'DIRECT 模式强制 PER_CALL',
    'register.endpoint.field.billingType.pollingHint':
      'UPSTREAM_POLLING 支持 PER_CALL、PER_TOKEN 或 DYNAMIC',
    'register.endpoint.field.billingType.opt.perCall': '按次（固定 USD）',
    'register.endpoint.field.billingType.opt.perToken': '按 token（LLM）',
    'register.endpoint.field.billingType.opt.perResource':
      '按资源数（response.count）',
    'register.endpoint.field.billingType.opt.dynamic': '动态计费（规则引擎）',
    'register.endpoint.field.description': '描述',
    'register.endpoint.field.description.hint':
      '将在 API 详情页和 SDK 文档中展示。',
    'register.endpoint.field.description.placeholder': '这个端点返回什么……',
    'register.endpoint.panel.basics': '基本信息',
    'register.endpoint.panel.behavior': '执行行为',
    'register.endpoint.panel.visibility': '可见性与访问',
    'register.endpoint.field.hidden': '从公开详情页隐藏',
    'register.endpoint.field.hidden.hint': '端点仍可调用，但不会公开列出。',
    'register.endpoint.field.hidden.on': '已隐藏',
    'register.endpoint.field.hidden.off': '可见',
    'register.endpoint.field.oauth': '调用方 OAuth 授权',
    'register.endpoint.field.oauth.hint':
      '可选。要求调用方通过该 OAuth provider 授权。',
    'register.endpoint.field.oauth.none': '无（不需要 OAuth）',
    // 批量价格弹窗用的计费类型短标签
    'register.endpoint.billing.short.perCall': 'per call',
    'register.endpoint.billing.short.perToken': 'per token',
    'register.endpoint.billing.short.perResource': 'per resource',

    // ── 端点编辑器：请求 tab（path / header / query / 私有 headers）─
    'register.endpoint.request.pathParams.title': '路径参数',
    'register.endpoint.request.pathParams.subtitle':
      '自动从路径占位符（如 :id 或 {id}）中提取。',
    'register.endpoint.request.pathParams.empty': '未检测到路径参数。',
    'register.endpoint.request.pathParams.emptySub':
      '在路径中写 :name 或 {name} 即可添加。',
    'register.endpoint.request.headers.title': 'Header 参数',
    'register.endpoint.request.headers.add': '添加 header',
    'register.endpoint.request.headers.empty': '暂无 header 参数。',
    'register.endpoint.request.query.title': 'Query 参数',
    'register.endpoint.request.query.add': '添加 query',
    'register.endpoint.request.query.empty': '暂无 query 参数。',
    'register.endpoint.request.privateHeaders.title': '端点私有 headers',
    'register.endpoint.request.privateHeaders.subtitle':
      '仅作用于此端点的额外上游 headers，会叠加在服务级私有 headers 之上。',
    'register.endpoint.request.privateHeaders.add': '添加 header',
    'register.endpoint.request.privateHeaders.empty': '暂无端点私有 headers。',

    // ── 端点编辑器：共享表格列头 ─
    'register.endpoint.col.name': '名称',
    'register.endpoint.col.example': '示例',
    'register.endpoint.col.type': '类型',
    'register.endpoint.col.description': '描述',
    'register.endpoint.col.required': '必填',
    'register.endpoint.col.key': '键',
    'register.endpoint.col.value': '值',

    // ── 端点编辑器：请求体 tab ─
    'register.endpoint.body.type': '请求体类型',
    'register.endpoint.body.empty': '无请求体。',
    'register.endpoint.body.emptySub': 'GET / DELETE 通常不携带请求体。',
    'register.endpoint.body.jsonExample': 'JSON 示例',
    'register.endpoint.body.jsonExample.hint':
      '同时作为文档化的 schema 和测试用的请求体。',
    'register.endpoint.body.formFields': '表单字段',
    'register.endpoint.body.formFields.hint':
      '键值对行。同时用作文档 schema 与测试载荷。',
    'register.endpoint.body.formFields.hintFile':
      '键值对行。把某行类型设为 File 可上传二进制(在测试台上传)。',
    'register.endpoint.body.noFields': '暂无字段。',
    'register.endpoint.body.fileUpload': '〔文件 · 测试时上传〕',
    'register.endpoint.body.addField': '添加字段',

    // ── 端点编辑器：响应 tab ─
    'register.endpoint.responses.add': '添加示例',
    'register.endpoint.responses.statusCode': '状态码',
    'register.endpoint.responses.label': '标签',
    'register.endpoint.responses.contentType': '内容类型',
    'register.endpoint.responses.body': '响应体',
    'register.endpoint.responses.empty': '未选择响应。',
    'register.endpoint.responses.emptySub': '点击 + 添加示例 开始。',

    // ── 端点编辑器：计费 — 按次 / 按资源 ─
    'register.endpoint.billing.perCall.cost': '按次成本（美元）',
    'register.endpoint.billing.perCall.cost.hint': '每次成功调用都会计费。',
    'register.endpoint.billing.perCall.share': '你的分成',
    'register.endpoint.billing.perCall.share.hint':
      '按次计费 → 80% 给提供方，20% 平台费。',
    'register.endpoint.billing.perResource.cost': '按资源成本（美元）',
    'register.endpoint.billing.perResource.maxResources': '预估单次最大资源数',
    'register.endpoint.billing.perResource.maxResources.hint':
      '用于预扣安全校验。',
    'register.endpoint.billing.perResource.countPath': '资源数 JSON 路径',
    'register.endpoint.billing.perResource.countPath.hint':
      '例如 "data.length" 或 "meta.total"。网关会从响应中读取它来按实际数量计费。',

    // ── 端点编辑器：Token JSON 路径（PER_TOKEN）─
    'register.endpoint.token.paths.title': 'Token JSON 路径',
    'register.endpoint.token.paths.overrideSub':
      '覆盖 {SPEC} 协议的默认路径（留空即用默认）。',
    'register.endpoint.token.paths.customSub':
      '在上游响应中如何定位 token 计数。选择一个预设或自定义。',
    'register.endpoint.token.paths.collapse': '收起',
    'register.endpoint.token.inputPath': '输入 tokens 路径',
    'register.endpoint.token.outputPath': '输出 tokens 路径',
    'register.endpoint.token.modelPathResponse': '模型路径（响应）',
    'register.endpoint.token.modelPathRequest': '模型路径（请求）',
    'register.endpoint.token.cacheWritePath': '缓存写入路径',
    'register.endpoint.token.cachePath.hint':
      '可选。Anthropic / OpenRouter 提示缓存。',
    'register.endpoint.token.cacheReadPath': '缓存读取路径',
    'register.endpoint.token.cacheIncluded': '缓存是否已计入 input',
    'register.endpoint.token.cacheIncluded.hint':
      '上游的 input / prompt_tokens 计数里是否已经包含缓存命中的 token。OpenAI 系=已包含(系统扣减后计费)，Anthropic=未包含(缓存单独计)。配错会导致缓存被双重计费或漏算折扣。',
    'register.endpoint.token.cacheIncluded.yes': '已包含（无需另计）',
    'register.endpoint.token.cacheIncluded.no': '未包含（单独计）',
    'register.endpoint.token.cacheIncluded.autoBy': '由 {SPEC} 协议自动判定 · ',
    'register.endpoint.token.cacheIncluded.autoIncluded':
      '该协议的 input/prompt_tokens 已含缓存 token，系统自动扣减后计费',
    'register.endpoint.token.cacheIncluded.autoSeparate':
      '该协议的 input_tokens 不含缓存，缓存单独计',

    // ── 端点编辑器：模型定价（PER_TOKEN）─
    'register.endpoint.pricing.editJsonTitle':
      '以 JSON 编辑（每 100 万 tokens，方便从文档粘贴）',
    'register.endpoint.pricing.addModel': '添加模型',
    'register.endpoint.pricing.cancel': '取消',
    'register.endpoint.pricing.apply': '应用',
    'register.endpoint.pricing.subtitle':
      '每 100 万 tokens。每个上游模型一行。用 "default" 作为兜底。',
    'register.endpoint.pricing.title': '模型定价',
    'register.endpoint.pricing.loadingEditor': '编辑器加载中…',
    'register.endpoint.pricing.hintPrefix':
      '提示：价格单位为每 100 万 tokens 的美元（例如 ',
    'register.endpoint.pricing.hintMid': ' = $3 / 100 万 tokens）。可选：',
    'register.endpoint.pricing.hintSuffix': '。',
    'register.endpoint.pricing.table.model': '模型',
    'register.endpoint.pricing.table.input': '输入 /1M',
    'register.endpoint.pricing.table.output': '输出 /1M',
    'register.endpoint.pricing.table.cacheWrite': '缓存写入 /1M',
    'register.endpoint.pricing.table.cacheRead': '缓存读取 /1M',
    'register.endpoint.pricing.modelName.placeholder':
      'default / gpt-4o / claude-3-opus',
    'register.endpoint.pricing.tiers.title': '阶梯定价（可选）',
    'register.endpoint.pricing.tiers.add': '添加阶梯',
    'register.endpoint.pricing.tiers.empty': '暂无阶梯定价。',
    'register.endpoint.pricing.tiers.threshold': '≥ tokens',
    'register.endpoint.pricing.tiers.thresholdPlaceholder': '输入 tokens 阈值',
    'register.endpoint.pricing.tiers.inputPlaceholder': '输入 /1M',
    'register.endpoint.pricing.tiers.outputPlaceholder': '输出 /1M',

    'register.endpoint.taskStrategy.label': '执行策略',
    'register.endpoint.taskStrategy.hint':
      '「Worker 阻塞」会保持 HTTP 连接直到上游响应；「上游轮询」提交后立即释放连接，由 xapi 自己轮询上游 — 视频生成这类长任务必须用它。',
    'register.endpoint.taskStrategy.opt.workerBlocking':
      'Worker 阻塞 (V1，默认)',
    'register.endpoint.taskStrategy.opt.upstreamPolling':
      '上游轮询 (V2，长任务专用)',
    'register.endpoint.taskStrategy.upstreamProvider': '上游平台',
    'register.endpoint.taskStrategy.upstreamProvider.placeholder':
      '— 选择上游平台 —',
    'register.endpoint.taskStrategy.fixedHold': '预扣金额（USD）',
    'register.endpoint.taskStrategy.fixedHold.hint':
      '每个任务预扣的金额（上限 ${MAX}）。留空时：PER_TOKEN 用 provider 默认值；PER_CALL 自动由 costPerCall 推导。',
    'register.endpoint.taskStrategy.fixedHold.autoPerCall':
      '自动由 costPerCall 推导',

    // 端点列表区（主从面板的标题栏 / 搜索 / 侧栏徽标）
    'register.endpoints.title': '接口 · {N}',
    'register.endpoints.add': '新增接口',
    'register.endpoints.filter': '搜索接口',
    'register.endpoints.none': '没有匹配的接口。',
    'register.endpoints.unnamed': '（未命名）',
    'register.endpoints.aiMapped': '已映射 {N} 个',
    'register.endpoints.aiPending': '待处理 {N} 个',
    'register.endpoint.removeTitle': '删除接口',
    'register.endpoint.selectHint': '请选择一个接口。',

    // AI 协议 — 端点 tab 名称
    'register.endpoint.tab.aiSpec': 'AI 协议',

    // AI 协议 — 接口上方的嗅探反馈条
    'register.ai.banner.active': '已标记为 AI 接口',
    'register.ai.banner.activeVia': '（来源：{BY}）',
    'register.ai.banner.configure': '配置模型映射 →',
    'register.ai.banner.lowSuggest':
      '此路径不匹配任何已知 AI 协议，但 PER_TOKEN 计费提示它可能提供',
    'register.ai.banner.lowYes': '是的，标记为 {SPEC}',
    'register.ai.banner.none':
      '未检测为 AI 接口。如果它提供 AI 模型，请点这里 →',
    'register.ai.banner.noneCta': '+ 配置为 AI 接口',
    'register.ai.toast.detected': '已识别为 {SPEC} — 「AI 协议」标签已打开',

    // AI 协议 — tab 内容
    'register.ai.interfaceSpec': '接口协议',
    'register.ai.interfaceSpec.sub': '该接口遵循哪个 AI 协议。',
    'register.ai.interfaceSpec.detectedBy': '识别来源：{BY}。',
    'register.ai.notAiEndpoint': '不是 AI 接口',
    'register.ai.notAiEndpointTitle': '标记为非 AI 接口',
    'register.ai.spec.field': '协议',
    'register.ai.modelMappings': '模型映射',
    'register.ai.modelMappings.subPerToken':
      '上游模型名自动从「按 token 计费」的定价行同步。为每个模型选择一个规范模型即可启用路由。',
    'register.ai.modelMappings.subPerCall':
      '列出该接口提供的上游模型名，并为每个模型选择一个规范模型，以启用路由。',
    'register.ai.modelMappings.add': '+ 新增模型',
    'register.ai.modelMappings.errCatalog': '加载规范模型目录失败：{MSG}',
    'register.ai.modelMappings.errSuggest':
      '建议服务暂不可用，你仍可从下方的完整目录手动选择。（{MSG}）',
    'register.ai.modelMappings.emptyPerToken':
      '还没有模型行。请在「计费 → 按 token 定价」面板里添加，模型会自动出现在这里。',
    'register.ai.modelMappings.emptyPerCall':
      '暂无模型。点击「+ 新增模型」开始添加。',
    'register.ai.modelMappings.col.upstream': '上游模型名',
    'register.ai.modelMappings.col.canonical': '规范模型',
    'register.ai.modelMappings.pending': '— 待处理（尚未映射）—',
    'register.ai.modelMappings.optgroup.suggested': '推荐匹配',
    'register.ai.modelMappings.optgroup.all': '全部规范模型',
    'register.ai.modelMappings.topSuggestion': '推荐：',
    'register.ai.modelMappings.placeholderSynced':
      '（自动同步自定价，点击可手动覆盖）',
    'register.ai.modelMappings.titleSynced':
      '自动同步自「按 token 定价」。手动修改后该行不再跟随定价变化。',
    'register.ai.modelMappings.removeTitle': '删除',

    // AI 协议 — 提交前的待处理预览弹窗
    'register.pending.title': '发现 {N} 个未映射的模型',
    'register.pending.body':
      '这些模型会以「待处理」状态保存，在管理员确认规范模型映射前不会被路由。也可以点击「返回修复」现在就在「AI 协议」标签中手动指定。',
    'register.pending.col.endpoint': '接口',
    'register.pending.col.spec': '协议',
    'register.pending.col.model': '模型名',
    'register.pending.back': '返回修复',
    'register.pending.submit': '保留待处理并提交',

    // 异步任务 — API 详情
    'apidetail.tag.async': '异步',
    'apidetail.tag.asyncRequired': '仅异步',
    'apidetail.tryit.async.toggle': '异步模式',
    'apidetail.tryit.async.toggleHint': '以任务形式提交，轮询获取结果',
    'apidetail.free.title': '完成账户注册，即可使用免费接口',
    'apidetail.free.readyTitle': '此接口可免费调用',
    'apidetail.free.signin': '请使用社交账号注册／登录，再到「控制台 → API Keys」创建密钥，并将其设为 XAPI_KEY。仅通过「快速开始」获取临时密钥，还不能使用免费接口。',
    'apidetail.free.signinAction': '注册／登录账户',
    'apidetail.free.complete': '你当前使用的是临时账户，尚未完成注册。绑定 X 账户后，即可使用免费接口，原有 API Key 可继续使用。',
    'apidetail.free.completeAction': '绑定 X，完成注册',
    'apidetail.free.existingAction': '已有账户，去登录',
    'apidetail.free.setKey': '你的账户已可使用免费接口。请到「控制台 → API Keys」创建或选择密钥，再到「环境变量」将其设为 XAPI_KEY，即可发送请求。',
    'apidetail.free.setKeyAction': '设置 XAPI_KEY',
    'apidetail.free.switchKey': '本次请求使用的 API Key 所属账户尚未完成注册。请将 XAPI_KEY 更换为你已注册账户的密钥，再重新调用。',
    'apidetail.free.switchKeyAction': '更换 XAPI_KEY',
    'apidetail.free.keysAction': '管理 API Key',
    'apidetail.tryit.async.required': '此接口仅支持异步调用。',
    'apidetail.tryit.async.submitted': '任务已提交',
    'apidetail.tryit.async.polling': '轮询任务中…',
    'apidetail.tryit.async.viewInConsole': '到控制台查看',
    'apidetail.tryit.async.cancel': '停止轮询',
    'apidetail.tryit.async.expiresIn': '过期剩余',
    'apidetail.tryit.async.longJobHint':
      '长任务（如视频生成）通常需要 60–120 秒。轮询会自动继续。',

    // 异步任务 — 控制台 tab
    'console.nav.tasks': '异步任务',
    'console.tasks.title': '异步任务',
    'console.tasks.subtitle': '通过 X-XAPI-Async 提交的长任务。',
    'console.tasks.signinSubtitle': '登录后查看你的异步任务。',
    'console.tasks.empty': '暂无任务。',
    'console.tasks.emptyHint':
      '在异步接口的 Try-It 中开启 Async 模式即可提交。',
    'console.tasks.keyPicker': 'API 密钥',
    'console.tasks.filter.all': '全部',
    'console.tasks.filter.inProgress': '进行中',
    'console.tasks.filter.completed': '已完成',
    'console.tasks.filter.failed': '失败',
    'console.tasks.status.pending': '等待中',
    'console.tasks.status.processing': '处理中',
    'console.tasks.status.succeeded': '成功',
    'console.tasks.status.failed': '失败',
    'console.tasks.status.expired': '已过期',
    'console.tasks.col.task': '任务',
    'console.tasks.col.endpoint': '接口',
    'console.tasks.col.status': '状态',
    'console.tasks.col.created': '创建时间',
    'console.tasks.col.duration': '耗时',
    'console.tasks.col.cost': '费用',
    'console.tasks.cost.pending': '完成后结算',
    'console.tasks.cost.heldHint': '处理中预扣 ${amount}，完成后按实际用量结算',
    'console.tasks.col.key': '密钥',
    'console.tasks.detail.timing': '时间',
    'console.tasks.detail.calling': '调用接口',
    'console.tasks.detail.apiKey': 'API 密钥',
    'console.tasks.detail.cost': '费用',
    'console.tasks.detail.result': '结果',
    'console.tasks.detail.error': '错误',
    'console.tasks.detail.fileExpired': '文件已过期，无法下载',
    'console.tasks.detail.download': '下载',
    'console.tasks.detail.openImage': '查看大图',
    'console.tasks.detail.copyId': '复制任务 ID',
    'console.tasks.loadMore': '加载更多',
    'console.tasks.refresh': '刷新',
    'console.tasks.autoRefresh': '有进行中任务时自动刷新',

    // ── Skill 市场 ──
    'skills.eyebrow': '技能市场',
    'skills.title': '可复用的 xapi Agent 工作流',
    'skills.subtitle':
      '浏览经过审核的技能包——它们说明 Agent 应如何组合 xapi 能力、提示词、依赖与用法示例。',
    'skills.baseSkill': 'xAPI 基础技能',
    'skills.creatorConsole': '创作者控制台',
    'skills.search': '按名称、slug、描述搜索',
    'skills.loadError': '加载技能失败',
    'skills.empty.title': '还没有已发布的技能',
    'skills.empty.body':
      '审核通过后，已发布的技能会显示在这里，附带安装命令与依赖说明。',
    'skills.empty.submit': '提交第一个技能',
    'skills.card.noDesc': '暂无描述。',
    'skills.card.deps': '{n} 个依赖',
    'skills.cat.all': '全部',
    'skills.cat.social': '社交',
    'skills.cat.search': '搜索',
    'skills.cat.crypto': '加密货币',
    'skills.cat.media': '媒体',
    'skills.cat.data': '数据',
    'skills.cat.automation': '自动化',
    'skills.cat.developer': '开发者',
    'skills.cat.research': '研究',
    'skills.detail.back': '返回市场',
    'skills.detail.install': '安装',
    'skills.detail.copyInstall': '复制安装命令',
    'skills.detail.unavailable': '技能不可用',
    'skills.detail.notFound': '未找到该技能',
    'skills.detail.openMarket': '打开市场',
    'skills.detail.noReadme': '暂无 README 内容。',
    'skills.detail.deps': '依赖',
    'skills.detail.noDeps': '未声明显式的 xapi 依赖。',
    'skills.detail.examples': '使用示例',
    'skills.detail.release': '发布信息',
    'skills.detail.owner': '创作者',
    'skills.detail.repository': '公开仓库',
    'skills.detail.installMetric': '去重复制意向',
    'skills.detail.permissions': '权限提示',
    'skills.detail.permissionWrites': '会写入外部状态',
    'skills.detail.permissionCredits': '可能消耗额度',
    'skills.detail.permissionPersonal': '处理个人数据',
    'skills.creator.eyebrow': '创作者控制台',
    'skills.creator.title': '提交技能包',
    'skills.creator.subtitle':
      '先填写 GitHub URL，或选择包含 SKILL.md 的文件夹 / zip；系统先解析元数据和版本，再确认提交。',
    'skills.creator.marketplace': '市场',
    'skills.creator.apiKeyNote':
      'API Key 会话可以提交技能，但该 Key 必须已开启 skill:submit 权限。',
    'skills.creator.entityNote':
      '提交技能需要实体账户，或使用已开启 skill:submit 权限的 API Key。',
    'skills.creator.signInTitle': '登录后提交技能',
    'skills.creator.signInBody':
      '创建技能需要已认证的实体账户，因为提交会产生审核与发布的审计记录。',
    'skills.creator.signIn': '登录',
    'skills.creator.step1': '1. 选择技能包',
    'skills.creator.name': '名称',
    'skills.creator.slug': 'Slug',
    'skills.creator.description': '描述',
    'skills.creator.categories': '分类（逗号分隔）',
    'skills.creator.tags': '标签（逗号分隔）',
    'skills.creator.createDraft': '创建草稿',
    'skills.creator.step2': '2. 确认元数据',
    'skills.creator.draft': '技能草稿',
    'skills.creator.selectSkill': '选择一个技能',
    'skills.creator.packageZip': '技能包 zip',
    'skills.creator.uploadZip': '上传 zip',
    'skills.creator.githubUrl': 'GitHub URL',
    'skills.creator.importGitHub': '导入 GitHub',
    'skills.creator.parseGitHub': '解析 GitHub',
    'skills.creator.parseFolder': '解析文件夹',
    'skills.creator.parseZip': '解析 zip',
    'skills.creator.packageParsed': '已解析技能包',
    'skills.creator.sourceHint':
      '优先使用 GitHub URL；本地开发包仍可用文件夹或 zip 上传。',
    'skills.creator.or': '或',
    'skills.creator.folderTab': '文件夹',
    'skills.creator.chooseFolder': '选择技能文件夹',
    'skills.creator.multiSkill':
      '该文件夹包含多个 skill。一次只能上传一个——请选择只含一个 SKILL.md 的文件夹。',
    'skills.creator.noSkillFound': '所选文件夹里没有找到 SKILL.md。',
    'skills.creator.filesInSkill': '该技能包含的文件',
    'skills.creator.skillMdRoot': 'SKILL.md 在根目录',
    'skills.creator.uploadFolder': '上传技能',
    'skills.creator.missing': '缺失',
    'skills.creator.validatedVersion': '已校验版本',
    'skills.creator.noValidated': '未选择已校验的包',
    'skills.creator.metadataWaiting': '等待解析技能包',
    'skills.creator.metadataParsing': '正在解析技能包',
    'skills.creator.metadataLocked': '包校验通过后即可编辑元数据。',
    'skills.creator.parsedPackageStatus': '技能包解析完成：{version}',
    'skills.creator.version': '版本',
    'skills.creator.slugChecking': '正在检查 Slug 是否可用…',
    'skills.creator.slugInvalid':
      'Slug 必须为 3-64 位，仅支持小写字母、数字和连字符。',
    'skills.creator.slugAvailable': 'Slug {slug} 可用。',
    'skills.creator.slugOwnedByYou':
      'Slug {slug} 已属于你（{status}），可以继续提交新版本。',
    'skills.creator.slugTaken':
      'Slug {slug} 已被 {owner} 占用（{status}），请换一个 Slug。',
    'skills.creator.slugReserved': 'Slug {slug} 是保留名称，请换一个 Slug。',
    'skills.creator.slugOwnerUnknown': '其他用户',
    'skills.creator.reviewerNote': '给审核者的备注',
    'skills.creator.reviewerNotePh': '可选：给审核者的补充说明。',
    'skills.creator.submitReview': '提交审核',
    'skills.creator.submitPackage': '提交审核',
    'skills.creator.mySkills': '我的技能',
    'skills.creator.mySkillsSub': '本账户的最新版本与提交记录。',
    'skills.creator.refresh': '刷新',
    'skills.creator.colSkill': '技能',
    'skills.creator.colStatus': '状态',
    'skills.creator.colVersions': '版本',
    'skills.creator.colSubmission': '最近提交',
    'skills.creator.colUpdated': '更新时间',
    'skills.creator.noDrafts': '还没有草稿。',
    'skills.creator.submissionProgress': '提交进度',
    'skills.creator.stageSubmitted': '提交',
    'skills.creator.stageAutoReview': '自动初审',
    'skills.creator.stageHumanReview': '人工审核',
    'skills.creator.stagePublish': 'GitHub 发布',
    'skills.creator.notSubmitted': '尚未提交',
    'skills.creator.waiting': '等待中',
    'skills.creator.notStarted': '尚未开始',
    'skills.creator.noReviewResult': '暂无结果',
    'skills.creator.noPublishJob': '批准后创建发布任务',
    'skills.creator.openCi': '查看 GitHub 检查',
    'skills.creator.openCommit': '查看提交',
    'skills.status.draft': '草稿',
    'skills.status.submitted': '已提交',
    'skills.status.in_review': '审核中',
    'skills.status.auto_passed': '自动初审通过',
    'skills.status.auto_failed': '自动初审未通过',
    'skills.status.pending': '待处理',
    'skills.status.pending_human': '待人工审核',
    'skills.status.approved': '已批准',
    'skills.status.needs_changes': '需修改',
    'skills.status.rejected': '已拒绝',
    'skills.status.publishing': '发布中',
    'skills.status.published': '已发布',
    'skills.status.queued': '排队中',
    'skills.status.running': '正在推送 GitHub',
    'skills.status.verifying': '正在运行 GitHub 检查',
    'skills.status.waiting_pr_merge': '等待 PR 合并',
    'skills.status.success': '发布成功',
    'skills.status.failed': '发布失败',
    'skills.status.blocked': '发布被拦截',
    // ===== Workers console (workers.jsx, WorkersWorkspace, billing, monitoring) =====
    'wk.id.label': 'Worker ID',
    'wk.id.hint': 'xAPI 实例唯一标识，用于 API、CLI 管理和日志查询；不是 API Key。',
    'wk.id.copy': '复制',
    'wk.id.copied': '已复制',
    'wk.id.copyAria': '复制 Worker ID',
    'wk.id.copiedStatus': 'Worker ID 已复制',
    'wk.id.copyFailed': '无法访问剪贴板，请选中上方 Worker ID 手动复制。',
    'wk.h.bindings': '资源绑定',
    'wk.h.noBindings': '没有绑定资源',
    'wk.url.copy': '复制地址',
    'wk.url.copyFallback': '复制兼容入口',
    'wk.url.copied': '已复制',
    'wk.url.none': '没有公开访问地址',
    'wk.url.fallback': '兼容入口',
    'wk.url.provisioning': 'DNS/TLS 配置中',
    'wk.s.rateUnknown': '计价基数未知',
    'wk.l.privacy': '不会采集请求正文与查询字符串。',
    'wk.resUse.kv_namespace': '低延迟配置、缓存与会话索引。',
    'wk.resUse.d1_database': '关系型 SQL 数据，表结构与迁移由你自己管理。',
    'wk.resUse.r2_bucket': '文件与生成产物；删除前需先清空。',
    'wk.resUse.durable_object': '按对象隔离的强一致状态与协调。',
    'wk.resUse.queue': '至少一次的异步投递，处理函数需幂等。',
    'wk.resUse.workflow': '持久化的多步骤运行，按步骤重试。',
    'wk.tab.health': '运行状况',
    'wk.tab.resources': '资源',
    'wk.tab.spend': '消费',
    'wk.tab.logs': '日志',
    'wk.tab.settings': '设置',
    'wk.env': '环境',
    'wk.refresh': '刷新',
    'wk.refreshing': '刷新中…',
    'wk.visit': '打开',
    'wk.range.1h': '最近 1 小时',
    'wk.range.24h': '最近 24 小时',
    'wk.range.7d': '最近 7 天',
    'wk.deployed': '已部署',
    'wk.notDeployed': '未部署',
    'wk.unavailable': '数据不可用',
    'wk.h.availability': '可用率',
    'wk.h.availabilityNote': '无 5xx 的请求占比',
    'wk.h.success': '成功率',
    'wk.h.successNote': '2xx/3xx；4xx 计为失败',
    'wk.h.p95': 'P95 延迟',
    'wk.h.p50Note': 'P50 {value}',
    'wk.h.requests': '请求数',
    'wk.h.requestsNote': '观察窗口内',
    'wk.h.sampledNote': '{count} 条采样记录',
    'wk.h.errors': '5xx 错误',
    'wk.h.errorsNote': '另有 {count} 次运行时异常',
    'wk.h.release': '当前版本',
    'wk.h.releaseNote': '已运行 {age}',
    'wk.h.noRelease': '没有运行中的版本',
    'wk.h.spendToday': '今日消费',
    'wk.h.budgetLeft': '预算剩余',
    'wk.h.budgetNote': '每日上限 {limit}',
    'wk.h.traffic': '流量与稳定性',
    'wk.h.noTraffic': '暂无遥测数据',
    'wk.h.legendOk': '无 5xx',
    'wk.h.legendErr': '含 5xx',
    'wk.h.lastTelemetry': '最后一条遥测在 {age} 前',
    'wk.h.endpoints': '端点',
    'wk.h.endpointsNote': '按流量前 8 条',
    'wk.h.col.endpoint': '端点',
    'wk.h.col.requests': '请求',
    'wk.h.col.4xx': '4xx',
    'wk.h.col.5xx': '5xx',
    'wk.h.col.p95': 'P95',
    'wk.h.noEndpoints': '本窗口内没有端点数据',
    'wk.h.domain': '域名与证书',
    'wk.h.retryDomain': '重试',
    'wk.r.none': '{env} 环境没有资源',
    'wk.r.usage': '用量',
    'wk.r.spend': '消费',
    'wk.r.location': '位置',
    'wk.r.logs': '日志',
    'wk.r.delete': '删除',
    'wk.r.r2Continue': '继续初始化',
    'wk.r.r2Retry': '重试创建',
    'wk.r.r2Pending': 'R2 初始化尚未完成。点击“继续初始化”检查进度；已有桶和资金记录会保留。',
    'wk.r.r2Review': 'R2 事件投递需要排查。已有桶会保留，请联系支持，不要另建一个桶。',
    'wk.r.r2RefreshFailed': '资源状态刷新失败。请刷新页面后，用原绑定名重试。',
    'wk.r.back': '返回资源列表',
    'wk.r.tab.monitor': '监测',
    'wk.r.tab.spend': '消费',
    'wk.r.tab.logs': '日志',
    'wk.r.noUsage': '该资源没有上报用量',
    'wk.r.noSpend': '没有归属到该资源的费用',
    'wk.r.logsHint': 'Worker 日志未按资源过滤，可搜索绑定名缩小范围。',
    'wk.r.openLogs': '打开 Worker 日志',
    'wk.r.metric': '指标',
    'wk.r.thisPeriod': '本期',
    'wk.s.balance': '可用余额',
    'wk.s.byResource': '按资源消费',
    'wk.s.ledger': '扣费明细',
    'wk.s.noLedger': '本次快照没有扣费记录',
    'wk.s.col.time': '时间',
    'wk.s.col.item': '项目',
    'wk.s.col.qty': '数量',
    'wk.s.col.price': '单价',
    'wk.s.col.amount': '金额',
    'wk.s.noBreakdown': '暂无按资源的消费数据',
    'wk.l.calls': '请求',
    'wk.l.logs': '日志',
    'wk.l.filter': '筛选路径、请求或 trace…',
    'wk.l.outcome.all': '全部',
    'wk.l.outcome.success': '成功',
    'wk.l.outcome.error': '错误',
    'wk.l.empty': '暂无{view}',
    'wk.l.noMatch': '没有符合筛选条件的事件',
    'wk.l.sampled': '高频事件由 Cloudflare 采样',
    'wk.set.deploy': '部署',
    'wk.set.schedules': '计划任务',
    'wk.set.env': '环境设置',
    'wk.set.budget': '每日预算（USD）',
    'wk.set.save': '保存',
    'wk.res.KV_NAMESPACE': 'KV 命名空间',
    'wk.res.D1_DATABASE': 'D1 数据库',
    'wk.res.R2_BUCKET': 'R2 存储桶',
    'wk.res.DURABLE_OBJECT': 'Durable Object',
    'wk.res.QUEUE': '消息队列',
    'wk.res.WORKFLOW': '工作流',
    'wk.m.WORKER_REQUEST': '请求数',
    'wk.m.WORKER_CPU_MS': 'CPU 时间',
    'wk.m.KV_READ': '读取',
    'wk.m.KV_WRITE': '写入',
    'wk.m.KV_DELETE': '删除',
    'wk.m.KV_LIST': '列举',
    'wk.m.KV_STORAGE_GB_HOUR': '存储',
    'wk.m.D1_ROWS_READ': '读取行数',
    'wk.m.D1_ROWS_WRITTEN': '写入行数',
    'wk.m.D1_STORAGE_GB_HOUR': '存储',
    'wk.m.R2_CLASS_A_OPERATION': '写操作',
    'wk.m.R2_CLASS_B_OPERATION': '读操作',
    'wk.m.R2_IA_CLASS_A_OPERATION': '写操作（低频）',
    'wk.m.R2_IA_CLASS_B_OPERATION': '读操作（低频）',
    'wk.m.R2_IA_RETRIEVAL_BYTE': '取回量（低频）',
    'wk.m.R2_STORAGE_GB_DAY': '存储',
    'wk.m.DO_REQUEST': '请求',
    'wk.m.DO_ROWS_READ': '读取行数',
    'wk.m.DO_ROWS_WRITTEN': '写入行数',
    'wk.m.DO_DURATION_GB_SECOND': '运行时长',
    'wk.m.DO_STORAGE_GB_HOUR': '存储',
    'wk.m.QUEUE_OPERATION': '操作数',
    'wk.m.WORKFLOW_INVOCATION': '运行次数',
    'wk.m.WORKFLOW_STEP': '步骤数',
    'wk.m.WORKFLOW_CPU_MS': 'CPU 时间',
    'wk.m.WORKFLOW_STORAGE_GB_HOUR': '存储',
  },
};

const STORAGE_KEY = 'xapi.locale';

const detectInitialLocale = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LOCALES.includes(saved)) return saved;
  } catch {}
  // Browser language: zh-CN / zh-TW / zh → zh; everything else → en.
  if (
    typeof navigator !== 'undefined' &&
    navigator.language &&
    /^zh\b/i.test(navigator.language)
  )
    return 'zh';
  return 'en';
};

const I18nCtx = createContext(null);

export const I18nProvider = ({ children }) => {
  const [locale, setLocale] = useState(detectInitialLocale);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {}
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      // Optional 2nd arg `vars` does {placeholder} → value substitution.
      // Kept tiny on purpose — no plural rules, no ICU. If a string needs
      // pluralization, write two keys and pick at call site.
      t: (key, vars) => {
        const table = dict[locale] || dict.en;
        const raw =
          table[key] != null
            ? table[key]
            : dict.en[key] != null
              ? dict.en[key]
              : key;
        if (!vars) return raw;
        return raw.replace(/\{(\w+)\}/g, (_, name) =>
          vars[name] != null ? String(vars[name]) : `{${name}}`,
        );
      },
    }),
    [locale],
  );

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
};

export const useT = () => {
  const ctx = useContext(I18nCtx);
  if (!ctx) {
    // Allow `useT` outside a provider (returns identity) so primitives can call it without
    // forcing every test/render path through the provider. Real app always has the provider.
    return {
      locale: 'en',
      setLocale: () => {},
      t: (k, vars) => {
        const raw = dict.en[k] != null ? dict.en[k] : k;
        if (!vars) return raw;
        return raw.replace(/\{(\w+)\}/g, (_, name) =>
          vars[name] != null ? String(vars[name]) : `{${name}}`,
        );
      },
    };
  }
  return ctx;
};
